import { type IMessagesType } from "@domain/intl/translations/de";

const auth = {
    "app.registration.caption": "Registration",
    "app.registration.description": "Please fill the data to create new account",
    "app.registration.firm": "Firm / Name",
    "app.registration.agb": "AGB",
    "app.registration.agb.agree": "Yes, I accept the {agbLink}.",
    "app.registration.privacy.agree": "Yes, I have read the {datenschutzLink} and the {cookieLink}.",
    "app.registration.message.send_verification_email": "Confirmation E-mail sent",
    "app.registration.almost_done": "Almost done",
    "app.registration.almost_done_description":
        "Thanks for signing up for Binale! Please confirm your email adress. Go to the link we have sent you to your email address.",
    "app.registration.send_again": "Send again",
    "app.registration.resend_again_in": "You can resend the email in",

    "app.login.description": "Welcome to Binale",
    "app.login.error.fail": "Wrong E-Mail address or password.",
    "app.login.loose_2fa_app": "Authenticator app unavailable?",
    "app.login.loose_2fa_app_instructions":
        "If your authentication app no longer available, please contact us at {email}",

    "app.reset_password.caption": "Password forgotten?",
    "app.reset_password.description": "Please enter your E-mail address and click 'Send' to reset your password.",
    "app.reset_password.confirmation":
        "We have send a link to reset your password on your E-Mail address. Please check your postbox.",

    "app.new_password.caption": "New password",
    "app.new_password.description": "Enter your new password.",
    "app.new_password.confirmation": "New password was saved.",

    "app.2fa.button.enable2fa": "Enable 2fa",
    "app.2fa.button.showPrivateKey": "Show My Private Key",
    "app.2fa.button.disable2fa": "Disable 2fa",
    "app.2fa.step.title": "Enable Authentificator",
    "app.2fa.input_label": "Enter the 6 digit code from your Authenticator app.",
    "app.2fa.confirm_delete": "Are you sure you want to disable 2fa?",
    "app.2fa.description":
        "With <b>two-factor-authentification</b> you add an additional layer of security to your user account. When logging in, in addition to your password, you use a one-time verification random code that is generated with an authentication app.",
    "app.2fa.enabled2fa_description": "<b>Two-Faktor-Authentification</b> ist enabled for your account!",

    "app.2fa.step.download.title": "Download App",
    "app.2fa.step.download.description": "Download and install one of the Athenticator apps below on your smartphone",

    "app.2fa.step.scan.title": "Scan QR Code",
    "app.2fa.step.scan.description": "Scan this QR code in your app",
    "app.2fa.step.scan.manual_enter":
        "If you are unable to scan the QR code, please enter this code manually into the app.",

    "app.2fa.step.backup.title": "Backup Key",
    "app.2fa.step.backup.description":
        "Please save this Key on paper. This Key will allow you to recover your Authenticator in case of phone loss.",

    "app.2fa.step.enable.title": "Enable authenticator",
    "app.2fa.token_error": "Incorrect 2FA verification code.",
    "app.2fa.too_many_attempts": "Too many incorrect codes. Please try again in 15 minutes.",
};

const errors = {
    // firebase errors
    "app.firebase.error.auth_requires_recent_login": "To change the password, you need to re-login to the system.",
    "app.firebase.error.auth_user_not_found":
        "There is no user record corresponding to this identifier. The user may have been deleted.",
    "app.firebase.error.auth_too_many_requests": "Too many requests.",
    "app.firebase.error.duplicate_email": "E-mail address is already used.",
    "app.firebase.error.auth_weak_password": "Password is weak",
    "app.firebase.error.auth_invalid_email": "E-mail is invalid",
    "app.firebase.error.auth_invalid_token": "Token is invalid",

    // error pages
    "app.error_page.title.something_went_wrong": "Something doesn't seem to have gone well...",
    "app.error_page.we_already_fix_it": "We'll take care of it as soon as possible. Please refresh the page.",
    "app.error_page.button.reload_page": "Reload",
    "app.error_page.title.problem_with_db": "There seems to be a problem with the connection to the database.",
    "app.error_page.fix_problems_with_db.1":
        'Please check first that you are not in a so-called "private window" of your browser. In this case the data cannot be loaded.',
    "app.error_page.fix_problems_with_db.2": "Please reload the page again.",
    "app.error_page.fix_problems_with_db.3":
        "If the problem persists or occurs repeatedly, please write to us briefly at {email}. We take care of this.",
    "app.error_page.not_found": "This page does not exist.",
    "app.error_page.button.back_home": "Home",

    // validation
    "app.validation.error.field_empty": "The field cannot be empty",
    "app.global.error": "Unexpected error.",
    "app.validation.error.short_password": "Password must be at least {length} symbols",
    "app.validation.error.passwords_not_match": "Passwords do not match",
    "app.validation.error.invalid_email": "E-mail address is not valid",
    "app.validation.error.strictLength": "{length} signs",
    "app.validation.error.consent_required": "Please accept to continue",

    "app.error.message.incomplete_data": "The data is incomplete. Please check the entries ({reason})",
    "app.error.message.bank_not_available": "The Bank is not available in the selected year",
    "app.error.message.kb_not_available": "The Ledger is not available in the selected year",
    "app.error.message.some_incorrect": "Some entries are incorrect",
};

export const messagesEn: IMessagesType = {
    ...auth,
    ...errors,

    "app.header.workplace": "Working place",
    "app.header.others": "Other programs",
    "app.header.navigation": "Navigation",
    "app.header.logout": "Log out",
    "app.header.login": "Log in",
    "app.releaseNotes.title": "What's new",
    "app.releaseNotes.markAllRead": "Mark all as read",
    "app.releaseNotes.clearAll": "Clear all",
    "app.releaseNotes.clear": "Clear",
    "app.releaseNotes.readMore": "Read more",
    "app.releaseNotes.showAll": "All release notes",
    "app.releaseNotes.empty": "No new updates",
    "app.releaseNotes.version": "Version {version}",
    "app.releaseNotes.unread": "{count} unread",
    "app.releaseNotes.germanOnly": "Release notes are available in German only.",
    "app.releaseNotes.history.versions": "Versions",
    "app.releaseNotes.history.latest": "Latest",
    "app.releaseNotes.history.title": "Release notes",
    "app.releaseNotes.history.empty": "No release notes yet.",
    "app.releaseNotes.history.error": "The release notes could not be loaded.",
    "app.settings.button.columns.text": "Columns",
    "app.settings.button.columns.title": "Select columns",
    "app.companies.view.table.admin": "Main user",
    "app.companies.view.table.user": "User",
    "app.companies.filter.datev": "DATEV",
    "app.companies.filter.withDatev": "With DATEV",
    "app.companies.filter.withoutDatev": "Without DATEV",
    "app.companies.filter.lockStatus": "Lock status",
    "app.companies.filter.locked": "Locked",
    "app.companies.filter.unlocked": "Unlocked",
    "app.companies.loadError": "Companies could not be fully loaded. Please reload the page.",
    "app.companies.detail_request.select_skr": "Please select SKR of your company",
    "app.companies.detail_request.select_kb": "Create or select the Ledger",
    "app.companies.detail_request.select_bank": "Create or select the Bank",
    "app.companies.permission": "Permission",
    "app.companies.permission.no": "No",
    "app.companies.permission.read": "Audit (Read)",
    "app.companies.permission.write": "Participant (Write)",
    "app.companies.permission.admin": "Admin",
    "app.companies.permission.admin-read": "Admin Read",
    "app.profile.select.title": "Language",
    "app.profile.button.change_password": "Change password",
    "app.profile.button.re_login": "Login",
    "app.profile.email_verification.success_message": "Email confirmed successfully.",
    "app.profile.email_verification.fail_message": "Email verification failure",
    "app.messages.no_elements_found": "No elements found",
    "app.message.no_document_found": "No document found",
    "app.items.create_tag": "Create Tag",
    "app.items.create_creditor": "Create Creditor",
    "app.items.create_debitor": "Create Debitor",
    "app.items.create_category": "Create Account/Category",
    "app.items.create_article": "Create an Article",

    "app.titles.ER": "Purchase Invoices",
    "app.titles.ER_A": "Prepayments",
    "app.titles.Deb": "Outgoing Invoices",
    "app.titles.FE": "Free input",
    "app.titles.POS": "POS accounting",
    "app.titles.KB": "Ledger",
    "app.titles.Bank": "Bank",
    "app.titles.Bank.pl": "Banks",
    "app.titles.Common": "Common",
    "app.titles.SuSa": "Summen und Salden",
    "app.titles.Vat": "VAT Report",
    "app.titles.KB.pl": "Ledgers",
    "app.titles.ER.erfassung": "Compilation",
    "app.titles.ER.rechnungen": "Bills",
    "app.titles.ER.auswertung": "Analysis",
    "app.titles.filtern": "Filters and Search",
    "app.titles.ER.festschreibung": "Festschreibung",
    "app.titles.ER.za": "Payment list",
    "app.titles.Lager": "Warehouse",
    "app.titles.LA": "Pay Slip",
    "app.titles.AVM": "Fixed Assets",
    "app.titles.OCR": "PDF Tool",
    "app.titles.KA": "Account overview",
    "app.titles.calendar": "Calendar",
    "app.titles.Contact": "Contact",
    "app.titles.Contacts": "Contacts",
    "app.titles.DMS": "Documents",
    "app.titles.Banks": "Banks",
    "app.titles.CashRegister": "Cash Book",
    "app.titles.Invoices": "Invoicing",
    "app.titles.payments": "Payments",
    "app.titles.templates": "Templates",
    "app.titles.data_export": "Data Export",
    "app.titles.data_import": "Data Import",
    "app.titles.settings": "Settings",
    "app.titles.settings.manage": "Manage",
    "app.titles.settings.formSettings": "Form settings",
    "app.titles.settings.optionalFields": "Optional fields",
    "app.titles.security": "Security",
    "app.titles.company": "Company",
    "app.titles.company.pl": "Companies",
    "app.titles.category": "Category",
    "app.titles.category.pl": "Categories",
    "app.titles.creditor": "Creditor",
    "app.titles.creditor.pl": "Creditors",
    "app.titles.debitor": "Debitor",
    "app.titles.debitor.pl": "Debitors",
    "app.titles.tag": "Tag",
    "app.titles.tag.pl": "Tags",
    "app.titles.postingRules": "Posting rules",
    "app.titles.article": "Article",
    "app.titles.article.pl": "Articles",
    "app.titles.profile": "Profile",
    "app.titles.kontenverwaltung": "Account management",
    "app.titles.import_export": "Import/Export",
    "app.titles.accounting": "Accounting",
    "app.titles.contactPerson": "Contact Person",
    "app.titles.legalInformation": "Legal Information",
    "app.titles.termsOfPayment": "Terms of payment",
    "app.titles.communication": "Communication",
    "app.titles.contacts.client": "Customer",
    "app.titles.contacts.partner": "Vendor",
    "app.titles.contacts.other": "Other (Free input)",
    "app.titles.contacts.employee": "Employee",
    "app.titles.contacts.shareholder": "Shareholder",
    "app.titles.contacts.bankDetails": "Bank details",
    "app.titles.contacts.classification": "Classification",
    "app.titles.bulkEdit": "Multi-Change",

    "app.bulkEdit.buGate.mixedScope":
        "Cannot change BU without a category: the selected records use accounts with mixed tax scopes (input and output VAT).",
    "app.bulkEdit.buGate.autoBu": "Cannot change BU without a category: at least one account is an automatic account.",
    "app.bulkEdit.buGate.noVatScope":
        "Cannot change BU without a category: at least one account has no tax scope (input/output VAT).",
    "app.bulkEdit.buGate.nonCategoryItems":
        "Cannot change BU without a category: at least one record is booked against a creditor/debitor instead of a general ledger account.",
    "app.bulkEdit.buGate.kust": "Cannot change BU: small business taxation (§ 19 UStG).",
    "app.bulkEdit.buGate.unresolvedCategory":
        "Cannot change BU without a category: at least one account could not be resolved in the chart of accounts.",
    "app.bulkEdit.splitHint.question": "Where are the other fields?",
    "app.bulkEdit.splitHint.explanation":
        "The selection contains split records with more than one position. Fields that belong to a single position cannot be applied unambiguously, so they are hidden. Only fields that apply to the whole record stay editable.",

    "app.postingRules.metro.category": "Category",
    "app.postingRules.metro.default": "Default",
    "app.postingRules.metro.own": "Own account",
    "app.postingRules.metro.sentinel": "no account, the bookkeeper decides",
    "app.postingRules.hint.metro":
        "Lines of Metro invoices are assigned to a category. The account comes from this table; the AI is not asked.",
    "app.postingRules.direction.incoming": "Incoming invoices",
    "app.postingRules.direction.outgoing": "Outgoing invoices",
    "app.postingRules.section.corpus": "Data basis",
    "app.postingRules.section.patterns": "Booking patterns",
    "app.postingRules.section.corrections": "Corrections",
    "app.postingRules.tile.skr": "Chart of accounts SKR {skr}",
    "app.postingRules.tile.ownRows": "Reviewed bookings",
    "app.postingRules.tile.pending": "Not yet available",
    "app.postingRules.tile.shared": "Comparison bookings of other companies",
    "app.postingRules.tile.shared.active": "active",
    "app.postingRules.tile.shared.inactive": "inactive",
    "app.postingRules.tile.corrections": "{total} corrections, {inPrompt} in the prompt",
    "app.postingRules.column.supplier": "Supplier",
    "app.postingRules.column.customer": "Customer",
    "app.postingRules.column.sachkonto": "Account",
    "app.postingRules.column.bu": "Tax key",
    "app.postingRules.column.lines": "Booking lines",
    "app.postingRules.column.share": "Share",
    "app.postingRules.column.lastBooking": "Last booking",
    "app.postingRules.column.from": "AI suggestion",
    "app.postingRules.column.to": "Booked",
    "app.postingRules.column.count": "Count",
    "app.postingRules.column.lastSeen": "Last seen",
    "app.postingRules.column.inPrompt": "Used in the prompt",
    "app.postingRules.column.counterparty": "Counterparty",
    "app.postingRules.column.purpose": "Purpose",
    "app.postingRules.column.rate": "VAT rate",
    "app.postingRules.column.status": "Status",
    "app.postingRules.column.applied": "Applied",
    "app.postingRules.column.updatedBy": "Changed by",
    "app.postingRules.unknownSupplier": "Unknown supplier",
    "app.postingRules.unknownCustomer": "Unknown customer",
    "app.postingRules.purpose.any": "Any purpose",
    "app.postingRules.purpose.general": "General",
    "app.postingRules.purpose.shipping": "Shipping",
    "app.postingRules.purpose.pfand": "Deposit",
    "app.postingRules.purpose.logistic_surcharge": "Logistics surcharge",
    "app.postingRules.purpose.ec_service_fee": "EC service fee",
    "app.postingRules.purpose.ec_zero_tax_fee": "EC fee (no VAT)",
    "app.postingRules.purpose.tip": "Tip",
    "app.postingRules.purpose.global_discount": "Overall discount",
    "app.postingRules.purpose.third_party_funded_discount": "Third-party-funded discount",
    "app.postingRules.purpose.authority_fee": "Authority fee",
    "app.postingRules.purpose.other": "Other",
    "app.postingRules.rate.any": "Any rate",
    "app.postingRules.status.draft": "Draft",
    "app.postingRules.status.active": "Active",
    "app.postingRules.status.rejected": "Rejected",
    "app.postingRules.warning.unknownKonto": "Unknown account",
    "app.postingRules.applied.never": "{count}×",
    "app.postingRules.applied.withDate": "{count}× last {date}",
    "app.postingRules.rules.filter.current": "Active / draft",
    "app.postingRules.rules.filter.rejected": "Rejected",
    "app.postingRules.rules.hint.status":
        "Only active rules are applied when posting. Drafts must be confirmed first, rejected rules are never applied.",
    "app.postingRules.rules.loadError": "The company rules could not be loaded.",
    "app.postingRules.rules.empty.title": "No company rules yet",
    "app.postingRules.rules.empty.description": "No rules have been set up for this direction yet.",
    "app.postingRules.rules.action.edit": "Edit rule",
    "app.postingRules.rules.action.confirm": "Confirm rule",
    "app.postingRules.rules.action.reject": "Reject rule",
    "app.postingRules.rules.editor.new": "New rule",
    "app.postingRules.rules.import": "Import suggestions",
    "app.postingRules.rules.import.result":
        "{count, plural, =0 {No new suggestions} one {# rule imported} other {# rules imported}}",
    "app.postingRules.rule.create": "Create rule",
    "app.postingRules.hint.corpus":
        "The AI learns from reviewed bookings (review status OK, not a draft, not cancelled). New bookings are usually available from the next day.",
    "app.postingRules.hint.retrieval":
        "For every new invoice the AI looks up the {retrieveK} most similar own bookings, mainly of the same supplier with similar lines, and follows their accounts.",
    "app.postingRules.hint.shared":
        "While there are fewer than {sharedBelow} own bookings, the AI adds anonymised bookings of other companies, without personal names or booking texts.",
    "app.postingRules.hint.corrections":
        "A reviewed booking that differs from the AI suggestion counts as a correction. The {correctionsLimit} newest corrections take precedence over booking patterns.",
    "app.postingRules.hint.confidence":
        "Suggestions with a confidence below {confidence} % are not applied; the field stays empty.",
    "app.postingRules.examples.unknown": "Without contact",
    "app.postingRules.examples.open": "Show indexed bookings",
    "app.postingRules.examples.routing": "Sachkonto {sachkonto}, BU {bu}",
    "app.postingRules.examples.empty": "No indexed bookings for this pattern.",
    "app.postingRules.hint.examples":
        "These are the indexed sentences of the bookings behind this pattern. The AI compares exactly these sentences with a new invoice.",
    "app.postingRules.hint.direction": "Incoming and outgoing invoices are evaluated separately.",
    "app.postingRules.patterns.empty.title": "No booking patterns yet",
    "app.postingRules.patterns.empty.description": "Reviewed bookings usually appear here from the next day.",
    "app.postingRules.corrections.empty.title": "No corrections yet",
    "app.postingRules.corrections.empty.description":
        "When a reviewed booking differs from the AI suggestion in Sachkonto or BU, it appears here.",
    "app.postingRules.ai.loadError":
        "The evidence could not be loaded. You may lack read access for this invoice type.",
    "app.postingRules.metro.loadError": "The Metro categories could not be loaded.",
    "app.postingRules.metro.ownLoadError":
        'The company\'s own accounts could not be loaded, so the "Own account" column stays empty. Read access to incoming invoices is required.',
    "app.postingRules.truncated": "Showing only the first {shown} of {total} entries.",
    "app.postingRules.tab.metro": "Metro categories",
    "app.postingRules.tab.rules": "Company rules",
    "app.postingRules.tab.ai": "AI posting patterns",
    "app.postingRules.hint.label": "Hint",
    "app.postingRules.hint.order":
        "Posting order: Metro categories first, then company rules, then the AI. An automatic account always sets its own tax key. A contact's booking template only fills fields that are still empty afterwards.",

    "app.titles.reconciliation": "Reconciliation",
    "app.titles.copy_data_from": "Copy new Accounts from",
    "app.titles.bind_to_bank": "Associate to Accounting Bank",
    "app.titles.bind_to_kb": "Associate to Accounting Ledger",
    "app.titles.unbind": "Remove association",
    "app.titles.bind_to_banking_bank": "Associate to Banking Bank",
    "app.titles.create_kb": "Add a Bank",
    "app.titles.update_kb": "Update the Bank",
    "app.titles.create_bank": "Add a Bank",
    "app.titles.update_bank": "Update the Bank",
    "app.titles.create_subtype": "Add subfolder",
    "app.titles.update_subtype": "Edit subfolder",

    "app.button.login": "Log in",
    "app.button.register": "Register",
    "app.button.confirm": "Confirm",
    "app.button.dmsSave": "save",
    "app.button.dmsSaveAndClose": "save and close",
    "app.button.aiReview": "AI Review",
    "app.button.send": "Send",
    "app.button.send_to_dms": "to DMS",
    "app.button.save": "Save",
    "app.button.saveAll": "Save all",
    "app.button.saveSelected": "Save selected",
    "app.button.create": "Create",
    "app.button.add": "Add",
    "app.button.cancel": "Cancel",
    "app.button.confAndClose": "Confirm and close",
    "app.button.close": "Close",
    "app.button.finish": "Finish",
    "app.button.done": "Done",
    "app.button.apply": "Apply",
    "app.button.unselect": "Unselect",
    "app.button.buchen": "Book",
    "app.button.delete": "Delete",
    "app.button.bind": "Associate",
    "app.button.unbind": "Remove association",
    "app.button.bind_banking": "Associate Banking",
    "app.button.unbind_banking": "Remove banking association",
    "app.button.bind_cash_register": "Associate Cash Register",
    "app.button.unbind_cash_register": "Remove cash register association",
    "app.button.storno": "Cancel record",
    "app.button.edit": "Edit",
    "app.button.view": "View",
    "app.button.copy": "Copy",
    "app.button.download": "Download",
    "app.button.select": "Select",
    "app.button.loading": "Loading...",
    "app.button.saving": "Saving...",
    "app.title.color": "Color",
    "app.button.color.remove_color": "Remove color",
    "app.button.color.red": "Red",
    "app.button.color.blue": "Blue",
    "app.button.color.purple": "Purple",
    "app.button.color.yellow": "Yellow",
    "app.button.color.green": "Green",
    "app.button.reorganise": "Reorganise",
    "app.button.upload": "Upload",
    "app.button.next": "Next",
    "app.button.back": "Back",
    "app.button.reset": "Reset",
    "app.button.default": "Default",
    "app.button.update": "Update",
    "app.button.openInNewTab": "Open in new Tab",
    "app.button.export": "Export",

    "app.confirmation.pdf.finish": "Finish the job?",
    "app.confirmation.record_remove.body": "Do you really want to remove this record?",
    "app.confirmation.invoice_remove.body": "Do you really want to remove this invoice?",
    "app.confirmation.record_cancel.body": "Do you really want to cancel this record?",
    "app.confirmation.festschreiben_edit.body": "This record is journaled! Do you really want to edit this record?",
    "app.kb.journal.no_items_tooltip":
        "No records selected, Saldo for some selected records is negative or previous records are not journaled",
    "app.confirmation.header": "Confirmation",
    "app.confirmation.header.record_delete": "Delete record!",
    "app.confirmation.header.record_cancel": "Cancel record",
    "app.confirmation.header.confirmed_edit": "Edit confirmed record!",
    "app.confirmation.header.festschreiben_edit": "Edit journaled record!",
    "app.confirmation.body": "Please confirm!",
    "app.fields.lfdnr": "#",
    "app.fields.nr": "Nr.",
    "app.fields.date": "Date",
    "app.fields.dateTo": "Date to",
    "app.auswertung.loadedYears.title": "Loaded years",
    "app.auswertung.loadedYears.info":
        "For performance reasons, only postings from the selected year and the preceding and following year are loaded. To see postings from other years, please select the corresponding year.",
    "app.fields.date.month": "Month",
    "app.fields.date.year": "Year",
    "app.fields.date.erfassung": "Input period",
    "app.fields.creditor": "Creditor",
    "app.fields.debitor": "Debitor",
    "app.fields.creditor_view": "Creditor view",
    "app.fields.konto_view": "Account view",
    "app.fields.bezeichnung": "Name",
    "app.fields.name": "Name",
    "app.fields.contact": "Contact",
    "app.fields.bankDetails": "Bank details",
    "app.fields.products": "Products",
    "app.fields.rechnung_num": "Invoice Nr.",
    "app.fields.internal_num": "Internal Nr.",
    "app.fields.konto": "Account",
    "app.fields.gkonto": "Out. Account",
    "app.fields.gkonto.full": "Outgoing Konto",
    "app.fields.brutto": "Brutto",
    "app.fields.betrag": "Amount",
    "app.fields.betragType": "Way",
    "app.fields.totalAmount": "Total amount",
    "app.fields.partialAmount": "Partial amount",
    "app.fields.originalAmount": "Original Amount",
    "app.fields.currency.rate": "Rate",
    "app.fields.vat%": "VAT, %",
    "app.fields.vat": "VAT",
    "app.fields.netto": "Netto",
    "app.fields.open": "Open",
    "app.fields.buchtext": "Product/Service",
    "app.fields.buchtext2": "Additional information",
    "app.fields.freigabe": "Approval",
    "app.fields.skonto": "Discount",
    "app.fields.zugewiesen": "Assigned",
    "app.fields.deadlineDays": "Deadline days",
    "app.fields.dueDate": "Due date",
    "app.fields.discountDays": "Discount days",
    "app.fields.discountDate": "Discount date",
    "app.fields.discountPercent": "Discount percent",
    "app.fields.discountAmount": "Discount amount",
    "app.fields.notes": "Notes",
    "app.fields.verrechnung": "Prepayment",
    "app.fields.wählen": "choose",
    "app.fields.rabatt": "Rabatt",
    "app.fields.offner_betrag": "Outstanding Amount",
    "app.fields.lastschrift": "Direct Debit/Lastschrift",
    "app.fields.draft": "Draft",
    "app.fields.uberweisung": "Bank transfer",
    "app.fields.barzahlung": "Cash payment",
    "app.fields.kartenzahlung": "Card payment",
    "app.fields.sonstiges": "Other",
    "app.fields.falligkeit": "Due date",
    "app.fields.actions": "Actions",
    "app.fields.saldo": "Saldo",
    "app.fields.payment": "Payment",
    "app.fields.konto_len": "Account length",
    "app.fields.color": "Color",
    "app.fields.user": "User",
    "app.fields.schleppend": "Follow",
    "app.fields.search": "Search",
    "app.fields.address": "Address",
    "app.fields.company_name": "Company name",
    "app.fields.producer": "Producer name",
    "app.fields.description": "Description",
    "app.fields.unit": "Unit",
    "app.fields.taxation": "Taxation of VAT",
    "app.fields.favouritePaymentType": "Main means of payment",
    "app.fields.datevNrConsultant": "Consultant number",
    "app.fields.datevNrCompany": "Client number",
    "app.fields.countryCode": "Country",
    "app.fields.euroCountryCode": "EU-Country",
    "app.fields.postcode": "Postcode",
    "app.fields.city": "Place",
    "app.fields.street": "Street",
    "app.fields.house": "House Nb.",
    "app.fields.additionalAddress": "Additional address",
    "app.fields.vatNb": "VAT Number",
    "app.fields.wIdNr": "W-IdNr.",
    "app.fields.wIdNr.invalid": "Invalid business identification number",
    "app.fields.wIdNr.missingSuffix": "Distinguishing suffix missing - full format: DE123456788-00001",
    "app.fields.taxNumber": "Tax Number",
    "app.fields.typeOfRegister": "Type of Register",
    "app.fields.registerCourt": "Register court",
    "app.fields.registerNumber": "Register number",
    "app.fields.13bUStG": "Option § 13b UStG",
    "app.fields.paymentPurpose": "Purpose of payment",
    "app.fields.paymentMethod": "Payment method",
    "app.fields.days": "Days",
    "app.fields.phone": "Phone",
    "app.fields.internalDesignation": "Internal designation",
    "app.fields.type": "Type",
    "app.fields.subType": "Subtype",
    "app.fields.companyName": "Company name",
    "app.fields.companyNameWithLegalForm": "Company Name (incl. Legal form)",
    "app.fields.legalForm": "Legal form",
    "app.fields.createdAt": "Created at",
    "app.fields.salutation": "Salutation",
    "app.fields.title": "Title",
    "app.fields.firstName": "First name",
    "app.fields.lastName": "Last name",
    "app.fields.customerNumber": "Customer number",
    "app.fields.ownerEmail": "Owner email",
    "app.fields.ownerEmail.hint": "This person must already be registered. They will become the owner of this company.",
    "app.fields.accountOwner": "Account owner",
    "app.fields.bankCodeNumber": "Bank code number",
    "app.fields.bankAccountNumber": "Bank account number",
    "app.fields.bankName": "Bank name",
    "app.fields.productService": "Product/Service",
    "app.fields.position": "Position",
    "app.fields.department": "Department",
    "app.fields.currency": "Currency",
    "app.fields.comment": "Comment",
    "app.fields.module": "Module",
    "app.fields.attachment": "Attachment",
    "app.fields.invoice": "Invoice",
    "app.fields.creditNote": "Credit note",

    "app.fields.fileUrl": "File URL",
    "app.fields.numPages": "Total pages",
    "app.fields.rowNumber": "Nr.",

    "app.fields.recordReview": "Review",
    "app.fields.recordReview.ok": "OK",
    "app.fields.recordReview.new": "New",
    "app.fields.recordReview.changed": "Changed",
    "app.fields.recordReview.question": "Question",
    "app.fields.recordReview.error": "Error",
    "app.fields.recordAiReview": "AI Review",
    "app.fields.recordAiReview.valid": "AI confirmed",
    "app.fields.recordAiReview.invalid": "AI mismatch",
    "app.fields.recordAiReview.unchecked": "unchecked",
    "app.fields.recordAiReview.checks.booking_date": "Booking date",
    "app.fields.recordAiReview.checks.brutto_amount": "Gross amount",
    "app.fields.recordAiReview.checks.vat_amount": "VAT amount",

    // PDF Stats filters
    "app.pdf.stats.date": "Date",
    "app.pdf.stats.module": "Module",
    "app.pdf.stats.user": "User",
    "app.pdf.stats.company": "Company",
    "app.pdf.stats.search": "Search...",
    "app.pdf.stats.csv": "CSV",
    "app.pdf.stats.loading": "Fetched {done}/{total}",

    "app.fields.ertrag": "Income",
    "app.fields.aufwand": "Expense",

    "app.fields.purpose": "Purpose",
    "app.fields.myIban": "My bank’s IBAN",
    "app.fields.myBankAccount": "My bank account",
    "app.fields.counterpartyName": "Counterparty name",
    "app.fields.counterpartyIbanOrAccountNum": "Counterparty Iban/Account",
    "app.fields.counterpartyBicOrBlz": "Counterparty BIC/BLZ",
    "app.fields.transactionType": "Transaction type",
    "app.fields.transactionAmount": "Amount",
    "app.fields.openingBalance": "Opening balance",
    "app.fields.openingBalanceDate": "Opening balance date",
    "app.fields.bankTitle": "Internal bank name",

    "app.labels.allYear": "Entire Year",
    "app.labels.paymentConditions": "Payment conditions",

    "app.components.split.title": "Bill separation",
    "app.components.editing": "Editing",
    "app.components.not_selected": "Not selected",
    "app.components.all_selected": "All selected",
    "app.components.nselected": "Selected",
    "app.components.all": "All",
    "app.components.all_months": "All months",
    "app.components.used": "My List",
    "app.components.creating": "Creating",
    "app.components.date.range_error": "Date should be in range",
    "app.components.table.no_items": "No items",
    "app.components.form.attachment": "Attach file",
    "app.components.form.clear": "Clear",

    // "app.cookie":
    // "This website might uses cookies to ensure that we give you the best experience on our website. If you continue we assume that you consent to receive all cookies.",
    "app.notification.update": "We have just released new version. Please reload the page to stay up to date!",
    "app.notification.copiedToClipboard": "Field copied",
    "app.notification.copyToClipboardFailed": "Copy to clipboard failed",

    "app.global.email": "E-Mail address",
    "app.global.password": "Password",
    "app.global.password_repeat": "Repeat password",
    "app.global.error": "Unexpected error.",
    "app.global.login_now": "Log in now",
    "app.global.register_now": "Register now",
    "app.global.do_not_have_account": "New client?",
    "app.global.already_have_account": "Already have account?",
    "app.global.not_selected": "Not selected",
    "app.global.total": "Total",
    "app.global.components.payment_connection.created": "Record in Module {module} was automatically created.",
    "app.global.components.payment_connection.updated": "Record in Module {module} was automatically updated.",
    "app.global.components.payment_connection.removed": "Record in Module {module} was removed.",
    "app.global.components.payment_connection.markedRemoved": "Record in Module {module} was marked as removed.",
    "app.global.components.num_corrector.title": "Assignment to the new number",
    "app.global.yes": "Yes",
    "app.global.no": "No",
    "app.global.step": "Step",
    "app.global.previous_step": "Previous Step",
    "app.global.bank": "Bank",
    "app.global.confirmRemoveInfo": "Do you really want to delete this information?",
    "app.global.confirmSentToTrash": "Send to Trash",
    "app.global.confirmRemoveDocument": "Delete permanently?",
    "app.global.saveChanges": "Save Changes?",
    "app.global.binaleNeedUpdate": "New Binale version is available. Please update the program!",
    "app.global.onlyNumbersHint": "only numbers are allowed",

    "app.payments.verrechnungen": "Select a prepayment.",
    "app.paymentConnections.record": "Record",
    "app.paymentConnections": "Payment connection",
    "app.paymentConnections.helperText": "There are some records where we could assign this payment",
    "app.paymentConnections.deleteAssignment": "Delete assignment",
    "app.paymentConnections.saveAssignment": "Save assignment",
    "app.paymentConnections.showAllOpenItems": "Show all open items",
    "app.paymentConnections.showClosedItems": "Include closed items",
    "app.paymentConnections.multiHint": "You can assign several invoices in this step",
    "app.paymentConnections.skontoSignHint":
        "The selected record in {product} uses a {sign, select, negative {negative} other {positive}} amount. Please use the same sign for the discount amount.",
    "app.paymentConnections.recordsWillBeCreated": "{n} new records will be created",
    "app.paymentConnections.differenz": "Difference",

    "app.contacts.new": "Create Contact",
    "app.contacts.type.organization": "Organization",
    "app.contacts.type.individual": "Natural Person",
    "app.contacts.type.institution": "Institution",
    "app.contacts.add": "Add an adress",
    "app.contacts.confirmRemoveContact": "Do you really want to delete this conctact?",

    "app.message.progress": "Action in progress...",
    "app.message.success": "Action successfully completed",
    "app.dms.delete.movedToTrash": "{count, plural, one {1 document} other {# documents}} moved to trash",
    "app.dms.delete.hardDeleted": "{count, plural, one {1 document} other {# documents}} permanently deleted",
    "app.dms.delete.keptAttached":
        "{count, plural, one {1 document stays} other {# documents stay}} in trash - still attached",
    "app.message.userSelectCompany": "Active company was switched",
    "app.message.companyCreateBank": "Bank was created",
    "app.message.companyCreateKB": "Ledger (Kasse) was created",
    "app.message.companyUpdateBank": "Bank was updated",
    "app.message.companyUpdateKB": "Ledger (Kasse) was updated",
    "app.message.companyDeleteBank": "Bank was removed",
    "app.message.companyDeleteKB": "Ledger (Kasse) was removed",
    "app.message.companySetProducts": "Company products were updated",
    "app.companies.accountingProductsEnabledReload": "The page will be reloaded now.",
    "app.message.companyCreate": "Company was created",
    "app.message.companyUpdate": "Company was updated",
    "app.message.companyDelete": "Company was removed",
    "app.message.companySaveMember": "Company member was saved",
    "app.message.companyDeleteMember": "Company member was removed",
    "app.message.nothingSelected": "Please select the type of contact ({ relations }) in the “General“ selection.",

    "app.tooltip.manageFavouritePaymentType": "Manage favourite payment type",
    "app.tooltip.noOpenItems": "no open items",
    "app.tooltip.form.correct": "Please correct {amount} {currency}",

    "app.ocr.button.checkAll": "check all",
    "app.ocr.button.deleteEmptyPages": "delete empty pages",
    "app.ocr.button.hideDeletedPages": "hide deleted pages",
    "app.ocr.button.showDeletedPages": "show deleted pages",
    "app.ocr.button.deletePage": "delete page",
    "app.ocr.button.restorePage": "restore page",
    "app.ocr.button.rotatePage": "rotate",
    "app.ocr.button.splitDocs": "split docs",
    "app.ocr.button.unifyDocs": "unify docs",
    "app.ocr.button.uncheckAll": "uncheck all",
    "app.ocr.button.addDocument": "add document",
    "app.ocr.button.selectDocument": "Select document",

    "app.ocr.parking": "Parking",
    "app.ocr.page": "Page",
    "app.ocr.document": "Document",
    "app.ocr.allPages": "All pages",
    "app.ocr.totalPages": "Total pages",
    "app.ocr.documentsNumber": "Number of documents",
    "app.ocr.selectedPages": "Selected pages",

    "app.ocr.mode.put": "Put together",
    "app.ocr.mode.qr": "Separate (QR-Code)",
    "app.ocr.mode.every": "Separate (every Page)",
    "app.ocr.mode.second": "Separate (every second Page)",
    "app.ocr.mode.manual": "Manual processing",

    "app.ocr.error.encrypted": "The selected document is encrypted or protected and cannot be processed.",
    "app.ocr.dragDocument": "Drag the required document here",
    "app.ocr.placeDocumentHere": "Place document here",

    "app.titles.taxes": "Taxes",
    "app.titles.contracts": "Contracts",
    "app.titles.year_report": "Year Report",
    "app.titles.uncategorized": "Uncategorized",

    "app.dms.add_documents": "Add {isSingular, select, true {Document} other {Documents}}",
    "app.titles.new_documents": "New documents",

    "app.dms.typeOfDocuments": "Folder",
    "app.titles.trash": "Deleted documents",
    "app.dms.add_subtype": "add item",

    "app.dms.list": "LIST",
    "app.dms.grid": "Cards",

    "app.dms.empty": "Empty",
    "app.dms.fileName": "File name",
    "app.dms.documentDate": "Date",
    "app.dms.documentNumber": "Document number",
    "app.dms.userName": "User name",
    "app.dms.fileInformation": "File information",
    "app.dms.createdBy": "Created by",
    "app.dms.createdAt": "Created at",
    "app.dms.updatedAt": "Updated at",
    "app.dms.hasXRechnung": "E",
    "app.dms.hasMarkdown": "MD",
    "app.dms.comment": "Comment",
    "app.dms.status": "Status",
    "app.dms.closeEditor": "Close Editor",
    "app.dms.total": "Total",
    "app.dms.totalDocuments": "{count, plural, one {# document} other {# documents}}",

    "app.dms.search.mode.quick": "Quick",
    "app.dms.search.mode.fulltext": "Full text",
    "app.dms.search.mode.semantic": "AI search",
    "app.dms.search.mode.label": "Search mode",
    "app.dms.search.placeholder.fulltext": "Full-text search, press Enter",
    "app.dms.search.placeholder.semantic": "AI search, press Enter",
    "app.dms.search.noResults": "No documents found",
    "app.dms.search.error": "Search failed",

    "app.dms.selectAll": "Select all",
    "app.dms.copyId": "Copy ID",
    "app.dms.datevBeleg": "DATEV document",
    "app.dms.eInvoiceBeleg": "E-invoice",
    "app.dms.eInvoiceBelegHint":
        "This e-invoice is sent to DATEV unchanged. The other documents of this booking are not sent.",
    "app.dms.datevBeleglinkHint": "The first up to 5 documents (max. 20 MB) are merged into one PDF and sent to DATEV.",
    "app.dms.primaryBeleg": "Primary document",
    "app.dms.primaryBelegHint": "This document is in first position and counts as the primary document.",
    "app.dms.documentPosition": "Position {position}",
    "app.dms.copyUrl": "Copy URL",
    "app.dms.idCopied": "ID copied",
    "app.dms.idCopyFailed": "ID copy failed",
    "app.dms.urlCopied": "URL copied",
    "app.dms.clickOrDrag":
        "Click or drag {isSingular, select, true {file} other {files}} (up to 20M) to this area to upload",
    "app.dms.supportFor": "Multiple selection is possible",
    "app.dms.identicalFiles": "Identical files. Select one file to upload and click Confirm",
    "app.dms.confirmResolving": "Confirm selection",
    "app.dms.deleteAll": "Delete all",
    "app.dms.view": "View",
    "app.dms.moveTo": "Move to",
    "app.dms.emptyDate": "Without date",

    "app.dms.complete": "Complete",
    "app.dms.allDocuments": "All documents",
    "app.dms.changeModeEnabled": "Change mode enabled",
    "app.dms.noticeOfDelete": "Notice of delete mode",
    "app.dms.cantDelete": "You can't delete this document from bucket",
    "app.dms.moveSelectedDocuments": "Move selected documents to preferred type!",
    "app.dms.pages": "{count, plural, one {# page} other {# pages}}",
    "app.dms.mainTypes": "Main types",
    "app.dms.tooltipDocumentNumber": "Please use the following characters",
    "app.dms.letters": "Letters",
    "app.dms.number": "Number",
    "app.dms.specialSymbols": "Special symbols",

    "app.dms.paymentStatus.offen": "open",
    "app.dms.paymentStatus.avis": "AVIS",
    "app.dms.paymentStatus.bezahlt": "paid",
    "app.dms.paymentStatus.storno": "Storno",
    "app.dms.vat": "VAT",
    "app.dms.corporation_tax": "Corporate income tax",
    "app.dms.business_tax": "Trade tax",
    "app.dms.statement": "Separate and uniform determination",
    "app.dms.income_tax": "Income tax",
    "app.dms.other_taxes": "Other taxes",
    "app.dms.tax_office": "Tax office",
    "app.dms.more": "More",
    "app.dms.isAttached": "with accounting connected",
    "app.dms.isTransactionAttached": "with banking transaction connected",
    "app.dms.isCashRegisterAttached": "with cash register transaction connected",
    "app.dms.filterConnected": "connected",
    "app.dms.filterNotConnected": "not connected",
    "app.dms.filterAccountingModule": "Accounting Module",
    "app.dms.filterAccountingModuleYearHint":
        "Only available with a selected year. Records are loaded for the selected year only, so without a year the module filter would be incomplete.",
    "app.dms.filterActive": "filter is active",
    "app.dms.selectBank": "select a bank",
    "app.dms.selectCashJournal": "select a cash journal",
    "app.dms.selectTaxes": "select a tax",
    "app.dms.noInternetConnection": "No internet connection",
    "app.dms.addDocumentId": "Add document-ID",
    "app.dms.selectInDms": "Select in DMS",
    "app.dms.noAssignment": "No assignment",
    "app.dms.filterColor": "Filter color",
    "app.dms.logs": "Log",

    "app.dms.payments.title": "Payments",
    "app.dms.payments.documentAmount": "Invoice amount",
    "app.dms.payments.openAmount": "Open amount",
    "app.dms.payments.paymentAmount": "Payment amount",
    "app.dms.payments.discountPercent": "Discount %",
    "app.dms.payments.discountAmount": "Discount amount",
    "app.dms.payments.account": "Bank / Cash register",
    "app.dms.payments.accountKind.bank": "Bank",
    "app.dms.payments.accountKind.kasse": "Cash register",
    "app.dms.payments.source.bankTransaction": "from bank transaction",
    "app.dms.payments.source.cashRegisterTransaction": "from cash register transaction",
    "app.dms.payments.paymentGroup": "Batch transfer",
    "app.dms.payments.noPayments": "No payments yet",
    "app.dms.payments.deleteConfirm": "Delete payment?",
    "app.dms.payments.deleteConfirmDesc": "Do you really want to delete this payment?",
    "app.dms.payments.lockedTooltip": "Locked by batch transfer",
    "app.dms.payments.viewGroup": "View batch transfer",
    "app.dms.payments.document": "Document",
    "app.dms.payments.goToBank": "Go to bank",
    "app.dms.payments.deleteGroupConfirm": "Delete batch payment?",
    "app.dms.payments.deleteGroupConfirmDesc": "All associated payments will also be deleted.",

    "app.invoice.createInvoice": "Create invoice",
    "app.invoice.createInvoiceFormat": "Create Invoice number format",
    "app.invoice.createInvoiceLineItemTemplate": "Create Product-/Services template",
    "app.invoice.createInvoicePaymentTemplate": "Create Payment Terms template",
    "app.invoice.invoicesList": "Invoices list",
    "app.invoice.invoicePaymentTemplates": "Payment Terms templates",
    "app.invoice.invoiceLineItemTemplates": "Product-/Services templates",

    "app.invoice.brutto": "with VAT",
    "app.invoice.netto": "without VAT",
    "app.invoice.contactDetails": "Contact Details",
    "app.invoice.contact": "Search or enter contact",
    "app.invoice.address": "Address",
    "app.invoice.street": "Street",
    "app.invoice.houseNumber": "House number",
    "app.invoice.zeep": "Post code",
    "app.invoice.city": "City",
    "app.invoice.countryCode": "Country",

    "app.invoice.invoiceDetails": "Invoice details",
    "app.invoice.isConfirmed": "Status",
    "app.invoice.invoiceNumberFormatId": "Invoice number format",
    "app.invoice.customerNum": "Customer number",
    "app.invoice.customerReceiverName": "Receiver",
    "app.invoice.date": "Date",
    "app.invoice.deliveryDate": "Delivery date",
    "app.invoice.serviceDate": "Service date",
    "app.invoice.servicePeriodDays": "Service period (Days)",
    "app.invoice.servicePeriodMonths": "Service period (Months)",
    "app.invoice.noShowDate": "Not show delivery date",

    "app.invoice.head": "Header section",
    "app.invoice.invoiceTitle": "Invoice title",
    "app.invoice.introductionText": "Introduction text",

    "app.invoice.lineItems": "Line items",
    "app.invoice.viewer": "Invoice viewer",
    "app.invoice.position": "Position",
    "app.invoice.productService": "Product / Service",
    "app.invoice.quantity": "Quantity",
    "app.invoice.price": "Price",
    "app.invoice.unit": "Unit",
    "app.invoice.tax": "VAT",
    "app.invoice.discount": "Discount",
    "app.invoice.total": "Total",
    "app.invoice.newLine": "New line",
    "app.invoice.subTotalNet": "Subtotal Net",
    "app.invoice.piece": "Piece",
    "app.invoice.hour": "Hour",
    "app.invoice.vat": "USt",
    "app.invoice.totalAmount": "Total amount",

    "app.invoice.foot": "Footer section",
    "app.invoice.paymentTerm": "Payment term",
    "app.invoice.description": "Description",

    "app.invoice.caching": "Caching",
    "app.invoice.goToPreview": "Go to preview",
    "app.invoice.save_tooltip":
        "You should have at least one position and all positions should have description and price",
    "app.invoice.save_contact_tooltip":
        "Do you want to save your customer data as contact? Using Contact you can fill your customer data in one click",
    "app.invoice.contact_tooltip": "Select your Contact to fill your customer data automatically",

    "app.invoiceList.invoiceDate": "Invoice date",
    "app.invoiceList.serviceDescription": "Service description",
    "app.invoiceList.project": "Project",
    "app.invoiceList.amountNetto": "Amount Netto (EUR)",
    "app.invoiceList.amountBrutto": "Amount Brutto (EUR)",

    "app.cash_register.allCashRegisters": "All Cash Registers",
    "app.cash_register.exportToFibu": "Export to FIBU",
    "app.cash_register.countCashRegisters": "Number of cash registers",
    "app.cash_register.fibuKasse": "FIBU cash register",
    "app.cash_register.newCashRegister": "New cash register",
    "app.cash_register.dmsDocuments": "DMS documents",
    "app.cash_register.deleteConfirm":
        "Please confirm that you want to delete the cash register. All data will be permanently deleted.",
    "app.cash_register.fibuExportTitle": "FIBU export",
    "app.cash_register.fibuExportTooltip": "Create records in the FIBU cash register <b>{name} ({accountNum})</b>",
    "app.cash_register.exportAll": "Export all",
    "app.cash_register.exportAllInfo": "All transactions will be exported.",
    "app.cash_register.exportNewInfo": "Only new transactions will be exported.",
    "app.cash_register.upload.aiRunning": "Binale AI is running...",
    "app.cash_register.upload.ocrRunning": "AI OCR is running...",
    "app.cash_register.upload.aiDescription": "{count} documents are currently being processed by AI, please wait",
    "app.cash_register.upload.processing": "Processing...",
    "app.cash_register.upload.processed": "Processed",
    "app.cash_register.upload.errorCount": "({count} errors)",
    "app.cash_register.upload.createTransactions": "Create transactions",
    "app.cash_register.fromDocuments.result": "{created} created, {linked} already linked, {refused} refused",

    "app.transactionExport.created": "{count} exported",
    "app.transactionExport.alreadyExported": "{count} already exported, skipped",
    "app.transactionExport.invalid": "{count} not exported: {message}",
    "app.transactionExport.notFound": "{count} not found",
    "app.transactionExport.reason.invalidDate": "Invalid booking date",
    "app.transactionExport.reason.noAccountForYear": "No accounting account for the transaction year",
    "app.transactionExport.reason.noAccountingYear": "No accounting year for the transaction year",
    "app.transactionExport.reason.noAmount": "Transaction without amount",
    "app.transactionExport.reason.nothing": "Nothing to export",
    "app.transactionExport.reason.noChart": "No BU timeframe of the chart for the booking date",
    "app.transactionExport.suggestionsFailed": "Contra account suggestions unavailable: {error}",
    "app.banks.allBanks": "All Banks",
    "app.banks.myBank": "My Bank",
    "app.banks.transactionInfo": "Transaction Info",
    "app.banks.counterparty": "Counterparty",
    "app.banks.newBank": "New Bank",
    "app.banks.editBank": "Edit Bank",
    "app.banks.editTransaction": "Edit transaction",
    "app.banks.editTransactions": "Edit transactions",
    "app.banks.uploadTransactions": "Upload your bank transaction file",
    "app.banks.unmapped": "Unmapped",
    "app.banks.selectDataField": "Select data field",
    // --- intl migration batch (phases 1-9) ---
    "app.fields.bicSwift": "BIC (SWIFT code)",
    "app.fields.federalState": "Federal state",
    "app.datev.disconnect": "Remove DATEV connection",
    "app.datev.connected": "DATEV is connected",
    "app.datev.connect": "Connect DATEV",
    "app.datev.refreshToken": "Refresh token",
    "app.titles.documents": "Documents",
    "app.titles.eInvoice": "E-Invoice",
    "app.titles.diverse": "various",
    "app.companies.deleteData": "Delete data",
    "app.companies.type.gastronomy": "Gastronomy",
    "app.companies.type.restaurant": "Restaurant",
    "app.companies.type.cafe": "Café",
    "app.companies.type.bakery": "Bakery",
    "app.companies.type.trade": "Trade",
    "app.companies.type.retail": "Retail",
    "app.companies.type.wholesale": "Wholesale",
    "app.companies.type.services": "Services",
    "app.companies.type.consulting": "Consulting services",
    "app.companies.type.servicesOther": "Other services",
    "app.companies.imageMaxSize": "Image (max. {size})",
    "app.companies.accountingYears": "Accounting years",
    "app.companies.companyLogo": "Company logo",
    "app.companies.validation.range": "must be in range [{min}, {max}]",
    "app.companies.invoice.email": "Invoice email",
    "app.companies.invoice.attention": "Attention (optional)",
    "app.companies.invoice.receiver": "Invoice recipient",
    "app.companies.invoice.director": "Managing director",
    "app.companies.invoice.headquarter": "Registered office",
    "app.companies.invoice.generalPartner": "General partner",
    "app.companies.invoice.taxInfo": "{country}: Tax information",
    "app.companies.invoice.commercialRegister": "{country} commercial register",
    "app.companies.invoice.invalidTaxNumber": "Invalid tax number",
    "app.companies.onlyAdminCanSetProducts": "Only an administrator can set products",
    "app.companies.saldoTransfer": "Balance carryforward",
    "app.companies.saldoTransfer.feWriteRequired":
        "This selection writes payment drafts to Free input. Write access to Free input is required.",
    "app.companies.logs": "Logs",
    "app.companies.go": "Go",
    "app.companies.updateAdmin": "Update admin",
    "app.companies.selectAtLeastOneProduct": "Select at least one product",
    "app.companies.createOrUpdateMember": "Create/update company member",
    "app.companies.modules": "Modules",
    "app.companies.roles": "Roles",
    "app.companies.noRolesAssigned": "No roles assigned",
    "app.companies.editRoles": "Edit roles",
    "app.companies.role.reviewer": "Reviewer",
    "app.companies.role.datevExporter": "DATEV exporter",
    "app.companies.role.elsterExporter": "ELSTER submitter",
    "app.companies.role.avmApprover": "Approve fixed asset register",
    "app.companies.role.dmsAdmin": "DMS admin",
    "app.companies.role.dmsNewDocuments": "DMS new documents",
    "app.companies.role.dmsReviewer": "DMS reviewer",
    "app.companies.fiscalYearConfig": "Fiscal year configuration",
    "app.companies.noAccountingProductsWarning":
        "This company doesn't have accounting products enabled, so the year could not be set. Please ask the administrator to enable accounting products.",
    "app.companies.yearsConfigTable": "Accounting years configuration",
    "app.companies.col.fiscalYearStart": "FY start",
    "app.companies.col.ustvaFrequency": "VAT return frequency",
    "app.companies.col.dauerfristverlaengerung": "Permanent extension",
    "app.companies.col.steuerlichBeraten": "Tax advisor",
    "app.companies.filingFrequency.monthly": "Monthly",
    "app.companies.filingFrequency.quarterly": "Quarterly",
    "app.companies.filingFrequency.none": "No VAT return",
    "app.companies.triState.notSet": "Not set",
    "app.companies.triState.yes": "Yes",
    "app.companies.triState.no": "No",
    "app.companies.taxPrepayments.title": "Tax prepayments",
    "app.companies.taxPrepayments.button": "Tax prepayments",
    "app.companies.taxPrepayments.empty": "No tax prepayments recorded",
    "app.companies.taxPrepayments.col.taxType": "Tax type",
    "app.companies.taxPrepayments.col.validFromYear": "Valid from year",
    "app.companies.taxPrepayments.col.amount": "Amount (quarterly)",
    "app.companies.taxPrepayments.col.note": "Note",
    "app.companies.taxPrepayments.col.docRef": "Document reference",
    "app.companies.taxPrepayments.taxType.est": "Income tax (EST)",
    "app.companies.taxPrepayments.taxType.kst": "Corporate tax (KST)",
    "app.companies.taxPrepayments.taxType.gewst": "Trade tax (GEWST)",
    "app.companies.taxPrepayments.addNew": "Add a new prepayment",
    "app.companies.noSkrForYearWarning":
        "Unable to create this year. There is no SKR configured for the year {year}, please contact the administrator.",
    "app.companies.dataCopyFromYearInfo": "Data will be copied from year {year}",
    "app.companies.taxation.none": "No VAT",
    "app.companies.kontoLen.standard": "Standard",
    "app.companies.kontoLen.extended": "Extended",
    "app.companies.vatNumbers": "VAT numbers",
    "app.admin.metroAccountingCategories.title": "Metro accounting categories",
    "app.admin.metroAccountingCategories.key": "Key",
    "app.admin.metroAccountingCategories.label": "Label",
    "app.admin.metroAccountingCategories.skr03": "SKR03",
    "app.admin.metroAccountingCategories.skr04": "SKR04",
    "app.admin.metroAccountingCategories.articles": "Articles",
    "app.admin.metroAccountingCategories.new": "New category",
    "app.admin.metroAccountingCategories.save": "Save",
    "app.admin.metroAccountingCategories.saved": "Category saved",
    "app.companies.metroAccountingCategories": "Metro accounting categories",
    "app.companies.metroAdministration": "Metro administration",
    "app.companies.metroAccountingCategories.default": "Default: {konto}",
    "app.companies.metroAccountingCategories.own": "Own account ({digits} digits)",
    "app.companies.metroAccountingCategories.noExt": "Own accounts need extended account numbers (kontoExt > 0).",
    "app.companies.metroAccountingCategories.saved": "Mapping saved",
    "app.admin.metroArticles.title": "Metro articles",
    "app.admin.metroArticles.tab.candidates": "Candidates",
    "app.admin.metroArticles.tab.articles": "Articles",
    "app.admin.metroArticles.search": "Article no., EAN or description",
    "app.admin.metroArticles.articleNo": "Article no.",
    "app.admin.metroArticles.ean": "EAN",
    "app.admin.metroArticles.eanSearch": "Search EAN on metro.de",
    "app.admin.metroArticles.group": "Product group",
    "app.admin.metroArticles.description": "Description",
    "app.admin.metroArticles.taxChar": "VAT code",
    "app.admin.metroArticles.aiCategories": "AI classification",
    "app.admin.metroArticles.category": "Category",
    "app.admin.metroArticles.source": "Source",
    "app.admin.metroArticles.note": "Note",
    "app.admin.metroArticles.documents": "Documents",
    "app.admin.metroArticles.companies": "Companies",
    "app.admin.metroArticles.netVolume": "Net",
    "app.admin.metroArticles.lastSeen": "Last",
    "app.admin.metroArticles.confirm": "Confirm",
    "app.admin.metroArticles.save": "Save",
    "app.admin.metroArticles.import": "Import (xlsx/csv)",
    "app.admin.metroArticles.importResult": "{inserted} new, {updated} updated, {rejected} rejected",
    "app.admin.metroArticles.importRejected": "Row {row}: {reason}",
    "app.admin.metroArticles.total": "{total} articles",
    "app.admin.metroArticles.truncated":
        "Showing only the first {shown} of {total} results. Please refine your search.",
    // AG Grid chrome (pagination controls); keys map to AG Grid's own localeText names.
    "app.grid.page": "Page",
    "app.grid.to": "to",
    "app.grid.of": "of",
    "app.grid.firstPage": "First page",
    "app.grid.previousPage": "Previous page",
    "app.grid.nextPage": "Next page",
    "app.grid.lastPage": "Last page",
    "app.grid.pageSize": "Rows per page:",
    "app.grid.pageSizeAria": "Rows per page",

    "app.companies.metroArticles": "Metro articles",
    "app.companies.pending.title": "Your company is being set up",
    "app.companies.pending.description":
        "We are setting up your company. As soon as it is ready it will appear here automatically - please reload the page then.",
    "app.companies.pending.contact": "If you have any questions, contact us at {email}.",
    "app.avv.accept": "Yes, I have read and accept the {avvLink}.",
    "app.avv.modal.description":
        "Almost there: {companyName} just needs to accept the {avvLink} (AVV). Under Art. 28 GDPR it describes how binale.app processes your data for you. Feel free to have a look, then simply confirm below.",
    "app.button.later": "Later",
    "app.page.legal.section": "Legal",
    "app.page.legal.compliance": "Privacy & legal",
    "app.page.legal.impressum": "Imprint",
    "app.page.legal.haftungsausschluss": "Disclaimer",
    "app.page.legal.agb": "Terms and Conditions",
    "app.page.legal.datenschutz": "Privacy Policy",
    "app.page.legal.avv": "Data Processing Agreement",
    "app.page.legal.cookiePolicy": "Cookie Policy",
    "app.page.legal.privacy": "Your Privacy",
    "app.page.home.temporarilyLocked": "Temporarily locked",

    "app.home.metrics.title": "Key figures",
    "app.home.metrics.expand": "Show details",
    "app.home.metrics.collapse": "Collapse",
    "app.home.metrics.guv": "P&L",
    "app.home.metrics.revenue": "Revenue",
    "app.home.metrics.costs": "Costs",
    "app.home.metrics.profit": "Profit",
    "app.home.metrics.op": "Open items",
    "app.home.metrics.op.receivables": "Receivables",
    "app.home.metrics.op.payables": "Payables",
    "app.home.metrics.op.count": "{count} open",
    "app.home.metrics.vat": "VAT {month}",
    "app.home.metrics.vat.zahllast": "Payable",
    "app.home.metrics.vat.erstattung": "Refund",
    "app.home.metrics.cash": "Cash register",
    "app.home.metrics.bank": "Bank",
    "app.home.metrics.bank.trend": "Last 30 days",
    "app.home.metrics.betriebsergebnis": "Operating result",
    "app.home.metrics.overdueReceivables": "Overdue receivables",
    "app.home.metrics.liquidity": "Liquid assets",
    "app.home.aiUsage.title": "AI processing",
    "app.home.aiUsage.scope": "Reporting scope",
    "app.home.aiUsage.scope.all": "All",
    "app.home.aiUsage.scope.company": "Company",
    "app.home.aiUsage.today": "Today",
    "app.home.aiUsage.week": "7 days",
    "app.home.aiUsage.window": "{days} days",
    "app.home.aiUsage.documents": "Documents",
    "app.home.aiUsage.documentsPerDay": "Documents per day",
    "app.home.aiUsage.cost": "Cost",
    "app.home.aiUsage.costPerDay": "Cost per day (USD)",
    "app.home.aiUsage.tokens": "Tokens",
    "app.home.aiUsage.tokens.input": "Input",
    "app.home.aiUsage.tokens.cached": "Cached",
    "app.home.aiUsage.tokens.output": "Output",
    "app.home.aiUsage.tokens.thoughts": "Thinking",
    "app.home.aiUsage.tokens.total": "Total",
    "app.home.aiUsage.models": "Models",
    "app.home.aiUsage.model": "Model",
    "app.home.aiUsage.model.unknown": "not recorded",
    "app.home.aiUsage.costByStage": "Cost by stage (USD)",
    "app.home.aiUsage.stage.EXTRACTION": "Document extraction",
    "app.home.aiUsage.stage.METRO_SPLIT": "METRO split",
    "app.home.aiUsage.stage.OCR": "OCR",
    "app.home.aiUsage.stage.DOCUMENTS_TO_RECORDS": "Documents to records",
    "app.home.aiUsage.stage.TRANSACTION_EXPORT": "Bank/cash export",
    "app.home.aiUsage.stage.TRANSACTION_SUGGESTIONS": "Contra account suggestions",
    "app.home.aiUsage.hint":
        "{from} – {to}. Only the latest AI run per document is counted; reprocessed documents move to the later day and earlier runs' cost is not included.",
    "app.home.calendar.title": "Deadlines",
    "app.home.calendar.summary.today": "Today: {count, plural, one {# deadline} other {# deadlines}}",
    "app.home.calendar.summary.next": "Next deadline: {title} in {days, plural, one {# day} other {# days}}",
    "app.home.calendar.summary.empty": "No upcoming deadlines",
    "app.home.calendar.group.heute": "Today",
    "app.home.calendar.group.woche": "This week",
    "app.home.calendar.group.monat": "This month",
    "app.home.calendar.group.spaeter": "Later",
    "app.home.calendar.ustva": "VAT return {month}",
    "app.home.calendar.ustvaQuarter": "VAT return Q{quarter}",
    "app.home.calendar.sondervz": "VAT special advance payment",
    "app.home.calendar.vzEst": "Income tax advance payment",
    "app.home.calendar.vzKst": "Corporate tax advance payment",
    "app.home.calendar.vzGewst": "Business tax advance payment",
    "app.home.calendar.annualUst": "VAT return",
    "app.home.calendar.annualEst": "Income tax return",
    "app.home.calendar.annualKst": "Corporate tax return",
    "app.home.calendar.annualGewst": "Business tax return",
    "app.home.calendar.offenlegung": "Disclosure of annual accounts",
    "app.home.calendar.softDeadline": "recommended by {date}",
    "app.home.calendar.hardDeadline": "latest {date}",
    "app.home.calendar.userEventTitle": "{title}",
    "app.home.calendar.addEvent": "Add event",
    "app.home.calendar.type.meeting": "Meeting",
    "app.home.calendar.type.appointment": "Appointment",
    "app.home.calendar.type.reminder": "Reminder",
    "app.home.calendar.type.delivery": "Delivery",
    "app.home.calendar.type.other": "Other",
    "app.home.calendar.modal.createTitle": "Create event",
    "app.home.calendar.modal.editTitle": "Edit event",
    "app.home.calendar.modal.fields.title": "Title",
    "app.home.calendar.modal.fields.eventDate": "Date",
    "app.home.calendar.modal.fields.type": "Type",
    "app.home.calendar.modal.fields.assignee": "Assignee",
    "app.home.calendar.modal.fields.assigneeNone": "No one assigned",
    "app.home.calendar.modal.fields.contact": "Contact",
    "app.home.calendar.modal.fields.notes": "Notes",
    "app.home.calendar.modal.fields.color": "Color",
    "app.home.calendar.modal.delete": "Delete event",
    "app.home.calendar.state.doneLabel": "Mark as done",
    "app.home.calendar.state.noteTrigger": "Edit note & color",
    "app.home.calendar.state.noteLabel": "Note",

    "app.calendar.tabs.month": "Month",
    "app.calendar.tabs.agenda": "Agenda",
    "app.calendar.month.badgeLabel": "{count, plural, one {# event} other {# events}}",
    "app.calendar.dayPanel.empty": "No events on this day",
    "app.calendar.dayPanel.selectPrompt": "Select a day to see its events",
    "app.calendar.agenda.empty": "No events this year",
    "app.home.openPositions.title": "Open items",
    "app.home.openPositions.empty": "No open items",
    "app.home.openPositions.bucket.ueberfaellig": "Overdue",
    "app.home.openPositions.bucket.heute": "Due today",
    "app.home.openPositions.bucket.woche": "This week",
    "app.home.openPositions.bucket.spaeter": "Later",
    "app.home.openPositions.debitor": "Receivables",
    "app.home.openPositions.kreditor": "Payables",
    "app.page.test.skrTestPage": "SKR test page",
    "app.page.auth.confirmingEmail": "Confirming email, please wait...",
    "app.page.profile.theme": "Theme",
    "app.page.profile.theme.system": "System",
    "app.page.profile.theme.light": "Light",
    "app.page.profile.theme.dark": "Dark",
    "app.page.profile.deleteAccount":
        "Would you like your account deleted? Write to us at {contact}. We will delete your account and the associated data, except where statutory retention obligations prevent it.",
    "app.page.profile.classicBackground": "Classic background",
    "app.page.profile.validating": "Validating...",
    "app.page.datev.deleteCache": "Delete DATEV cache",
    "app.page.datev.cache": "DATEV (cache)",
    "app.page.datev.notConnected": "DATEV is not connected",
    "app.admin.vat.drafts": "Drafts",
    "app.admin.vat.showHidden": "Show hidden",
    "app.admin.vat.ibanActive": "IBAN active",
    "app.admin.vat.searchPlaceholder": "Search by VAT, address or anything",
    "app.admin.vat.legalName": "Legal name",
    "app.admin.vat.confirmed": "Confirmed",
    "app.admin.vat.vatNumber": "VAT number",
    "app.admin.vat.show": "Show",
    "app.admin.vat.hide": "Hide",
    "app.banks.creditCardNumber": "Card number",
    "app.banks.deleteBankConfirm":
        "Please confirm that you want to delete the bank. All data will be permanently deleted.",
    "app.banks.onlyCsv": "CSV only",
    "app.banks.isConnected": "is connected",
    "app.banks.isNotConnected": "is not connected",
    "app.banks.dmsBankLabel": "Bank: {name}",
    "app.banks.dmsBankBindHeading": "Link DMS Bank",
    "app.banks.bankType": "Bank type",
    "app.banks.bankTypeCreditCard": "Credit Card",
    "app.banks.bankTypePlatform": "Platform",
    "app.banks.import": "Import",
    "app.banks.stepInitConnection": "Initialize connection",
    "app.banks.stepEstablishConnection": "Establish connection",
    "app.banks.finapiConnection": "FinAPI connection",
    "app.banks.linkImportedAccount": "Link imported account",
    "app.banks.connectNewBank": "Connect new bank",
    "app.banks.startConnection": "Start connection",
    "app.banks.stepUpdateConnection": "Update connection",
    "app.banks.stepImportTransactions": "Import transactions",
    "app.banks.finapiImport": "FinAPI import",
    "app.banks.lastImportDate": "Last import date",
    "app.banks.importFrom": "Import from",
    "app.banks.importFromInvalid": "Choose a date between {min} and {max}",
    "app.banks.onlyNewTransactions": "Only new transactions",
    "app.banks.finapiIsConnected": "<b>FinAPI</b> is connected.",
    "app.banks.disconnect": "Disconnect",
    "app.banks.accountLinkedSuccess": "Account linked successfully",
    "app.banks.noAvailableAccounts": "No available accounts found.",
    "app.banks.stepInitiatePayment": "Initiate payment",
    "app.banks.stepFinalizePayment": "Finalize payment",
    "app.banks.paymentTransfer": "Payment transfer",
    "app.banks.recipientName": "Recipient name",
    "app.banks.amountEur": "Amount (EUR)",
    "app.banks.startPayment": "Start payment",
    "app.banks.paymentInitiatedSuccess": "Payment initiated successfully.",
    "app.banks.paymentIdLabel": "Payment ID: {id}",
    "app.banks.transactionsProcessed":
        "{count, plural, one {# transaction processed} other {# transactions processed}}",
    "app.banks.transactionsTimeframe": "Timeframe from {from} to {to}",
    "app.banks.findDmsDocument": "Find DMS document",
    "app.banks.findAndFillContact": "Find and fill contact (name, IBAN)",
    "app.banks.contactSuggestions.button": "Suggest contacts from base data",
    "app.banks.contactSuggestions.scopeHint":
        "For the selected transactions, without a selection for all transactions without a contact.",
    "app.banks.contactSuggestions.title":
        "{count, plural, one {Suggest # contact from base data} other {Suggest # contacts from base data}}",
    "app.banks.contactSuggestions.description":
        "The IBAN of these transactions belongs to exactly one company in the VAT ID base data. The checked contacts are created and linked to their transactions.",
    "app.banks.contactSuggestions.transactions": "{count, plural, one {# transaction} other {# transactions}}",
    "app.banks.contactSuggestions.confirm": "Create contacts",
    "app.banks.contactSuggestions.empty": "No contact suggestions found in the base data.",
    "app.banks.contactSuggestions.loadError": "Error loading the contact suggestions: {error}",
    "app.banks.contactSuggestions.saving": "Creating contacts",
    "app.banks.contactSuggestions.saveError": "Error creating the contacts: {error}",
    "app.banks.contactSuggestions.created": "{count, plural, one {# contact created} other {# contacts created}}",
    "app.banks.contactSuggestions.existing": "{count} already present",
    "app.banks.contactSuggestions.unavailable": "{count} no longer available",
    "app.banks.contactSuggestions.nothing": "No contacts created",
    "app.banks.fibuExportTooltip": "Create records in the FIBU bank <b>{name} ({accountNum})</b>",
    "app.banks.bookings": "Bookings",
    "app.banks.contactMatched": "matched with contact",
    "app.banks.contactMismatch": "does not match contact",
    "app.banks.finapiTransactionNotDeletable": "Transactions imported from the bank via FinAPI cannot be removed",
    "app.banks.bankUndefined": "Bank undefined",
    "app.banks.closeTransactionImport": "Close transaction import",
    "app.banks.hideExistingTransactions": "Hide existing transactions",
    "app.banks.transactionsRecognized": "Bank transactions have been successfully recognized",
    "app.banks.goToTransactionData": "Go to transaction data",
    "app.contacts.basicInformation": "Basic information",
    "app.contacts.markAsInvoiceAddress": "Mark as invoice address",
    "app.contacts.commercialRegister": "Commercial register",
    "app.contacts.personalIdNr": "Personal tax ID",
    "app.contacts.idNrIndividualOnly": 'available only for the "individual" type',
    "app.contacts.clearFilters": "Clear filters",
    "app.contacts.errorSaving": "Error saving contact",
    "app.contacts.noWritePermission": "You don't have permission to save.",
    "app.contacts.errorSearching": "Error searching contacts",
    "app.contacts.searchTerm": "Search term",
    "app.contacts.searchPlaceholder": "Name, IBAN, VAT ID, tax number...",
    "app.dms.aiLog.title": "AI Log · {fileName}",
    "app.dms.aiLog.extractionFailed": "AI extraction failed",
    "app.dms.aiLog.loadError": "Error loading the AI log: {error}",
    "app.dms.aiLog.empty": "No AI log available for this document.",
    "app.dms.aiLog.copyJson": "Copy JSON",
    "app.dms.ibanCheck.unknownIbans": "IBAN not stored on the contact",
    "app.dms.markdown.title": "Text recognition: {fileName}",
    "app.dms.markdown.tab.rendered": "Preview",
    "app.dms.markdown.tab.raw": "Raw",
    "app.dms.markdown.copy": "Copy text",
    "app.dms.markdown.empty": "No text available",
    "app.dms.markdown.loadError": "Loading error: {error}",
    "app.dms.markdown.tabs.label": "Markdown view",
    "app.dms.markdown.search": "Search in text",
    "app.dms.markdown.search.next": "Next match",
    "app.dms.markdown.search.previous": "Previous match",
    "app.dms.markdown.search.matches": "{current} of {total}",
    "app.dms.markdown.search.noMatches": "No matches",
    "app.dms.markdown.search.hint": "Enter at least 2 characters",
    "app.dms.markdown.breakdown.title": "AI breakdown",
    "app.dms.markdown.breakdown.loading": "Loading breakdown",
    "app.dms.markdown.breakdown.error": "Could not load AI breakdown",
    "app.dms.markdown.breakdown.empty": "No AI data available",
    "app.dms.markdown.breakdown.metroApplied": "Metro split by category",
    "app.dms.markdown.breakdown.checks": "Checks {passed}/{total}",
    "app.dms.markdown.breakdown.passed": "Passed",
    "app.dms.markdown.breakdown.failed": "Failed",
    "app.dms.markdown.breakdown.lines": "{count, plural, one {# line} other {# lines}}",
    "app.dms.markdown.breakdown.net": "Net",
    "app.dms.markdown.breakdown.tax": "VAT",
    "app.dms.markdown.breakdown.gross": "Gross",
    "app.dms.markdown.breakdown.expected": "Expected",
    "app.dms.markdown.breakdown.actual": "Actual",
    "app.dms.aiStatus": "AI status",
    "app.dms.aiStatus.progress": "In progress",
    "app.dms.aiStatus.success": "Success",
    "app.dms.aiStatus.error": "Error",
    "app.dms.documentsEdited": "{count, plural, one {# document edited} other {# documents edited}}",
    "app.dms.amountMode.input": "Input",
    "app.dms.amountMode.tooltipInput": "Input: the amount you enter is written to all selected documents.",
    "app.dms.amountMode.tooltipSign":
        "Income / Expense: each document keeps its amount, only the sign is set to match its folder.",
    "app.dms.amountMode.tooltipSkipped":
        "Documents without an amount, attachments and documents outside the accounting folders stay unchanged.",
    "app.dms.tagFieldHint.title": "This field can only be edited when:",
    "app.dms.tagFieldHint.condition1": "1. the date is not empty and",
    "app.dms.tagFieldHint.condition2": "2. an accounting configuration exists for the year of that date.",
    "app.dms.upload.alreadyExisting": "Already exist, not uploaded again: {count}",
    "app.dms.fileInfo.documentType": "Document type",
    "app.dms.fileInfo.uploadedAt": "Uploaded at",
    "app.dms.upload.unsupportedFiles": "Unsupported files: {count}",
    "app.dms.upload.errorLabel": "Error: {error}",
    "app.dms.upload.success": "{count, plural, one {# document uploaded} other {# documents uploaded}}",
    "app.dms.upload.failed":
        "{count, plural, one {# file could not be uploaded} other {# files could not be uploaded}}",
    "app.dms.payments.createGroup": "Create collective payment",
    "app.dms.fileNameOriginal": "Original",
    "app.dms.fileNameExport": "Export",
    "app.dms.avis.createdAt": "Created",
    "app.dms.avis.documentsCount": "Documents ({count})",
    "app.dms.contactsCreated": "Contacts have been created.",
    "app.dms.noContactsCreated": "No contacts created.",
    "app.dms.contacts.create": "Create contacts",
    "app.dms.contacts.create.desc": "Contacts will be created using data from the selected documents.",
    "app.dms.contacts.fill": "Fill contact",
    "app.dms.contacts.fill.desc": "Using AI data we will fill the suitable contact in the selected documents.",
    "app.dms.contacts.filled": "{count} document(s) linked to a contact.",
    "app.dms.contacts.noneFilled": "No matching contacts found for the selected documents.",
    "app.dms.duplicates.title": "Duplicate check",
    "app.dms.duplicates.tooltip":
        "Duplicate check within the selection: filters the list down to selected documents sharing the same invoice number and amount. Click again to clear the filter.",
    "app.dms.duplicates.none": "No duplicates found in the selection.",
    "app.dms.duplicates.found":
        "{groups, plural, one {# group} other {# groups}} ({documents, plural, one {# document} other {# documents}})",
    "app.dms.pdfAi.maxDocs":
        "Binale PDF AI processes at most {max} documents per run. {selected} are currently selected.",
    "app.dms.pdfAi.normal": "Normal processing",
    "app.dms.pdfAi.normal.desc": "Processes only documents the AI has not processed yet.",
    "app.dms.pdfAi.force": "Force processing",
    "app.dms.pdfAi.force.desc": "Re-runs the AI on already processed documents, costs AI usage, max {max} documents.",
    "app.domain.skrLoading": "Loading data... {progress}%",
    "app.domain.skrLoaded": "SKR data loaded",
    "app.domain.userDataLoading": "Loading user data…",
    "app.domain.simultaneousLogin.title": "Simultaneous login detected",
    "app.domain.simultaneousLogin.heading": "Somebody logged in with your credentials",
    "app.domain.simultaneousLogin.body":
        "Unfortunately, this is impossible to work on multiple computers using the same credentials. You will be logged out in 5 seconds.",
    "app.domain.simultaneousLogin.device": "New sign-in from: {device}",
    "app.domain.sessionRevoked.title": "Session ended",
    "app.domain.sessionRevoked.heading": "You ended this session",
    "app.domain.sessionRevoked.body": "You will be signed out in 5 seconds.",
    "app.profile.sessions.title": "Active sessions",
    "app.profile.sessions.current": "Current session",
    "app.profile.sessions.description":
        "Browser sessions for this account. Closed browsers drop off the list by themselves.",
    "app.profile.sessions.singleSession": "Automatically sign out other sessions",
    "app.profile.sessions.singleSession.hint":
        "While this is on, every sign-in ends every other session of this account.",
    "app.profile.sessions.since": "Signed in since",
    "app.profile.sessions.end": "End",
    "app.profile.sessions.end.confirm": "End this session?",
    "app.profile.sessions.empty": "No other sessions.",
    "app.domain.recordUpdated.title": "Record was updated",
    "app.domain.recordUpdated.body": "There were {count} updates made to the record from the attached document",
    "app.domain.debitorName": "Debitor name",
    "app.invoice.saveCustomerAsContact": "Save customer details as contact",
    "app.invoice.completed": "Completed",
    "app.invoice.example": "Example",
    "app.invoice.finalize": "Finalize",
    "app.invoice.template": "Template",
    "app.invoice.plusVat": "plus VAT {rate}% ( {amount} )",
    "app.invoice.numberFormat": "Number format",
    "app.invoice.invalidCharacters": "Invalid characters",
    "app.invoice.prefix": "Prefix",
    "app.invoice.numberLength": "Number length",
    "app.invoice.startNumber": "Start number",
    "app.invoice.maxValue": "Max {max}",
    "app.invoice.suffix": "Suffix",
    "app.invoice.formats": "Formats",
    "app.invoice.lineItemTemplates": "Product/Service templates",
    "app.invoice.paymentTemplates": "Payment terms templates",
    "app.invoice.pdfWithERechnung": "PDF with E-Rechnung (ZUGFeRD 2.4 / Factur-X)",
    "app.pdf.stats.pages": "Number of pages",
    "app.pdf.stats.title": "OCR statistics",
    "app.pdf.ocrOptions": "OCR options",
    "app.pdf.skipQrCheck": "Treat unreadable QR codes as separators",
    "app.pdf.selectDocument": "Select document {name}",
    "app.pdf.toast.decodedEncrypted": "Decoded encrypted file",
    "app.pdf.viewer.search": "Search in document",
    "app.pdf.viewer.search.next": "Next match",
    "app.pdf.viewer.search.previous": "Previous match",
    "app.pdf.viewer.search.matches": "{current} of {total}",
    "app.pdf.viewer.search.noMatches": "No matches",
    "app.pdf.viewer.search.close": "Close search",
    "app.pdf.viewer.search.unavailable": "This document contains no searchable text",
    "app.components.selectAccount": "Select account",
    "app.components.xrechnung.formatDeprecated": "Warning, this format is deprecated!",
    "app.components.xrechnung.email": "Email",
    "app.components.xrechnung.paymentDetails": "Payment details",
    "app.components.xrechnung.paymentMethodNotSpecified": "Payment method was not specified",
    "app.components.xrechnung.dueDate": "Due date",
    "app.components.xrechnung.invoiceLines": "Invoice lines",
    "app.components.xrechnung.bookingLines": "Booking lines",
    "app.components.xrechnung.rule": "Rule",
    "app.components.xrechnung.location": "Location",
    "app.components.xrechnung.hasErrors": "E-invoice contains errors or warnings",
    "app.components.xrechnung.validating": "Validating...",
    "app.components.xrechnung.isValid": "E-invoice is valid",
    "app.components.xrechnung.showXml": "Show e-invoice XML",
    "app.components.xrechnung.xmlTitle": "E-invoice XML",
    // --- intl accounting migration ---
    "app.fields.period": "Period",
    "app.button.ok": "OK",
    "app.columns.column2": "Column 2",
    "app.columns.column3": "Column 3",
    "app.dms.copyFailed": "Copy failed",
    "app.dms.cashRegister.export": "Transfer to the cash register",
    "app.dms.cashRegister.exportConfirm":
        "{count, plural, one {# document} other {# documents}} will be created as cash movements in <b>{name}</b>.",
    "app.dms.datev.export": "Send selected documents to DATEV",
    "app.dms.datev.exportTooltip": "Selected documents are grouped by document month and sent to DATEV.",
    "app.dms.datev.notConnected": "An active DATEV connection is required for export.",
    "app.dms.datev.missingDocumentDate": "All selected documents require a document date.",
    "app.dms.datev.result": "DATEV export: {uploaded} uploaded, {exists} already existed, {failed} failed.",
    "app.dms.datev.error": "The documents could not be sent to DATEV.",
    "app.dms.autoSort.button": "Sort automatically",
    "app.dms.autoSort.failed": "Automatic sorting failed",
    "app.dms.autoSort.summaryTitle":
        "{moved, plural, =0 {No documents sorted} one {# document sorted} other {# documents sorted}}",
    "app.dms.autoSort.summaryDescription":
        "Incoming invoices: {er} · Outgoing invoices: {deb} · Bank: {bank} (linked to a transaction: {linked}) · Cash: {kb} · left in New documents: {remaining}{skipped, plural, =0 {} other { · # skipped}}{failed, plural, =0 {} other { · # failed}}",
    "app.dms.bewirtung.title": "Entertainment receipt",
    "app.dms.bewirtung.kind.business": "Business entertainment",
    "app.dms.bewirtung.kind.employees": "Employees only",
    "app.dms.bewirtung.date": "Date of the entertainment",
    "app.dms.bewirtung.place": "Place (restaurant, address)",
    "app.dms.bewirtung.participants": "Persons entertained (one per line, including the host)",
    "app.dms.bewirtung.occasion": "Occasion of the entertainment",
    "app.dms.bewirtung.amount": "Amount of the expenses",
    "app.dms.bewirtung.tip": "Tip",
    "app.dms.bewirtung.host": "Host",
    "app.dms.bewirtung.confirmed": "Confirmed by {user} on {date}",
    "app.dms.bewirtung.confirmedOn": "Confirmed on {date}",
    "app.dms.bewirtung.save": "Save",
    "app.dms.bewirtung.occasionRequired": "Please state the specific occasion",
    "app.dms.bewirtung.create": "Create entertainment receipt",
    "app.dms.bewirtung.edit": "Edit",
    "app.dms.bewirtung.staleNotice": "The entertainment receipt was changed in the meantime. Reload it to save.",
    "app.dms.bewirtung.reload": "Reload",
    "app.dms.bewirtung.saveFailed": "The entertainment receipt could not be saved",
    "app.dms.bewirtung.removeFailed": "The entertainment receipt could not be removed",
    "app.dms.bewirtung.removeConfirm": "Really remove the entertainment receipt?",
    "app.dms.bewirtung.discardUnsaved": "Discard unsaved changes to the entertainment receipt?",
    "app.dms.bewirtung.discardConfirm": "Discard",
    "app.dms.bewirtung.remove": "Remove entertainment receipt",
    "app.dms.bewirtung.aiLoadFailed": "AI data could not be loaded",
    "app.dms.bewirtung.virtualSigned": "Signed",
    "app.dms.bewirtung.virtualUnsigned": "Not signed",
    "app.dms.bewirtung.virtualAria": "Show Bewirtungsbeleg ({state})",
    "app.dms.bewirtung.virtualHint": "Generated from this document's Bewirtung data",
    "app.dms.bewirtung.viewPdf": "View PDF",
    "app.dms.bewirtung.viewPdfLocked": "View signed Bewirtungsbeleg as PDF",
    "app.dms.bewirtung.pdfFailed": "The PDF could not be created",
    "app.dms.bewirtung.column": "BW",
    "app.dms.bewirtung.amountHint":
        "The receipt must be machine-printed and secured by a certified till (TSE). Above €250 gross, the invoice must also show the host's name as the invoice recipient, the restaurant's tax number or VAT ID and a sequential invoice number (BMF letter of 19.11.2025).",
    "app.dms.bewirtung.hostHint":
        'Name of the hosting person, e.g. "Max Muster (Managing Director Muster GmbH)", not an email address. On invoices above €250 this name must appear on the invoice.',
    "app.dms.bewirtung.missingHint":
        "This receipt looks like a business meal. To deduct it, please record the occasion and participants promptly (§ 4(5) No. 2 EStG).",
    "app.dms.bewirtung.sign": "Sign and lock",
    "app.dms.bewirtung.signConfirm":
        "You are signing as {name}. After signing, the Bewirtungsbeleg is locked and can no longer be changed or removed.",
    "app.dms.bewirtung.signConfirmButton": "Sign",
    "app.dms.bewirtung.signed": "Bewirtungsbeleg signed and locked",
    "app.dms.bewirtung.signFailed": "Signing failed",
    "app.dms.bewirtung.alreadyLocked": "The Bewirtungsbeleg is already signed and locked",
    "app.dms.bewirtung.lockedBy": "Signed and locked by {user} on {date}",
    "app.dms.bewirtung.lockedOn": "Signed and locked on {date}",
    "app.dms.bewirtung.signatureChecking": "Checking signature …",
    "app.dms.bewirtung.signatureValid": "Signature valid: content unchanged since locking",
    "app.dms.bewirtung.signatureInvalid": "Signature invalid: content does not match the signed version",
    "app.dms.bewirtung.signatureUnavailable": "Signature could not be checked",
    "app.dms.sortAssistant.title": "Sorting assistant",
    "app.dms.sortAssistant.progress": "Step {current} of {total}",
    "app.dms.sortAssistant.showDocument": "Show document",
    "app.dms.sortAssistant.hideDocument": "Hide document",
    "app.dms.sortAssistant.skip": "Skip",
    "app.dms.sortAssistant.close": "Close assistant",
    "app.dms.sortAssistant.bewirtungQuestion": "What was this?",
    "app.dms.sortAssistant.noBewirtung": "No business entertainment (travel, private)",
    "app.dms.sortAssistant.bankPickQuestion": "Which transaction does this receipt belong to?",
    "app.dms.sortAssistant.noneOfThem": "None of them",
    "app.dms.sortAssistant.whereQuestion": "Where does this receipt belong?",
    "app.dms.sortAssistant.toKasse": "Cash book",
    "app.dms.sortAssistant.toBank": "Bank without transaction",
    "app.dms.sortAssistant.toEr": "Incoming invoices",
    "app.dms.sortAssistant.stay": "Leave in New documents",
    "app.dms.fibu.export.title": "FIBU export",
    "app.dms.fibu.export.disabled.not_accounting_type": "This is not an accounting document.",
    "app.dms.fibu.export.disabled.too_many_documents": "Too many documents selected (max. 200).",
    "app.dms.fibu.export.disabled.mixed_types": "Please select documents of the same type only.",
    "app.dms.fibu.export.disabled.no_type": "No document type detected.",
    "app.dms.fibu.export.disabled.documents_not_ready": "Not all documents are complete (status: draft).",
    "app.dms.fibu.export.disabled.no_group_id": "Please select an account (bank or cash register) first.",
    "app.dms.fibu.export.disabled.missing_document_date": "At least one document is missing a document date.",
    "app.dms.fibu.export.disabled.not_a_receipt": "Informational document, not a receipt: no booking is created.",
    "app.dms.fibu.export.disabled.year_not_in_accounting_years":
        "The document date is outside the configured accounting years ({years}).",

    "app.mailbox.button": "Email import",
    "app.mailbox.title": "Connect mailbox",
    "app.mailbox.description":
        "Connect an email mailbox to automatically import invoices (PDF attachments) into the document management every hour.",
    "app.mailbox.provider": "Provider",
    "app.mailbox.provider.imap": "IMAP",
    "app.mailbox.provider.microsoft365": "Microsoft 365",
    "app.mailbox.provider.googleWorkspace": "Google Workspace",
    "app.mailbox.provider.comingSoon": "Coming soon",
    "app.mailbox.m365.connect": "Connect with Microsoft 365",
    "app.mailbox.m365.connectedAs": "Connected as",
    "app.mailbox.m365.reconnect": "Reconnect",
    "app.mailbox.m365.connecting": "Connecting Microsoft 365…",
    "app.mailbox.host": "Server (host)",
    "app.mailbox.port": "Port",
    "app.mailbox.secure": "Secure connection (SSL/TLS)",
    "app.mailbox.username": "Username",
    "app.mailbox.folder": "Folder (optional)",
    "app.mailbox.folder.placeholder": "INBOX",
    "app.mailbox.password.configured": "Password stored",
    "app.mailbox.password.change": "Change password",
    "app.mailbox.password.new": "New password",
    "app.mailbox.enabled": "Automatic import enabled",
    "app.mailbox.testConnection": "Test connection",
    "app.mailbox.testSuccess": "Connection established successfully",
    "app.mailbox.testFailed": "Connection failed",
    "app.mailbox.status.title": "Status",
    "app.mailbox.status.connection": "Connection status",
    "app.mailbox.status.lastPolled": "Last polled",
    "app.mailbox.status.neverPolled": "not polled yet",
    "app.mailbox.status.lastError": "Last error",
    "app.mailbox.status.untested": "Not tested",
    "app.mailbox.status.ok": "OK",
    "app.mailbox.status.error": "Error",
    "app.mailbox.delete": "Delete configuration",
    "app.mailbox.delete.confirm": "Do you really want to delete the mailbox configuration?",
    "app.mailbox.template": "Template",
    "app.mailbox.template.custom": "Custom",
    "app.mailbox.template.gmail": "Gmail",
    "app.mailbox.template.yahoo": "Yahoo",
    "app.mailbox.template.gmx": "GMX",
    "app.mailbox.template.webde": "WEB.DE",
    "app.mailbox.template.aol": "AOL",
    "app.mailbox.hint.openGuide": "Open guide",
    "app.mailbox.hint.gmail":
        "Gmail requires two-factor authentication and an app password - your normal password does not work for IMAP. Enable 2FA in your Google account, create an app password and enter it here as the password.",
    "app.mailbox.hint.yahoo":
        "Yahoo requires an app password. Sign in to Yahoo Account Security, choose “Generate app password”, give it a name (e.g. Binale) and enter the generated password here.",
    "app.mailbox.hint.aol":
        "AOL requires an app password. Create an app password in your AOL security settings and enter it here.",
    "app.mailbox.hint.gmx":
        "GMX requires IMAP access to be enabled in the mailbox settings first. Then use your full GMX address and your normal mail password.",
    "app.mailbox.hint.webde":
        "WEB.DE requires IMAP access to be enabled in the mailbox settings first. Then use your full WEB.DE address and your normal mail password.",

    "app.fields.drag": "Drag",
    "app.invoicing.lineItemTemplate": "Template",
    "app.titles.banks": "Banks",
    "app.titles.buchungen": "Postings",
    "app.titles.cashRegisters": "Cash books",
    "app.titles.invoicing": "Invoicing",
    "app.validation.pattern": "Invalid format",
    "app.validation.required": "Required",
    "app.datev.configTitle": "DATEV configuration",
    "app.datev.group": "Group",
    "app.datev.exportOptions": "Export options",
    "app.datev.exportTabs": "Export tabs",
    "app.datev.exportResults": "DATEV export results",
    "app.datev.exportDocuments": "Export documents",
    "app.datev.exportBelegfeld2": "Export Belegfeld2",
    "app.datev.exportKostenstelle": "Export cost center",
    "app.datev.transferDocuments": "Transfer documents",
    "app.datev.exportFormatMonthly": "Export DATEV format (monthly)",
    "app.datev.exportFormatCombined": "Export DATEV format (periods combined)",
    "app.datev.sendToDatev": "Send to DATEV (booking data service)",
    "app.datev.documentsTitle": "DATEV documents",
    "app.datev.step1DocumentTable": "1. DATEV document table CSV",
    "app.datev.step2DocumentsZip": "2. Documents ZIP",
    "app.datev.step3Download": "3. {label}",
    "app.datev.missingFilesInZip": "Missing files in ZIP",
    "app.datev.invalidAmount": "invalid amount",
    "app.datev.errorColumnCount": "All rows must have 40-{max} columns",
    "app.datev.errorPeriodDecode": "Incorrect period decoded: {period}, allowed values are [1, 12]",
    "app.datev.documentsAttached": "Attached {attached} of {total} documents",
    "app.datev.unattachedDocuments": "{count} unattached documents will be imported as well",
    "app.datev.creditorsToCreate": "Creditors to create on save: {list}",
    "app.datev.debitorsToCreate": "Debitors to create on save: {list}",
    "app.datev.categoriesToCreate": "Accounts to create on save: {list}",
    "app.datev.missingKostenstelle": "Missing cost center: {list}",
    "app.datev.missingBanks": "Missing banks: {list}",
    "app.datev.missingKassen": "Missing cash registers: {list}",
    "app.datev.annotation": "Annotation",
    "app.datev.unassignedGroup": "Without bank/cash register",
    "app.datev.importError.missingBank": "Bank {value} does not exist - this row cannot be imported",
    "app.datev.importError.missingKasse": "Cash register {value} does not exist - this row cannot be imported",
    "app.datev.selectedForImport": "Selected {selected} of {total} records for import",
    "app.datev.loadingDocuments": "Loading documents",
    "app.items.sinceYear": "(since {year})",
    "app.overview.bwa.umsatzerloese": "Revenue",
    "app.overview.bwa.bestandsveraenderung": "Change in inventory FE/UE",
    "app.overview.bwa.aktivierteEigenleistungen": "Capitalized own work",
    "app.overview.bwa.gesamtleistung": "Total output",
    "app.overview.bwa.materialWareneinkauf": "Material/goods purchases",
    "app.overview.bwa.rohertrag": "Gross profit",
    "app.overview.bwa.sonstigeBetrieblicheErloese": "Other operating income",
    "app.overview.bwa.betrieblicherRohertrag": "Operating gross profit",
    "app.overview.bwa.personalkosten": "Personnel costs",
    "app.overview.bwa.raumkosten": "Premises costs",
    "app.overview.bwa.betrieblicheSteuern": "Operating taxes",
    "app.overview.bwa.versicherungenBeitraegeAbgaben": "Insurance, contributions and levies",
    "app.overview.bwa.besondereKosten": "Special costs",
    "app.overview.bwa.fahrzeugkosten": "Vehicle costs (excl. tax)",
    "app.overview.bwa.werbeReisekosten": "Advertising and travel costs",
    "app.overview.bwa.kostenWarenabgabe": "Goods delivery costs",
    "app.overview.bwa.abschreibungen": "Depreciation",
    "app.overview.bwa.reparaturenInstandhaltungen": "Repairs and maintenance",
    "app.overview.bwa.sonstigeKosten": "Other costs",
    "app.overview.bwa.gesamtkosten": "Total costs",
    "app.overview.bwa.betriebsergebnis": "Operating result",
    "app.overview.bwa.zinsaufwendungen": "Interest expenses",
    "app.overview.bwa.sonstigerNeutralerAufwand": "Other neutral expenses",
    "app.overview.bwa.neutralerAufwand": "Neutral expenses",
    "app.overview.bwa.zinsertraege": "Interest income",
    "app.overview.bwa.sonstigerNeutralerErtrag": "Other neutral income",
    "app.overview.bwa.verrechneteKalkKosten": "Allocated imputed costs",
    "app.overview.bwa.neutralerErtrag": "Neutral income",
    "app.overview.bwa.kontenklasseUnbesetzt": "Unassigned accounts",
    "app.overview.bwa.ergebnisVorSteuern": "Result before taxes",
    "app.overview.bwa.steuernEinkommenErtrag": "Taxes on income and earnings",
    "app.overview.bwa.vorlaeufigesErgebnis": "Preliminary result",
    "app.overview.bwa.footerSelected": "Selection total",
    "app.overview.bwa.pdf.title": "Business evaluation (BWA)",
    "app.overview.bwa.pdf.berichtsposition": "Report line",
    "app.overview.bwa.pdf.unit": "Values in EUR",
    "app.overview.bwa.pdf.range": "{from} to {to}",
    "app.overview.bwa.pdf.quarter": "Q{n} {year}",
    "app.overview.bwa.pdf.saldenvortrag": "01.01.{year} (opening balance)",
    "app.overview.bwa.pdf.abschluss": "31.12.{year} (closing)",
    "app.overview.bwa.pdf.created": "Created on {date} with Binale-Buchhaltung (version {version})",
    "app.overview.bwa.pdf.createdOn": "Created on {date}",
    "app.overview.bwa.pdf.disclaimer": "The preliminary result reflects the current state of the bookkeeping.",
    "app.overview.bwa.pdf.page": "Page {page} of {total}",
    "app.overview.reconciliation.startPrompt": "Press OK to start (total {count})",
    "app.overview.reconciliation.manual.title": "Manual binding",
    "app.overview.reconciliation.manual.approvalNeeded":
        "A journaled or confirmed booking would change. Please link it in the table and confirm there.",
    "app.overview.reconciliation.manual.body":
        "{count, plural, =1 {The 2 selected records will be connected with a payment.} other {The invoice is connected to # payment representations, with one payment for each.}}",
    "app.overview.reconciliation.tooltip.auto":
        "Automatic (default): the number on the button counts the payment representations (bank, cash register, free entry) that have a suggestion. A suggestion is made when:",
    "app.overview.reconciliation.tooltip.select":
        "The Select button in the Quick tab selects all records belonging to those suggestions in the table.",
    "app.overview.reconciliation.tooltip.manual":
        "Manual: with one payment representation and exactly one invoice selected the button turns red and connects that pair after a confirmation - without an amount check, since the selection is a deliberate override. Preconditions:",
    "app.overview.reconciliation.tooltip.split":
        "Several invoices: when more than one invoice is selected for a payment representation, the representation is split into one line per invoice and the original line is deleted. Additionally required:",
    "app.overview.reconciliation.split.title": "Split payment",
    "app.overview.reconciliation.split.body":
        "The payment representation is split into {count} lines - one per invoice - and the original line is deleted.",
    "app.overview.reconciliation.tooltip.multi":
        "Several payment representations: if several payment representations are selected for one invoice, each is connected to the invoice with its own payment. Additionally required:",
    "app.overview.reconciliation.tooltip.mixed":
        "Several invoices together with several payment representations cannot be connected manually.",
    "app.overview.reconciliation.criteria.selectedSumEqualsOpen":
        "the sum of the selected payment representations equals the open amount of the invoice exactly",
    "app.overview.reconciliation.criteria.sumEqualsBrutto":
        "the sum of the open amounts matches the payment representation's amount exactly",
    "app.overview.reconciliation.criteria.singleItemNotJournaled":
        "the payment representation has a single position and is not journaled",
    "app.overview.reconciliation.criteria.unbound": "the payment representation is not connected yet",
    "app.overview.reconciliation.criteria.matchingSource":
        "a matching invoice (incoming invoice, prepayment, debtor) is found",
    "app.overview.reconciliation.criteria.sumEqualsOpen":
        "the sum of all related payment representations matches the invoice's open amount exactly",
    "app.overview.reconciliation.criteria.pairSelection":
        "exactly one invoice (incoming invoice, prepayment, debtor) and one payment representation",
    "app.overview.reconciliation.criteria.sourceOpen": "the invoice still has an open amount",
    "app.overview.reconciliation.tab.quick": "Quick",
    "app.overview.reconciliation.tab.advanced": "Advanced",
    "app.overview.reconciliation.quick.criteria": "Invoice no. ↔ Belegfeld1",
    "app.overview.reconciliation.summary":
        "{payments, plural, one {# payment representation} other {# payment representations}} for {invoices, plural, one {# invoice} other {# invoices}}",
    "app.overview.reconciliation.select": "Select",
    "app.overview.reconciliation.connect": "Connect",
    "app.overview.reconciliation.key.label": "Match by",
    "app.overview.reconciliation.key.belegfeld1": "Belegfeld1",
    "app.overview.reconciliation.key.belegfeld2": "Belegfeld2",
    "app.overview.reconciliation.advanced.hint":
        "Connected when exactly one open invoice and the payment representations with the same value on the same account settle the open amount exactly.",
    "app.overview.reconciliation.skipped.title": "{count, plural, one {# group skipped} other {# groups skipped}}",
    "app.overview.reconciliation.skipped.multipleSources": "{count} open invoices with this value",
    "app.overview.reconciliation.skipped.noSource": "no open invoice with this value",
    "app.overview.reconciliation.skipped.sumMismatch": "Difference {diff} €",
    "app.overview.reconciliation.advanced.prompt":
        "{payments, plural, one {# payment representation} other {# payment representations}} will be connected to {invoices, plural, one {# invoice} other {# invoices}}. Press OK to start.",
    "app.overview.reconciliation.outcome.applied": "{count, plural, one {# invoice} other {# invoices}} linked",
    "app.overview.reconciliation.outcome.changed":
        "{count, plural, one {# invoice has} other {# invoices have}} changed since the preview, not linked",
    "app.overview.reconciliation.outcome.alreadyApplied":
        "{count, plural, one {# invoice was} other {# invoices were}} already linked",
    "app.overview.reconciliation.outcome.failed": "{count} failed: {message}",
    "app.overview.reconciliation.tooltip.advanced":
        "Advanced: invoice and payment representations are grouped by a chosen field (Belegfeld1, Belegfeld2, additional info). Groups that do not settle exactly are listed with the reason.",
    "app.accounting.buInsteadOfUst": "BU instead of USt",
    "app.accounting.useCurrency": "Use currency",
    "app.accounting.moreFields": "More fields",
    "app.accounting.datev.openInCompanyList": "Open in company list",
    "app.accounting.datev.noBuchungsdatenservice": "This company has not enabled the Buchungsdatenservice",
    "app.accounting.datev.searchByIdOrName": "Search by ID or name",
    "app.accounting.datev.filterService": "Filter service",
    "app.accounting.datev.expired": "Expired",
    "app.accounting.datev.connectedAt": "Connected on",
    "app.accounting.datev.verifyWithDatev": "Verify with Datev",
    "app.accounting.datev.connectedApps": "Connected applications",
    "app.accounting.sinceYear": "Since (year)",
    "app.accounting.searchPlaceholder": "Search...",
    "app.accounting.verrechnung.available": "Available",
    "app.accounting.verrechnung.connectionInfo": "There are records that can be assigned to this payment",
    "app.accounting.verrechnung.ignore": "Ignore",
    "app.accounting.verrechnung.assign": "Assign",
    "app.accounting.remainingAmount": "Remaining amount",
    "app.accounting.paymentDate": "Payment date",
    "app.accounting.records": "Records",
    "app.accounting.noPaymentsYet": "No payments yet",
    "app.accounting.paymentList": "Payment list",
    "app.accounting.dueDates": "Due dates",
    "app.accounting.keyboardShortcuts": "Keyboard shortcuts",
    "app.accounting.toFirstField": "to first field",
    "app.accounting.toLastField": "to last field",
    "app.accounting.recordOrigin.badge": "AI",
    "app.accounting.recordOrigin.ai": "AI proposal",
    "app.accounting.recordOrigin.ai.hint": "This draft entry was created automatically from the document.",
    "app.accounting.bruttoNettoMode": "Gross/net mode",
    "app.accounting.doppelerfassungWarning":
        "<b>Attention! </b>This invoice number has already been used. Please check for a duplicate entry!",
    "app.accounting.notesStatus.done": "Done",
    "app.accounting.notesStatus.inProgress": "In progress",
    "app.accounting.notesStatus.task": "Task",
    "app.notes.trigger": "Open notes",
    "app.notes.placeholder": "Write a note",
    "app.notes.send": "Send",
    "app.notes.sendHint": "to send",
    "app.notes.statusNone": "None",
    "app.notes.edit": "Edit note",
    "app.notes.delete": "Delete note",
    "app.notes.deleteConfirm": "Delete note?",
    "app.notes.deleteAll": "Delete all",
    "app.notes.deleteAllConfirm": "Delete all notes?",
    "app.notes.deleteAllConfirmDesc": "The entire thread will be removed.",
    "app.notes.emptyTitle": "No notes yet",
    "app.notes.emptyBody": "Write the first note.",
    "app.notes.emptyReadOnly": "No notes.",
    "app.notes.readOnly": "Read-only access. Notes cannot be changed.",
    "app.notes.loadError": "Notes could not be loaded.",
    "app.notes.retry": "Try again",
    "app.notes.edited": "edited",
    "app.notes.imported": "Imported",
    "app.notes.you": "You",
    "app.accounting.notes.unsaved": "Notes are available once the booking is saved.",
    "app.accounting.ok": "Ok",
    "app.accounting.selectContact": "Select contact",
    "app.accounting.addContactWithName": "Add contact with this name in the Contacts module",
    "app.accounting.copyTemplatesFromPreviousYear": "Copy templates from previous year",
    "app.accounting.contactsWithoutTemplates": "Contacts without templates",
    "app.accounting.festschreibenCount": "Finalize ({done}/{total})",
    "app.table.summe": "Total:",
    "app.table.modulLabel": "Module:",
    "app.table.bankingTransaction": "Banking transaction",
    "app.table.bankingTransactionId": "Banking transaction ID",
    "app.table.cashRegisterReceipt": "Cash register receipt",
    "app.table.cashRegisterReceiptId": "Cash register receipt ID",
    "app.table.autoRecord": "Automatic record",
    "app.table.saldoTransferRecord": "Balance carryforward",
    "app.table.expected": "expected",
    "app.table.actual": "actual",
    "app.table.avis.remove": "Remove payment notice",
    "app.table.avis.change": "Change payment notice",
    "app.table.moveToFE": "Move to FE",
    "app.table.moveToPOS": "Move to POS",
    "app.table.unsplit": "Unsplit",
    "app.table.toPeriod0": "To period 0",
    "app.table.toPeriod13": "To period 13",
    "app.table.payment.journaledWarning":
        "By this payment change some related <strong>JOURNALED</strong> entities will be cancelled.",
    "app.table.payment.confirmedWarning":
        "By this payment change some related <strong>CONFIRMED</strong> entities are also updated and/or removed.",
    "app.toolbar.aiImport.importPdf": "Import PDF with AI",
    "app.toolbar.aiImport.fibuRunning": "AI FIBU running...",
    "app.automation.d2r.running": "Creating booking drafts ({done}/{total})",
    "app.automation.d2r.summary":
        "{created} created, {withoutAccount} without account, {alreadyBooked} already booked, {refused} refused, {failed} failed",
    "app.automation.d2r.failed": "The operation could not be completed.",
    "app.automation.d2r.more": "and {count} more",
    "app.automation.d2r.reason.DOCUMENT_NOT_FOUND": "Document not found",
    "app.automation.d2r.reason.IN_TRASH": "Document is in the trash",
    "app.automation.d2r.reason.IN_NEW_DOCUMENTS": "Document is still in the new documents",
    "app.automation.d2r.reason.MISSING_DOCUMENT_DATE": "No document date",
    "app.automation.d2r.reason.MISSING_GROUP": "No bank account or cash register selected",
    "app.automation.d2r.reason.YEAR_NOT_IN_ACCOUNTING_YEARS":
        "The document year is not one of the company's accounting years",
    "app.automation.d2r.reason.PRODUCT_NOT_ALLOWED": "Booking from documents is not possible for this area",
    "app.automation.d2r.reason.MIRROR_SYNC_FAILED": "Booked, but the synchronisation failed",
    "app.automation.d2r.reason.CONVERSION_FAILED": "The document could not be read",
    "app.automation.d2r.reason.NOT_A_RECEIPT": "Informational document, not a receipt",
    "app.automation.d2r.reason.SAVE_FAILED": "The booking could not be saved",
    "app.automation.d2r.reason.PERMISSION_DENIED": "No write permission for this area",
    "app.toolbar.fillBank.unifyTwo.title": "Unify transaction with records",
    "app.toolbar.fillBank.unifyTwo.description":
        "Unifies one transaction-exported posting with one or more manually created records whose amounts sum up to the transaction, by the following criteria:",
    "app.toolbar.fillBank.unifyMultiple.title": "Unify multiple records",
    "app.toolbar.fillBank.unifyMultiple.description":
        "Unifies transaction-created records and manually created records with a filled-in contact by the following criteria:",
    "app.toolbar.fillBank.connectByDocs.title": "Connect via documents",
    "app.toolbar.fillBank.connectByDocs.description":
        "Connects selected bank postings with their source invoice (ER, ER_A, Deb) via shared documents by the following criteria:",
    "app.toolbar.fillBank.connectByDocs.skipNote": "Already connected bank postings are skipped.",
    "app.toolbar.fillBank.outcome.matched": "{count} matched",
    "app.toolbar.fillBank.outcome.ambiguous": "{count} ambiguous, nothing changed",
    "app.toolbar.fillBank.outcome.ambiguousZuordnung":
        "{count} ambiguous, not linked; Kreditor/Debitor filled where possible",
    "app.toolbar.fillBank.outcome.noMatch": "{count} without a match",
    "app.toolbar.fillBank.outcome.alreadyLinked": "{count} already linked",
    "app.toolbar.fillBank.outcome.journaled": "{count} journaled, not changed",
    "app.toolbar.fillBank.outcome.notFound": "{count} not found",
    "app.toolbar.fillBank.classification.step1":
        "1. Fill creditor or debitor from templates in ER or AR based on the <b>contact</b>",
    "app.toolbar.fillBank.classification.step2":
        "2. Connects bank postings from the banking import directly to the open invoice (ER, ER_A, Deb) by the following criteria:",
    "app.toolbar.fillBank.classification.step3":
        "3. Otherwise: unifies transaction-created records and manually created records with a filled-in contact by the following criteria:",
    "app.toolbar.fillBank.criteria.sumBetrag": "Sum of amounts = transaction amount",
    "app.toolbar.fillBank.criteria.invoiceNumInPurpose": "Invoice number in the payment purpose (Verwendungszweck)",
    "app.toolbar.fillBank.criteria.belegfeld1": "Belegfeld1",
    "app.toolbar.fillBank.criteria.sharedDocument": "Shared document",
    "app.toolbar.fillBank.criteria.openEqualsBank": "Open amount = bank amount",
    "app.toolbar.fillBank.criteria.creditorDebitor": "Creditor/Debitor",
    "app.toolbar.chainEdit.title": "Chain editing",
    "app.toolbar.chainEdit.description":
        "Saves the current record with + and immediately loads the next selected record in table order.",
    "app.toolbar.chainEdit.completed.title": "Chain editing completed",
    "app.toolbar.chainEdit.completed.message": "The selected postings have been fully processed.",
    "app.toolbar.duplicateCheck.title": "Duplicate check",
    "app.toolbar.duplicateCheck.description":
        "Filters the selection down to records sharing an invoice number and a gross amount.",
    "app.toolbar.journal.title": "Journal",
    "app.toolbar.showRemovedRecords": "show removed postings",
    "app.toolbar.paymentStatus.avis": "Avis",
    "app.toolbar.ust13b.with": "With § 13b",
    "app.toolbar.ust13b.without": "Without § 13b",
    "app.toolbar.export.csvDescription": "comma-separated file",
    "app.toolbar.export.pdfDescription": "printable PDF file",
    "app.toolbar.actions": "Actions",
    "app.toolbar.actions.saldoDescription": "Carry forward last year's closing balance",
    "app.toolbar.actions.aiReviewDescription":
        "Checks the selected postings against the AI data of the linked document.",
    "app.toolbar.actions.saldoTransfer": "Balance carry-forward",
    "app.toolbar.actions.saldoConfirm": "Carry forward the closing balance of {yearFrom} into {yearTo}?",
    "app.ustva.shortDescription": "Short description",
    "app.ustva.drilldown.kennzahl": "Code {route}",
    "app.ustva.drilldown.calculated": "Calculated code. No direct booking.",
    "app.ustva.drilldown.showBookings": "Show bookings for code {route}, {period}",
    "app.ustva.drilldown.composition.drill": "Break down code {route}",
    "app.ustva.drilldown.composition.subtracted": "is subtracted",
    "app.ustva.drilldown.composition.rate": "Tax rate {rate, number, percent}",
    "app.ustva.drilldown.composition.total": "Total",
    "app.ustva.drilldown.composition.deltaTotal": "Difference from the transmission",
    "app.ustva.drilldown.composition.rateNote":
        "Rounded commercially on the period total, not per booking. A per-booking tax amount is therefore not shown.",
    "app.ustva.bmgAbbr": "Tax base",
    "app.ustva.stAbbr": "Tax",
    "app.ustva.bemessung": "Tax base",
    "app.ustva.steuer": "Tax",
    "app.ustva.quartal": "Quarter {quartalNum} {year}",
    "app.ustva.transferticket": "Transfer ticket",
    "app.ustva.exports.journalRest": "Journal",
    "app.ustva.exports.allJournaled": "All bookings of this return are already journaled",
    "app.ustva.exports.filedBy": "Transmitted by",
    "app.ustva.exports.zahllast": "Payable / refund",
    "app.ustva.exports.export": "Export",
    "app.ustva.exports.inDms": "Saved in DMS: Taxes / VAT",
    "app.ustva.exports.inAccounting": "Booked in accounting (FE)",
    "app.ustva.exportSave.title": "Save protocol",
    "app.ustva.exportSave.open": "Save (DMS/FIBU)",
    "app.ustva.exportSave.dms": "DMS: Taxes / VAT",
    "app.ustva.exportSave.dmsDone": "Already saved",
    "app.ustva.exportSave.accounting": "Accounting: free entry (draft)",
    "app.ustva.exportSave.accountingDone": "Already booked",
    "app.ustva.exportSave.preview": "{soll} to {haben} · {amount} · {text} · {date} · draft",
    "app.ustva.exportSave.noFeWrite": "No write access to free entry",
    "app.ustva.exportSave.noKz83": "No payment or refund to book",
    "app.ustva.exportSave.noConfig": "Accounting year {year} is not set up",
    "app.ustva.exportSave.unsupportedSkr":
        "Chart of accounts {skr} is not supported; please check the chart of accounts of the accounting year",
    "app.ustva.exportSave.noPermission": "Saving needs write access to DMS or free entry",
    "app.ustva.exportSave.pdfMissing": "The transmission protocol could not be loaded",
    "app.ustva.exportSave.hashFailed": "The transmission protocol could not be read. Please reload the page.",
    "app.ustva.filings.title": "Included in VAT advance returns",
    "app.ustva.filings.empty": "This booking is not included in any transmitted advance return.",
    "app.ustva.filings.showPdf": "Show PDF",
    "app.ustva.filings.transmittedBy": "transmitted by",
    "app.ustva.filings.columnHeader": "VAT advance return",
    "app.ustva.bookings.module": "Module",
    "app.ustva.bookings.amount": "Amount",
    "app.ustva.bookings.status": "Status",
    "app.ustva.bookings.manual": "Manual correction",
    "app.ustva.bookings.total": "Total",
    "app.ustva.bookings.totalFiltered": "Total (filtered)",
    "app.ustva.bookings.onlyDeviations": "Deviations only ({count})",
    "app.ustva.bookings.search": "Search document or account",
    "app.ustva.bookings.prevDeviation": "Previous deviation",
    "app.ustva.bookings.nextDeviation": "Next deviation",
    "app.ustva.bookings.status.added": "New",
    "app.ustva.bookings.status.storno": "Storno",
    "app.ustva.bookings.status.removed": "Deleted",
    "app.ustva.bookings.status.changed": "Changed",
    "app.ustva.bookings.status.unchanged": "Unchanged",
    "app.ustva.reconciliation.filed": "Transmitted",
    "app.ustva.reconciliation.difference": "Difference",
    "app.ustva.reconciliation.today": "Value today",
    "app.ustva.reconciliation.complete": "These causes account for the difference in full.",
    "app.ustva.reconciliation.rest": "{count, plural, one {# further booking} other {# further bookings}}",
    "app.ustva.deviation.summary.scope":
        "{kzCount, plural, one {# code} other {# codes}} and {recordCount, plural, one {# booking} other {# bookings}} affected",
    "app.ustva.deviation.summary.openDeviations": "Show deviations",
    "app.ustva.deviation.export": "Deviations as PDF",
    "app.ustva.deviation.export.title": "Deviations from the VAT return",
    "app.ustva.deviation.export.filtered": "Filtered view: this list is not complete.",
    "app.ustva.deviation.export.total": "Total difference",
    "app.ustva.deviation.export.generated": "Created on",
    "app.ustva.diff.title": "Changes to {beleg}",
    "app.ustva.diff.identical": "This booking is unchanged.",
    "app.ustva.deviation.warning": "Transmitting today would produce a different return.",
    "app.ustva.deviation.unchanged": "Unchanged since the transmission on {date}.",
    "app.ustva.deviation.filedForQuarter": "This return was transmitted for Q{quartalNum} {year}.",
    "app.ustva.deviation.showQuarter": "Show the quarter",
    "app.ustva.deviation.noBaseline":
        "No Kennzahl breakdown was stored for this transmission, so it cannot be compared with the current figures.",
    "app.ustva.deviation.filedOn": "Transmitted on {date} by",
    "app.ustva.deviation.filed": "transmitted",
    "app.ustva.deviation.current": "today",
    "app.ustva.deviation.delta": "difference",
    "app.ustva.deviation.cause.added": "booked after the transmission",
    "app.ustva.deviation.cause.storno": "Storno counter-entry after the transmission",
    "app.ustva.deviation.cause.removed": "storniert or deleted after the transmission",
    "app.ustva.deviation.cause.changed": "changed after the transmission",
    "app.ustva.deviation.cause.manual": "manual correction at transmission time",
    "app.ustva.journalAfterExport.title": "Journal bookings",
    "app.ustva.journalAfterExport.body":
        "The transmission succeeded. Should the {count} bookings that fed this return be journaled now? Journaled bookings can only be corrected by a Storno.",
    "app.ustva.journalAfterExport.confirm": "Journal",
    "app.ustva.journalAfterExport.dismiss": "Later",
    "app.ustva.journalAfterExport.hint": "You can do this at any time from the list of transmissions.",
    "app.ustva.journalAfterExport.done": "Bookings were journaled",
    "app.ustva.groupA": "A. Taxable supplies, other services and free-of-charge transfers",
    "app.ustva.taxableSales19": "Taxable sales 19 %",
    "app.ustva.taxableSales7": "Taxable sales 7 %",
    "app.ustva.taxableSales0": "Taxable sales 0 %",
    "app.ustva.otherTaxRates": "Other tax rates",
    "app.ustva.agriculturalWithVatId": "Agricultural businesses with USt-ID",
    "app.ustva.salesPara24":
        "Sales subject to tax under § 24 UStG (sawmill products, beverages and alcoholic liquids, e.g. wine)",
    "app.ustva.groupB": "B. Tax-free supplies, other services and free-of-charge transfers",
    "app.ustva.intraCommunityDeliveriesWithVatId": "Intra-Community supplies with USt-ID",
    "app.ustva.newVehiclesToBuyerWithoutVatId": "New vehicles to buyers without USt-ID",
    "app.ustva.newVehiclesOutsideBusiness": "New vehicles outside a business (§ 2a UStG)",
    "app.ustva.otherTaxFreeWithInputTax":
        "Further tax-free sales with input tax deduction (e.g. export supplies, sales under § 4 No. 2 to 7 UStG)",
    "app.ustva.taxFreeWithoutInputTax":
        "Tax-free sales without input tax deduction (e.g. sales under § 4 No. 8 to 29 UStG)",
    "app.ustva.groupC": "C. Intra-Community acquisitions",
    "app.ustva.taxFreeEuAcquisitions":
        "Tax-free EU acquisitions of certain goods and investment gold (§§ 4b and 25c UStG)",
    "app.ustva.taxableEuAcquisitions19": "Taxable EU acquisitions 19 %",
    "app.ustva.taxableEuAcquisitions7": "Taxable EU acquisitions 7 %",
    "app.ustva.taxableEuAcquisitions0": "Taxable EU acquisitions 0 %",
    "app.ustva.newVehiclesFromSuppliersWithoutVatId":
        "New vehicles (§ 1b para. 2 and 3 UStG) from suppliers without USt-ID",
    "app.ustva.groupD": "D. Recipient of services as tax debtor (§ 13b UStG)",
    "app.ustva.otherServicesPara3a":
        "Other services under § 3a para. 2 UStG of an entrepreneur established in the rest of the Community territory (§ 13b para. 1 UStG)",
    "app.ustva.salesUnderGrEStG": "Sales falling under the GrEStG (§ 13b para. 2 No. 3 UStG)",
    "app.ustva.otherServicesPara13b": "Other services (§ 13b para. 2 No. 1, 2, 4 to 12 UStG)",
    "app.ustva.groupE": "E. Supplementary information on sales",
    "app.ustva.firstBuyerTriangularTransactions":
        "Supplies of the first buyer in intra-Community triangular transactions (§ 25b UStG)",
    "app.ustva.taxableSalesRecipientOwesTax":
        "Taxable sales of the supplying entrepreneur for which the recipient owes the tax under § 13b para. 5 UStG",
    "app.ustva.nonTaxableOtherServicesPara18b": "Non-taxable other services under § 18b sentence 1 No. 2 UStG",
    "app.ustva.otherNonTaxableSales": "Other non-taxable sales (place of supply not domestic)",
    "app.ustva.groupF": "F. Deductible input tax amounts and correction of input tax deduction",
    "app.ustva.inputTaxFromInvoices":
        "Input tax amounts from invoices of other entrepreneurs (§ 15 para. 1 sentence 1 No. 1 UStG), from services within the meaning of § 13a para. 1 No. 6 UStG (§ 15 para. 1 sentence 1 No. 5 UStG) and from intra-Community triangular transactions (§ 25b para. 5 UStG)",
    "app.ustva.inputTaxFromIntraCommunityAcquisition":
        "Input tax amounts from the intra-Community acquisition of goods (§ 15 para. 1 sentence 1 No. 3 UStG)",
    "app.ustva.importVat": "Import VAT incurred (§ 15 para. 1 sentence 1 No. 2 UStG)",
    "app.ustva.inputTaxFromPara13b":
        "Input tax amounts from services within the meaning of § 13b UStG (§ 15 para. 1 sentence 1 No. 4 UStG)",
    "app.ustva.inputTaxAverageRates": "Input tax amounts calculated using general average rates (§ 23a UStG)",
    "app.ustva.inputTaxNewVehiclesSmallBusiness":
        "Input tax deduction for intra-Community supplies of new vehicles outside a business (§ 2a UStG) and by small entrepreneurs within the meaning of § 19 para. 1 UStG (§ 15 para. 4a UStG)",
    "app.ustva.inputTaxCorrectionPara15a": "Correction of input tax deduction (§ 15a UStG)",
    "app.ustva.inputTax": "Input tax",
    "app.ustva.remainingAmount": "Remaining amount",
    "app.ustva.groupG": "G. Other tax amounts",
    "app.ustva.taxFromChangeOfTaxationForm":
        "Tax due to a change in the form of taxation as well as additional tax on taxed prepayments and similar due to a change in the tax rate",
    "app.ustva.incorrectlyShownTaxPara14c":
        "Tax amounts incorrectly or unjustifiably shown in invoices (§ 14c UStG) as well as tax amounts owed under § 6a para. 4 sentence 2, § 17 para. 1 sentence 7, § 25b para. 2 UStG or by a remover or warehouse keeper under § 13a para. 1 No. 6 UStG",
    "app.ustva.groupH": "H. VAT prepayment / surplus",
    "app.ustva.deductionSpecialPrepayment":
        "Deduction of the assessed special prepayment for permanent deadline extension",
    "app.ustva.remainingVatPrepayment": "Remaining VAT prepayment / surplus (negative)",
    "app.ustva.groupI":
        "I. Supplementary information on reductions under § 17 para. 1 sentence 1, 2 in conjunction with para. 2 No. 1 sentence 1 UStG",
    "app.ustva.reductionOfTaxBase": "Reduction of the tax base",
    "app.ustva.reductionOfDeductibleInputTax": "Reduction of deductible input tax amounts",
    "app.ustva.aiCheckFailed": "AI check failed",
    "app.ustva.elsterUstvaOk": "Elster UStVA ok",
    "app.ustva.missingTaxInfo": "to continue, please enter the tax information in your company settings",
    "app.ustva.selectSpecificPeriod": "to continue, please select a specific period",
    "app.ustva.yearWithoutUstva":
        'This year is set to "No VAT return" in the year settings. Only the annual declaration is filed, so sending the advance return via ELSTER is disabled.',
    "app.ustva.missingElsterRole":
        "You are not allowed to submit to ELSTER. This requires the “ELSTER submitter” role, which a company admin can grant you.",
    "app.ustva.correctedReturn": "Corrected return",
    "app.ustva.documentsSubmittedSeparately": "Documents are submitted separately",
    "app.ustva.checkReturnWithAi": "Check return with AI",
    "app.ustva.aiCheckExperimentalHint": "Experimental - check the plausibility of the figures with AI",
    "app.ustva.aiCheckReviewNotice":
        "The AI check is a non-binding plausibility check and does not replace professional review. ELSTER transmission takes place only after your review and explicit action.",
    "app.ustva.followUpQuestions": "Follow-up questions:",
    "app.ustva.certificateInvalid": ".pfx certificate is invalid",
    "app.ustva.pinInvalid": "PIN is incorrect",
    "app.ustva.encrypting": "Encrypting",

    "avm.nav.dashboard": "Dashboard",
    "avm.nav.assets": "Assets",
    "avm.nav.movements": "Movements",
    "avm.nav.import": "Import",
    "avm.nav.restwert": "Residual Value Register",
    "avm.nav.spiegel": "Fixed Asset Schedule",
    "avm.nav.forecast": "Depreciation Forecast",
    "avm.nav.inventory": "Inventory List",
    "avm.nav.batches": "Posting Batches",
    "avm.nav.rules": "Calculation rules",
    "avm.rules.title": "Calculation rules of the fixed-asset register",
    "avm.rules.search": "Search rule, term or ID",
    "avm.rules.statute": "Legal basis",
    "avm.rules.statute.none": "Register rule without a statutory requirement of its own",
    "avm.rules.example": "Example",
    "avm.rules.status.planned": "planned",
    "avm.rules.status.unsupported": "not supported",
    "avm.rules.glossary.title": "Options in the fixed-asset register",
    "avm.rules.noResults": "No rule or term matches the search.",
    "avm.rules.graph.toggle": "Relationships",
    "avm.rules.graph.label": "Relationships of the rules in {topic}",
    "avm.rules.graph.stub": "Rules in the section {topic}",
    "avm.rules.graph.hint": "An arrow points to the rule a rule builds on. Click a rule to jump to it.",
    "avm.rules.loading": "Loading",
    "avm.rules.chart.toggle": "Example as a chart",
    "avm.rules.chart.linear": "Linear",
    "avm.rules.chart.degressive": "Declining balance",
    "avm.rules.chart.bookValue": "Book value at the end of the fiscal year, computed by the register",
    "avm.rules.chart.afa": "Depreciation per fiscal year, computed by the register",
    "avm.rules.chart.afaSeries": "Depreciation",

    "avm.dashboard.title": "Dashboard",
    "avm.dashboard.kpi.bookValueEnd": "Book value at year end",
    "avm.dashboard.kpi.bookValueStart": "Book value at year start",
    "avm.dashboard.kpi.afa": "Depreciation {year}",
    "avm.dashboard.kpi.gwgShare": "of which low-value assets {value}",
    "avm.dashboard.kpi.additions": "Additions",
    "avm.dashboard.kpi.disposals": "Disposals",
    "avm.dashboard.kpi.cost": "Acquisition cost",
    "avm.dashboard.kpi.count": "{count} assets",
    "avm.dashboard.lastAdditions": "Recent additions",
    "avm.dashboard.todo.title": "To do",
    "avm.dashboard.todo.usefulLifeEndsNextYear": "{count} assets are fully depreciated in {year}",
    "avm.dashboard.todo.iabDeadlineDue": "The {year} investment deduction must be assigned to an asset this year",
    "avm.dashboard.todo.iabDeadlineReached": "The {year} investment deduction is overdue and must be reversed",
    "avm.dashboard.todo.batchReady": "A posting batch for {year} is waiting to be transferred",
    "avm.dashboard.todo.yearNotClosed": "{year} was transferred but has not been closed yet",
    "avm.dashboard.todo.poolPolicyWithoutGroup":
        "The pooled-asset policy is active, but there is no pooled-asset group",
    "avm.dashboard.todo.poolAssetWrongGroup": "{count} pooled items are stored outside the pooled-item group",
    "avm.dashboard.todo.snapshotDrift": "Deviation from the {year} year-end closing",
    "avm.dashboard.todo.none": "Nothing to do",

    "avm.iab.title": "Investment allowances",
    "avm.iab.lead":
        "Deduct up to 50 percent of the expected acquisition cost in advance under section 7g (1) EStG. The amount must be assigned by the end of the third year following the acquisition, otherwise the deduction has to be reversed.",
    "avm.iab.add": "Record an investment allowance",
    "avm.iab.col.formationYear": "Formation year",
    "avm.iab.col.deadlineYear": "Deadline",
    "avm.iab.col.amount": "Amount",
    "avm.iab.col.asset": "Assigned to",
    "avm.iab.col.status": "Status",
    "avm.iab.status.assigned": "assigned",
    "avm.iab.status.open": "open",
    "avm.iab.status.dueThisYear": "Deadline runs out {year}",
    "avm.iab.status.overdue": "Deadline exceeded",
    "avm.iab.noFiscalYear":
        "Without computable fiscal years no investment deductions can be kept. The reason is shown at the top of this page.",
    "avm.iab.empty": "No investment allowances recorded",
    "avm.iab.form.formationYear": "Formation year",
    "avm.iab.form.amount": "Amount",
    "avm.iab.form.asset": "Fixed asset",
    "avm.iab.form.assetNone": "Not assigned",
    "avm.iab.form.note": "Note",
    "avm.iab.form.ceiling": "at most {amount}, which is 50 percent of the acquisition cost",
    "avm.iab.error.amount": "Amount must be greater than 0",
    "avm.iab.error.ceiling": "An investment allowance may not exceed 50 percent of the acquisition cost",
    "avm.iab.error.assigned": "This investment allowance has since been assigned to another asset",
    "avm.iab.error.yearLocked": "Fiscal year {year} is closed. The assignment can only be changed after reopening it.",
    "avm.iab.error.yearLockedBefore":
        "Fiscal year {year} lies before the closed fiscal year {latest}. The assignment can only be changed once fiscal year {latest} is reopened.",
    "avm.iab.delete": "Delete investment allowance",

    "avm.settings.fiscalYears.title": "Start of the fiscal year",
    "avm.settings.fiscalYears.lead":
        "The fixed asset register uses the calendar year until a start is entered. The start applies to every year; a later switch is entered as a change from a given year.",
    "avm.settings.fiscalYears.add": "Add change",
    "avm.settings.fiscalYears.year": "From year",
    "avm.settings.fiscalYears.calendarYear": "{month} (calendar year)",
    "avm.settings.fiscalYears.change": "from {year}: {month}",
    "avm.settings.fiscalYears.month": "Month",
    "avm.settings.fiscalYears.accountingDiffers":
        "Accounting starts the fiscal year in {accountingMonth}, the fixed asset register in {avmMonth}.",
    "avm.settings.fiscalYears.takeOver": "Take the start over from accounting",
    "avm.settings.fiscalYears.base": "The fiscal year starts in: {month}",
    "avm.settings.fiscalYears.baseMonth": "Start of the fiscal year",
    "avm.settings.fiscalYears.baseSave": "Save start",
    "avm.settings.fiscalYears.baseDelete": "Delete start",
    "avm.settings.fiscalYears.baseDelete.confirm":
        "Delete the start of the fiscal year? The calendar year applies afterwards.",
    "avm.settings.fiscalYears.closed": "Closed",
    "avm.settings.fiscalYears.delete": "Delete change",
    "avm.settings.fiscalYears.delete.confirm": "Delete the change from {year} from the fixed asset register?",

    "avm.settings.title": "Fixed Asset Register Settings",
    "avm.settings.gwgPolicy": "Low-Value Asset Policy",
    "avm.settings.gwgPolicy.immediate": "Immediate expensing",
    "avm.settings.gwgPolicy.pool": "Pooled",
    "avm.settings.roundingMode": "Rounding of new assets (default)",
    "avm.settings.roundingMode.floorEuro": "Round down to full euro",
    "avm.settings.roundingMode.hint":
        "Preselected in the wizard and the import for new assets. Existing assets keep their rounding; it is changed on the asset.",
    "avm.settings.roundingMode.cent": "To the cent",
    "avm.settings.memoValue": "Residual book value of new assets (default)",
    "avm.settings.memoValue.hint":
        "Preselected in the wizard and the import for new assets. Existing assets keep their value; it is changed on the asset.",
    "avm.settings.vatDefault": "Default VAT Treatment",
    "avm.settings.vatDefault.net": "Net",
    "avm.settings.vatDefault.gross": "Gross",
    "avm.settings.vatDefault.partial": "Partially input-tax deductible",
    "avm.settings.vatDefault.kust": "Small business (§19 UStG)",
    "avm.settings.usedByEnabled": "Track user / location",
    "avm.settings.save": "Save",
    "avm.settings.roles.title": "Roles",
    "avm.settings.roles.description": "Controls who may approve postings in the fixed asset register.",
    "avm.settings.taxParameters.title": "Tax Parameters",
    "avm.settings.taxParameters.hint":
        "The values shown are valid from the stated date. A change in the law is reflected by a new row with its own effective date, not by editing this row.",
    "avm.settings.taxParameters.validFrom": "Valid from",
    "avm.settings.taxParameters.immediateExpenseLimit": "Immediate expensing limit",
    "avm.settings.taxParameters.gwgLimit": "Low-value asset limit",
    "avm.settings.taxParameters.poolMin": "Pool minimum",
    "avm.settings.taxParameters.poolMax": "Pool maximum",
    "avm.settings.taxParameters.poolYears": "Pool depreciation years",
    "avm.settings.taxParameters.specialRate": "Special depreciation rate",
    "avm.settings.taxParameters.specialYears": "Special depreciation years",
    "avm.settings.taxParameters.iabDeadline": "IAB deadline",

    "avm.settings.anonymize.title": "Anonymise user",
    "avm.settings.anonymize.lead":
        "Replaces the name in Used by with anonymised on every affected asset. This action is final and is recorded in the change log.",
    "avm.settings.anonymize.person": "Used by",
    "avm.settings.anonymize.action": "Anonymise",
    "avm.settings.anonymize.confirm":
        "{count, plural, one {1 asset will be permanently anonymised} other {# assets will be permanently anonymised}}. This action cannot be undone. Continue?",
    "avm.settings.anonymize.done": "{count, plural, one {1 asset anonymised} other {# assets anonymised}}",
    "avm.settings.anonymize.empty": "No names on file",
    "avm.settings.anonymize.approverOnly": "Only the AVM approver role may anonymise.",

    "avm.groups.title": "Asset Groups",
    "avm.groups.new": "New group",
    "avm.groups.seed": "Create default groups",
    "avm.groups.seed.confirm":
        "Create the default groups? Existing asset groups stay unchanged. Posting accounts are created only at the transfer to accounting.",
    "avm.groups.seed.skipped": "Not created, the name is already taken: {names}",
    "avm.groups.name": "Name",
    "avm.groups.assetKonto": "Asset account",
    "avm.groups.afaKonto": "Depreciation account",
    "avm.groups.specialAfaKonto": "Special depreciation account",
    "avm.groups.specialAfaKonto.hint":
        "Without a special depreciation account, special depreciation of this group cannot be transferred to the accounting.",
    "avm.groups.writeDownKonto": "Write-down account (Teilwertabschreibung)",
    "avm.groups.writeDownKonto.hint":
        "Without this account, a Teilwertabschreibung in this asset group cannot be transferred.",
    "avm.groups.afaaKonto": "AfaA account",
    "avm.groups.afaaKonto.hint":
        "Account for extraordinary technical or economic wear and tear. Without it, an AfaA in this asset group cannot be transferred.",
    "avm.groups.writeUpKonto": "Write-up account",
    "avm.groups.writeUpKonto.hint": "Without a write-up account, no write-up can be booked in this asset group.",
    "avm.groupAccounts.title": "Posting accounts",
    "avm.groupAccounts.hint":
        "Only the transfer to accounting needs the posting accounts. Every account is optional; a posting line without its account is not transferred.",
    "avm.groupAccounts.skr": "Chart of accounts",
    "avm.groupAccounts.skr.hint":
        "This asset group's posting accounts belong to this chart of accounts; it must match the chart of accounting.",
    "avm.groupAccounts.skr.clears": "Changing the chart of accounts empties this group's accounts.",
    "avm.groupAccounts.save": "Save posting accounts",
    "avm.groupAccounts.drawer.title": "Posting accounts: {name}",
    "avm.groups.kind": "Kind",
    "avm.groups.kind.movable": "Movable assets",
    "avm.groups.kind.intangible": "Intangible assets",
    "avm.groups.kind.nonDepreciable": "Non-depreciable assets",
    "avm.groups.kind.building": "Buildings",
    "avm.groups.kind.immovable": "Immovable assets (outdoor facilities)",
    "avm.groups.kind.pool": "Pool (Sammelposten)",
    "avm.groups.role": "Role",
    "avm.groups.role.none": "none",
    "avm.groups.role.goodwill": "Goodwill",
    "avm.groups.role.land": "Land of a built-on plot",
    "avm.groups.role.hint":
        "The role carries rules: goodwill takes no write-up, and when a purchase is split the land share goes to the group with this role.",
    "avm.groups.role.seeded": "A default group keeps its role, even when it is renamed.",
    "avm.groups.role.kind":
        "This role does not fit the kind: goodwill only for intangible assets, land only for non-depreciable assets.",
    "avm.groups.kind.hint":
        "The kind decides which depreciation methods are allowed and whether special depreciation under § 7g EStG is possible.",
    "avm.groups.kind.locked":
        "Assets are assigned to this group. The kind can only change once those assets are assigned to another group or their transfers into this group are cancelled.",
    "avm.groups.disposalGainKonto": "Disposal gain account",
    "avm.groups.disposalLossKonto": "Disposal loss account",
    "avm.assets.method.kindNotAllowed": "Not allowed in this asset group",
    "avm.assets.method.notInBand": "Not allowed for this acquisition cost",
    "avm.assets.method.error.kind":
        "This method is not allowed in the selected asset group. Please choose an allowed method or another asset group.",
    "avm.wizard.error.methodKind":
        "This method is not allowed in the selected asset group. Please choose an allowed method or another asset group.",
    "avm.wizard.error.specialKind":
        "Special depreciation under § 7g EStG is only possible for movable assets. Please untick it or choose an asset group for movable assets.",
    "avm.wizard.special.kindUnavailable":
        "The selected asset group is not for movable assets; special depreciation under § 7g EStG is not possible.",
    "avm.movements.error.specialKind":
        "Special depreciation under § 7g EStG is only possible for movable assets, and this asset belongs to a group of another kind. Please assign the asset to a group for movable assets or choose another movement type.",
    "avm.movements.error.specialKindTransferred":
        "Special depreciation under § 7g EStG is only possible for movable assets, and this asset was transferred into a group of another kind. Please cancel the transfer first or choose another movement type.",
    "avm.movements.form.targetGroup.kindNotAllowed": "Not allowed for this asset's depreciation method",
    "avm.movements.form.targetGroup.specialNotAllowed":
        "Not allowed: special depreciation under § 7g EStG is booked on this asset",
    "avm.assets.method.none": "No depreciation",
    "avm.wizard.method.none": "No depreciation (non-depreciable)",
    "avm.wizard.method.noneDesc":
        "for land, construction in progress, advance payments and financial assets: the book value stays unchanged until disposal, impairment or transfer",
    "avm.inventory.status.nonDepreciable": "Non-depreciable",
    "avm.movements.error.specialNone":
        "Special depreciation under § 7g EStG is not possible for a non-depreciable asset. Please book an impairment as a write-down.",
    "avm.movements.error.noneBlocked":
        "This movement type is not possible for a non-depreciable asset. Please choose another movement type.",
    "avm.movements.form.targetGroup.completed": "Not allowed: the asset was completed and is being depreciated",
    "avm.movements.form.targetGroup.iabNotAllowed": "Not allowed: an investment deduction is assigned to the asset",
    "avm.movements.error.completionLife":
        "Depreciation starts with this transfer. Please enter the useful life in months, at least 1.",
    "avm.movements.form.completionLife": "Useful life from completion (months)",
    "avm.movements.form.completionLifeHint": "The transfer date is the start of depreciation.",
    "avm.wizard.error.iabKind":
        "An investment deduction under § 7g EStG is only possible for movable assets. Please clear the selection or choose a group for movable assets.",
    "avm.wizard.iab.kindUnavailable":
        "The selected group is not set up for movable assets; an investment deduction under § 7g EStG cannot be assigned.",
    "avm.wizard.preview.noneMethod":
        "No depreciation: the book value stays unchanged until disposal, impairment or transfer.",
    "avm.import.row.error.noneBookValue":
        "Non-depreciable asset with a book value below its acquisition cost. Please import the row with the book value equal to the cost and then book the impairment as a write-down.",
    "avm.iab.form.assetKindNotAllowed":
        "Not allowed: the asset is or was in a group that takes no investment deduction under § 7g EStG",
    "avm.groups.defaultUsefulLife": "Default useful life",
    "avm.groups.sortOrder": "Sort order",
    "avm.groups.empty": "No asset groups yet",
    "avm.groups.delete.confirm": "Delete this asset group?",

    "avm.assets.title": "Assets",
    "avm.assets.new": "New asset",
    "avm.assets.search": "Search",
    "avm.assets.group": "Asset group",
    "avm.assets.allGroups": "All groups",
    "avm.assets.inventoryNum": "Inventory number",
    "avm.assets.name": "Name",
    "avm.assets.supplier": "Supplier",
    "avm.assets.acquisitionDate": "Acquisition date",
    "avm.assets.acquisitionDate.error.tooEarly": "Assets acquired before 01.01.2018 are not supported.",
    "avm.assets.cost": "Acquisition cost",
    "avm.assets.bookValueStart": "Book value start",
    "avm.assets.additions": "Additions",
    "avm.assets.disposals": "Disposals",
    "avm.assets.afa": "Depreciation",
    "avm.assets.bookValueEnd": "Book value end",
    "avm.assets.quantity": "Quantity",
    "avm.assets.method": "Depreciation method",
    "avm.assets.method.linear": "Straight-line",
    "avm.assets.method.gwg": "Low-value asset",
    "avm.assets.method.pool": "Pool",
    "avm.assets.method.degressive": "Declining balance",
    "avm.assets.method.degressiveUnavailable":
        "Not available: the acquisition date is outside the statutory window for declining-balance depreciation.",
    "avm.assets.usefulLife": "Useful life",
    "avm.assets.location": "Location",
    "avm.assets.costCenter": "Cost center",
    "avm.assets.usedBy": "Used by",
    "avm.assets.note": "Note",
    "avm.assets.vatTreatment": "VAT treatment",
    "avm.assets.opening": "Opening balance",
    "avm.assets.openingYear": "Opening year",
    "avm.assets.openingBookValue": "Opening book value",
    "avm.assets.openingRemainingMonths": "Remaining months",
    "avm.assets.total": "Total",
    "avm.assets.groupSum": "Group total",
    "avm.assets.empty": "No assets yet",
    "avm.assets.tag.gwg": "Low-value",
    "avm.assets.tag.memo": "Memo value",
    "avm.assets.tag.addition": "Addition",
    "avm.assets.tag.disposal": "Disposal",
    "avm.assets.delete.confirm": "Delete this asset?",

    "avm.movements.title": "Movements",
    "avm.movements.eyebrow": "Subsequent changes",
    "avm.movements.lead":
        "Every change to an asset is recorded as a dated movement and affects depreciation, book value and reports. Nothing is overwritten, the history stays traceable.",
    "avm.movements.yearTable": "Movements in fiscal year {year}",
    "avm.movements.add": "Record movement",
    "avm.movements.noAssets":
        "No asset has been recorded for this company yet. A movement is always a change to an existing asset, so it can only be recorded once the first asset exists.",
    "avm.movements.noAssets.action": "Record an asset",
    "avm.movements.empty": "No movements in fiscal year {year}",
    "avm.movements.closedYear": "Fiscal year {year} is closed. Movements are possible after reopening it.",
    "avm.movements.col.asset": "Asset",
    "avm.movements.col.type": "Type",
    "avm.movements.col.amount": "Amount",
    "avm.movements.col.effect": "Effect on book value",
    "avm.movements.type.zugang": "Addition",
    "avm.movements.type.disposal": "Disposal",
    "avm.movements.type.partialDisposal": "Partial disposal",
    "avm.movements.type.costAddition": "Subsequent acquisition cost",
    "avm.movements.type.transfer": "Transfer",
    "avm.movements.type.writeDown": "Impairment",
    "avm.movements.type.writeUp": "Write-up",
    "avm.movements.type.specialDepreciation": "Special depreciation",
    "avm.movements.type.storno": "Reversal",
    "avm.movements.desc.disposal":
        "Sale, withdrawal or scrapping. Depreciation runs pro rata to the disposal month, the remaining book value is written off and the gain or loss is determined.",
    "avm.movements.desc.partialDisposal":
        "Part of a multi-unit asset leaves. Cost and accumulated depreciation are written off proportionally, the asset continues with the rest.",
    "avm.movements.desc.costAddition":
        "Extension or improvement after acquisition. Raises the depreciation base from the year of the expense, the remaining life can be adjusted.",
    "avm.movements.desc.transfer": "Change of asset group. Values stay, only the presentation changes.",
    "avm.movements.desc.writeDown":
        "Permanent impairment under section 253 (3) HGB. Reduces the book value in addition to scheduled depreciation, down to at most the memo value; the remaining useful life can be adjusted.",
    "avm.movements.desc.writeUp":
        "Reversal of value after an earlier impairment. At most up to the book value the asset would have without the write-down.",
    "avm.movements.desc.specialDepreciation":
        "Special depreciation under section 7g (5) EStG. In the year of acquisition and the following four years, at most {rate} percent of the assessment base in total.",
    "avm.movements.desc.specialDepreciationAny":
        "Special depreciation under section 7g (5) EStG. In the year of acquisition and the following four years, at most the percentage in force for the year of acquisition, of the assessment base in total.",
    "avm.movements.effect.zugang": "Capitalised at cost",
    "avm.movements.effect.disposal":
        "Depreciation to the disposal month {afa}, remaining book value {bookValue} written off, proceeds {proceeds}: {gainLoss}",
    "avm.movements.effect.gain": "gain {amount}",
    "avm.movements.effect.loss": "loss {amount}",
    "avm.movements.effect.partialDisposal":
        "{leaving} of {before} units leave, cost {cost} written off proportionally, {after} units remain",
    "avm.movements.effect.costAddition":
        "Base from {before} to {after}, depreciation {year} {afa}, book value at year end {bookValue}",
    "avm.movements.effect.transfer": "Move from group {from} to {to}, book value {bookValue} unchanged",
    "avm.movements.effect.writeDown": "Book value without the write-down {before}, with it {after}, applied {applied}",
    "avm.movements.effect.writeUp":
        "Write-up {applied} of {requested}, ceiling {ceiling}, book value at year end {bookValue}",
    "avm.movements.effect.specialDepreciation":
        "Special depreciation {applied}, remaining cap {cap}, book value at year end {bookValue}",
    "avm.movements.effect.notApplied": "has no effect",
    "avm.movements.effect.cancelled": "reversed",
    "avm.movements.effect.storno": "reverses the movement of {date}",
    "avm.movements.effect.none": "No effect in the selected fiscal year",
    "avm.movements.effect.kontoMissing":
        "This asset's group has no gain and loss accounts. The disposal will later produce a posting line without an account; enter the accounts in the asset group's posting accounts.",
    "avm.movements.form.asset": "Asset",
    "avm.movements.form.type": "Movement type",
    "avm.movements.form.amount": "Amount",
    "avm.movements.form.maxAmount": "at most {amount}",
    "avm.movements.form.remainingMonthsRequired":
        "Remaining useful life required, otherwise the amount is expensed in full immediately",
    "avm.movements.form.noTargetGroup": "Not possible: there is no other asset group to transfer into.",
    "avm.movements.form.proceeds": "Proceeds",
    "avm.movements.form.quantity": "Quantity",
    "avm.movements.form.quantityHint": "proportional write-off of cost and accumulated depreciation",
    "avm.movements.form.targetGroup": "Target group",
    "avm.movements.form.remainingMonths": "New remaining life (months)",
    "avm.movements.form.remainingMonthsHint": "leave empty to keep the current remaining life",
    "avm.movements.form.note": "Note",
    "avm.movements.form.effectTitle": "Effect",
    "avm.movements.form.writeDownKind": "Kind of write-down",
    "avm.movements.form.writeDownKind.teilwert": "Teilwertabschreibung (write-down to fair value)",
    "avm.movements.form.writeDownKind.teilwertHint": "balance-sheet accounting only (Betriebsvermögensvergleich)",
    "avm.movements.form.writeDownKind.afaa": "AfaA (extraordinary wear and tear)",
    "avm.movements.form.writeDownKind.afaaNone":
        "Not possible while the asset is not depreciated (§ 7 Abs. 1 Satz 7 EStG); please record it as a Teilwertabschreibung.",
    "avm.movements.error.date": "Date required",
    "avm.movements.error.amount": "Amount required",
    "avm.movements.error.quantity": "Quantity between 1 and the current quantity required",
    "avm.movements.error.targetGroup": "Target group required",
    "avm.movements.error.writeUpCeiling": "Write-up at most {amount}",
    "avm.movements.error.specialCap": "Special depreciation at most {amount}",
    "avm.movements.error.specialWindow":
        "Special depreciation only in the year of acquisition and the following four years",
    "avm.movements.error.writeDownAmount": "Impairment at most {amount}",
    "avm.movements.error.writeDownKind": "Please choose Teilwertabschreibung or AfaA.",
    "avm.movements.error.taxParametersMissing":
        "No maximum can be determined: no tax parameters are on file. Please contact support.",
    "avm.movements.error.periodMissing": "No fiscal year is configured for this date",
    "avm.movements.error.yearLocked":
        "Fiscal year {year} is closed. The movement can only be recorded after reopening it.",
    "avm.movements.error.yearLockedBefore":
        "Fiscal year {year} lies before the closed fiscal year {latest}. The movement can only be recorded once fiscal year {latest} is reopened.",
    "avm.movements.error.specialMethod":
        "Special depreciation is only possible alongside straight-line depreciation (§ 7a Abs. 4 EStG). Please switch the asset to straight-line depreciation or choose a different movement type.",
    "avm.movements.error.poolSpecial":
        "Special depreciation is not possible for a pooled asset: a pooled asset is depreciated as a group, not as an individual asset. Please choose a different movement type.",
    "avm.movements.error.poolWriteDown":
        "An impairment write-down is not possible for a pooled asset: a pooled asset is depreciated as a group, not as an individual asset. Please choose a different movement type.",
    "avm.movements.error.poolWriteUp":
        "A write-up is not possible for a pooled asset: a pooled asset is depreciated as a group, not as an individual asset. Please choose a different movement type.",
    "avm.movements.error.poolCostAddition":
        "Subsequent acquisition costs are not possible for a pooled asset: a pooled asset is depreciated as a group, not as an individual asset. Please choose a different movement type.",
    "avm.movements.error.writeUpFirmenwert":
        "A write-up on acquired goodwill is not permitted under § 253 (5) sentence 2 HGB. The recovery in value is not booked.",
    "avm.movements.error.writeUpUnknownGroup":
        "The asset group the asset belongs to on this date was not found. Please reload the page.",
    "avm.movements.storno.title": "Reverse movement",
    "avm.movements.storno.date": "Reversal date",
    "avm.movements.storno.text":
        "The movement is cancelled by a reversal movement. Both stay visible, neither has an effect.",

    "avm.detail.plan": "Depreciation Schedule",
    "avm.detail.plan.year": "Year",
    "avm.detail.plan.months": "Months",
    "avm.detail.plan.status.closed": "Closed",
    "avm.detail.plan.status.current": "Current",
    "avm.detail.plan.status.plan": "Planned",
    "avm.detail.plan.cumulativeAfaEnd": "Cumulative AfA End",
    "avm.detail.plan.status": "Status",
    "avm.detail.plan.empty": "No depreciation schedule available",
    "avm.detail.chart": "Chart",
    "avm.detail.stammdaten": "Master Data",
    "avm.detail.edit": "Edit",
    "avm.detail.delete": "Delete",
    "avm.detail.bookValue": "Book value",
    "avm.detail.bookValue.noRowForYear": "Not recorded for this year",
    "avm.detail.kontierung": "Account assignment",
    "avm.detail.kontierung.missing":
        "This asset group has no account configured. The posting batch cannot post this line.",
    "avm.assetDetail.iabReduction": "Investment allowance {amount} reduces the depreciation base to {base}",
    "avm.documents.title": "Receipts and documents",
    "avm.documents.addFile": "File",
    "avm.documents.open": "open",
    "avm.documents.unlink": "Remove link",
    "avm.documents.tag.avm": "Asset register",
    "avm.documents.tag.record": "Accounting",
    "avm.documents.empty": "No documents yet",
    "avm.documents.recordHint": "Document of the linked record",
    "avm.documents.recordLoading": "Loading the documents of the linked record",
    "avm.documents.missing": "Document {id} not found",
    "avm.documents.noDmsAccess": "Read access to the documents module is required for receipts",
    "avm.documents.noWriteAccess": "Write access to the asset register is required for receipts",
    "avm.detail.changeLog": "Change Log",
    "avm.detail.changeLog.empty": "No changes yet",
    "avm.detail.notFound": "Asset not found",

    "avm.restwert.title": "Residual Value Register",
    "avm.restwert.subtitle": "Overview of residual values as of the reference date",
    "avm.restwert.legend": "Legend",
    "avm.restwert.empty": "No data available",
    "avm.export.csv": "Export CSV",
    "avm.export.pdf": "Export PDF",
    "avm.export.xlsx": "Export Excel",

    "avm.spiegel.title": "Fixed Asset Schedule",
    "avm.spiegel.subtitle": "Fiscal year",
    "avm.spiegel.col.group": "Asset group",
    "avm.spiegel.col.costStart": "Cost, opening balance",
    "avm.spiegel.col.additions": "Additions",
    "avm.spiegel.col.disposals": "Disposals",
    "avm.spiegel.col.transfers": "Transfers",
    "avm.spiegel.col.costEnd": "Cost, closing balance",
    "avm.spiegel.col.cumAfaStart": "Accumulated depreciation, opening",
    "avm.spiegel.col.afa": "Depreciation for the year",
    "avm.spiegel.col.writeUps": "Write-ups",
    "avm.spiegel.col.cumAfaDisposals": "Accumulated depreciation, disposals",
    "avm.spiegel.col.cumAfaTransfers": "Accumulated depreciation, transfers",
    "avm.spiegel.col.cumAfaEnd": "Accumulated depreciation, closing",
    "avm.spiegel.col.bookValueEnd": "Book value end",
    "avm.spiegel.col.bookValuePrevEnd": "Book value, prior year end",
    "avm.spiegel.total": "Total",
    "avm.spiegel.empty": "No assets exist for this year",

    "avm.forecast.title": "Depreciation forecast",
    "avm.forecast.window": "Period",
    "avm.forecast.window.years": "{count} years",
    "avm.forecast.col.name": "Description",
    "avm.forecast.col.total": "Total",
    "avm.forecast.pool": "Pooled item {year}",
    "avm.forecast.empty": "No planned depreciation in the selected period",

    "avm.pool.settings.noGroup":
        "The pooled-item policy is active, but no asset group owns a pooled-item account. Create the default groups or add a group of the kind pooled item.",
    "avm.pool.settings.hint":
        "Pooled items are written off evenly over {years} years, regardless of the acquisition month.",
    "avm.pool.asset.noPlan":
        "Pooled items have no depreciation plan of their own. The carrying amount is shown on the pooled-item row.",

    "avm.inventory.title": "Inventory list",
    "avm.inventory.filter.applied": "filtered",
    "avm.inventory.filter.location": "Location",
    "avm.inventory.filter.status": "Status",
    "avm.inventory.status.active": "Active",
    "avm.inventory.status.writtenOff": "Written off",
    "avm.inventory.status.disposed": "Disposed",
    "avm.inventory.export.xlsx": "Excel",
    "avm.inventory.export.sheet": "Stocktaking sheet",
    "avm.inventory.sheet.counted": "Counted",
    "avm.inventory.sheet.note": "Note",
    "avm.inventory.empty": "No assets on the register",

    "avm.batches.title": "Posting Batches",
    "avm.batches.tab.afa": "Depreciation Batch",
    "avm.batches.tab.disposal": "Disposal Batch",
    "avm.batches.kpi.total": "Total",
    "avm.batches.kpi.lines": "Lines",
    "avm.batches.status.none": "No batch",
    "avm.batches.status.ready": "Ready",
    "avm.batches.status.transferred": "Transferred",
    "avm.batches.create": "Create batch",
    "avm.batches.createDifference": "Create difference batch",
    "avm.batches.createDifference.hint": "Posts only the change.",
    "avm.batches.createReplacement": "Reversal and new batch",
    "avm.batches.createReplacement.hint": "Reverses the transferred batch and posts the current state again.",
    "avm.batches.transfer": "Transfer batch",
    "avm.batches.transfer.confirm": "Transfer {count} lines totalling {total} as drafts to Freie Erfassung?",
    "avm.batches.transfer.lockYear":
        "The transfer closes the fiscal year {year}. Movements and value changes in {year} are locked afterwards.",
    "avm.batches.transfer.closedYear": "The fiscal year {year} is closed.",
    "avm.batches.transfer.remaining": "The fiscal year {year} closes once the {batch} is transferred as well.",
    "avm.batches.transfer.stillOpen":
        "The fiscal year {year} is not closed yet. Please close it in the year closing below.",
    "avm.batches.transfer.stale":
        "{count, plural, one {The {batches} {year} was} other {The {batches} {year} were}} transferred before a later change; the difference has not reached accounting. The fiscal year stays open. Please choose “Create difference batch” or “Reversal and new batch” and transfer it; the fiscal year then closes.",
    "avm.batches.transfer.stale.join": " and the ",
    "avm.batches.transfer.alsoLocks":
        "{count, plural, one {This also locks the fiscal year {last}.} =2 {This also locks the fiscal years {first} and {last}.} other {This also locks the fiscal years {first} to {last}.}}",
    "avm.batches.delete": "Delete batch",
    "avm.batches.recreate": "Delete and recreate",
    "avm.batches.csv": "As CSV",
    "avm.batches.stale": "The stored batch differs from the current calculation",
    "avm.batches.staleTransferred":
        "The register has changed since this batch was posted. This shows the lines that were actually posted, not the current calculation. Please choose “Create difference batch” or “Reversal and new batch” and transfer it.",
    "avm.batches.difference.empty":
        "The posted lines match the current calculation; there is no difference to post. The difference batch without lines records the current state of the register.",
    "avm.batches.empty": "No postings arise for this year",
    "avm.batches.goToFe": "Open drafts in Freie Erfassung",
    "avm.batches.goToFeHint": "Select the Draft filter there to confirm the transferred postings.",
    "avm.batches.goToFeNoAccess": "The posted drafts are in Freie Erfassung; you do not have read access there.",
    "avm.batches.transfer.blocked.fe": "This requires write access to Freie Erfassung",
    "avm.batches.notice.transfer":
        "The fixed asset register creates no posting for reclassifications. Book the account change in Freie Erfassung if needed.",
    "avm.batches.notice.disposalGroup":
        "The disposal is posted to the group as of the disposal date, while the register lists the asset under its year-end group.",
    "avm.batches.notice.poolExit":
        "The pooled asset leaves the register; the asset movement schedule shows a disposal at acquisition cost, but no posting is created for it.",
    "avm.batches.notice.missingKonto": "This line has no account. Complete the asset group before creating the batch.",
    "avm.batches.missingKonten.title": "Asset groups have no accounts yet",
    "avm.batches.missingKonten.body":
        "The transfer to accounting is only possible once these asset groups have the accounts of their posting lines. Clicking an asset group opens its posting accounts.",
    "avm.batches.chartMismatch.title": "Posting accounts of another chart of accounts",
    "avm.batches.chartMismatch.body":
        "Accounting {year} uses SKR 0{skr}. The posting accounts of these asset groups belong to another chart of accounts; please choose SKR 0{skr} in their posting accounts and enter the accounts of that chart:",
    "avm.batches.accounts.none": "For the transfer to accounting, every asset group needs posting accounts.",
    "avm.batches.accounts.noChart":
        "Accounting has no SKR 03 or SKR 04 chart. Please enter the posting accounts for each asset group by hand.",
    "avm.batches.accounts.seed": "Create posting accounts",
    "avm.batches.accounts.fillable":
        "Some default groups do not have all posting accounts of their chart of accounts yet. Completing them leaves entered accounts unchanged.",
    "avm.batches.accounts.fill": "Complete posting accounts",
    "avm.batches.accounts.unconfigured.title": "Asset groups without posting accounts",
    "avm.batches.accounts.unconfigured.body":
        "There are no default accounts for these asset groups. A group without a posting line in the batch does not hold up the transfer.",
    "avm.batches.accounts.unconfigured": "not configured",
    "avm.batches.fiscalYearMismatch.title": "Fiscal year starts differ",
    "avm.batches.fiscalYearMismatch.body":
        "Fiscal year {year} starts on {avmStart} in the fixed asset register and on {accountingStart} in accounting. The transfer is still possible.",
    "avm.batches.fiscalYearMismatch.consequences":
        "<li>The postings carry the date {avmEnd} from the register's fiscal year and therefore land in accounting year {recordYear}.</li><li>The posting period is set to month {postingMonth}, so monthly evaluations show the whole year's depreciation in that month.</li><li>The period accounting keeps as fiscal year {year} therefore does not receive the depreciation of exactly that period, and every further year shifts the same way.</li>",
    "avm.batches.fiscalYearMismatch.culprit":
        "Check accounting first: the wrong start is more likely to sit there, because a wrong fiscal year in the fixed asset register would have been wrong from its very first posting.",
    "avm.batches.col.date": "Date",
    "avm.batches.col.soll": "Debit",
    "avm.batches.col.haben": "Credit",
    "avm.batches.col.text": "Text",
    "avm.batches.col.amount": "Amount",

    "avm.closing.title": "Year-end closing",
    "avm.closing.close": "Close year",
    "avm.closing.reopen": "Reopen year",
    "avm.closing.closed": "Closed on {date}",
    "avm.closing.reopened": "Reopened on {date}",
    "avm.closing.totalAfa": "Total depreciation",
    "avm.closing.totalBookValue": "Book value at year end",
    "avm.closing.blocked.batch": "Not every posting batch of the year has been transferred yet",
    "avm.closing.blocked.earlier": "The closing for {year} is missing",
    "avm.closing.blocked.locked":
        "The fiscal year {year} is before the closed fiscal year {latest}. Please reopen {latest} first.",
    "avm.closing.blocked.empty": "The closing for {year} contains no assets",
    "avm.closing.blocked.role": 'This requires the "Approve fixed asset register" permission',
    "avm.closing.blocked.write": "This requires write access to the fixed asset register",
    "avm.closing.confirm.close": "Close {count} assets with {total} depreciation?",
    "avm.closing.confirm.stale": "Only close if the difference has been booked by hand in accounting.",
    "avm.closing.confirm.reopen":
        "The closing stays on record as evidence. The transferred postings stay in accounting; a correction is posted there. Reopen the year?",
    "avm.closing.drift": "Deviation from the {year} year-end closing",
    "avm.closing.drift.field": "Field",
    "avm.closing.drift.stored": "Stored",
    "avm.closing.drift.computed": "Computed",
    "avm.closing.drift.onlyComputed": "Added since the closing",
    "avm.closing.drift.onlyStored": "Removed since the closing",

    "avm.restricted.title": "No access",
    "avm.restricted.description": "You do not have read access to the fixed asset register.",

    "avm.error.periods": "This company's fiscal years are inconsistent: {message}",
    "avm.error.outsideRange":
        "{count, plural, one {# asset lies} other {# assets lie}} outside the calculated fiscal years and will be carried without depreciation.",
    "avm.error.planFailed":
        "{count, plural, one {No depreciation plan could be calculated for # asset. It is missing} other {No depreciation plan could be calculated for # assets. They are missing}} from the asset movement schedule, the residual value list, the inventory list, the AfA batch and the year closing. Creating a batch and closing the year stay blocked until this is fixed.",
    "avm.error.planFailed.action":
        "Open the asset named below and change its depreciation method in the master data, for example to linear. If the asset lies in a fiscal year that is already closed, reopen that fiscal year first.",
    "avm.error.writeUpAboveCap":
        "The write-up of {date} for {amount} is above its cap of {cap}. Cancel the write-up (storno) and book at most {cap} again; if it lies in a closed fiscal year, reopen that year first.",
    "avm.error.writeUpUnimpairedMissing":
        "The write-up of {date} is capped by the book value without impairment, since the building was taken over with its own depreciation base. Enter the book value without impairment in the master data.",
    "avm.fiscalYear.action": "Go to the AVM settings",
    "avm.yearSelect.label": "Fiscal year",
    "avm.error.noTaxParameters": "No tax parameters are configured for this fiscal year.",
    "avm.error.noGroups":
        "No asset groups exist for this company yet, and an asset cannot be recorded without one. Asset groups are created once in the settings, where the create standard groups action adds them together with the two accounts for disposal gain and disposal loss matching the chart of accounts.",
    "avm.error.noGroups.action": "Open settings",
    "avm.error.loadFailed": "The fixed asset register data could not be loaded.",
    "avm.loading": "Loading...",

    "avm.classify.expense": "Immediately deductible as an expense",
    "avm.classify.gwg": "Low-value asset",
    "avm.classify.pool": "Pool",
    "avm.classify.linear": "Straight-line depreciation",

    "avm.wizard.eyebrow": "Guided entry",
    "avm.wizard.title": "New asset",
    "avm.wizard.lead":
        "Four steps. Useful life, depreciation method and account are proposed by the system, every proposal can be overridden.",
    "avm.wizard.step1": "What was acquired?",
    "avm.wizard.step2": "Cost and date",
    "avm.wizard.step3": "Depreciation",
    "avm.wizard.step4": "Check and save",
    "avm.wizard.save": "Save asset",
    "avm.wizard.field.name": "Description",
    "avm.wizard.field.supplier": "Supplier / origin",
    "avm.wizard.field.group": "Asset group",
    "avm.wizard.field.groupHint": "determines the account and the proposed useful life",
    "avm.wizard.field.location": "Location",
    "avm.wizard.field.costCenter": "Cost centre",
    "avm.wizard.field.costCenterNone": "no cost centre",
    "avm.wizard.field.quantity": "Quantity",
    "avm.wizard.field.quantityHint":
        "2 or more units form a multi-unit asset, partial disposals are written off proportionally",
    "avm.wizard.field.usedBy": "Used by / responsible",
    "avm.wizard.field.amountNet": "Acquisition cost net",
    "avm.wizard.field.amountGross": "Acquisition cost gross (invoice amount)",
    "avm.wizard.field.amountPartial": "Capitalised amount (net plus non-deductible VAT)",
    "avm.wizard.field.incidental": "Incidental acquisition costs",
    "avm.wizard.field.incidentalHint": "freight, installation, assembly",
    "avm.wizard.field.date": "Acquisition date",
    "avm.wizard.field.dateHint": "the date the asset was ready for use",
    "avm.wizard.field.vat": "Input VAT",
    "avm.wizard.field.vatHint": "default from the settings",
    "avm.wizard.vat.net": "fully deductible, capitalise net",
    "avm.wizard.vat.gross": "not deductible, capitalise gross",
    "avm.wizard.vat.partial": "partly deductible, enter the share",
    "avm.wizard.vat.kust": "no VAT shown (§ 19 UStG), invoice amount",
    "avm.wizard.field.record": "Record from accounting",
    "avm.wizard.record.none": "Link a record later",
    "avm.wizard.classification.expense":
        "Up to {limit} net: immediate expense. No entry in the asset register is needed.",
    "avm.wizard.classification.recordAnyway": "Record as an asset anyway",
    "avm.wizard.classification.gwg":
        "Low-value asset. Up to {limit} net fully depreciated in the year of acquisition, the asset stays in the register.",
    "avm.wizard.classification.pool": "Pool asset. Between {min} and {max} net, released over {years} years.",
    "avm.wizard.classification.linear":
        "Capitalised and depreciated over the useful life. Above {limit} net: scheduled depreciation pro rata from the acquisition month.",
    "avm.wizard.method.linear": "Straight-line depreciation",
    "avm.wizard.method.linearDesc": "evenly over the useful life, pro rata in the first year",
    "avm.wizard.method.gwg": "Low-value asset, immediate write-off",
    "avm.wizard.method.gwgDesc": "fully depreciated in the year of acquisition",
    "avm.wizard.method.pool": "Pool (Sammelposten)",
    "avm.wizard.method.degressive": "Declining-balance depreciation",
    "avm.wizard.method.poolDesc": "released over {years} years regardless of individual disposals",
    "avm.wizard.method.degressiveDesc":
        "three times the straight-line rate, capped at 30 percent, only for acquisitions inside the statutory window",
    "avm.wizard.field.usefulLifeYears": "Useful life (years)",
    "avm.wizard.field.usefulLifeMonths": "Useful life (months)",
    "avm.wizard.field.usefulLifeHint": "proposal from the asset group",
    "avm.wizard.field.usefulLifeHintBuilding": "follows from use and date (Section 7 (4) EStG)",
    "avm.wizard.field.usefulLifeHintLease": "follows from the end of the lease",
    "avm.wizard.field.usefulLifeHintBuildingGroup":
        "follows from use and date (Section 7 (4) EStG) or from the end of the lease",
    "avm.wizard.special.label": "Special depreciation under Section 7g (5) EStG",
    "avm.wizard.special.description":
        "{amount} booked in full in the year of acquisition, in addition to the regular depreciation, at most {rate} percent of the base; available within the first {years} years from acquisition.",
    "avm.wizard.special.windowUnavailable":
        "No special depreciation under Section 7g (5) EStG is possible for this acquisition date.",
    "avm.wizard.iab.label": "Assign an investment deduction allowance",
    "avm.wizard.iab.empty": "No unassigned investment deduction allowance available.",
    "avm.memoValue.label": "Residual book value at the end of the useful life",
    "avm.memoValue.one": "1.00 (memo value)",
    "avm.memoValue.zero": "0.00",
    "avm.memoValue.wizardHint":
        "Preselected from the company settings; the default can be changed there and applies only to new assets.",
    "avm.memoValue.assetHint":
        "Applies to this asset only. Changing the default in the settings does not change this value.",
    "avm.roundingMode.label": "Rounding of the depreciation",
    "avm.roundingMode.wizardHint":
        "The rounding is preset from the company settings; the preset can be changed there and applies to new assets only.",
    "avm.roundingMode.assetHint":
        "Applies to this asset only. Changing the preset in the settings does not change this rounding.",
    "avm.oneYear.hardwareQuestion": "Is this computer hardware or a peripheral (BMF letter of 22.02.2022)?",
    "avm.oneYear.hardware.yes": "Yes",
    "avm.oneYear.hardware.no": "No",
    "avm.oneYear.hardwareHint":
        "Computers, notebooks, tablets, thin clients, workstations, docking stations, small servers, external power supplies and peripherals.",
    "avm.oneYear.label": "Useful life under the BMF letter of 22.02.2022",
    "avm.oneYear.term.one": "1 year",
    "avm.oneYear.term.oneDesc": "Full depreciation in the fiscal year of acquisition, whatever the month",
    "avm.oneYear.term.three": "3 years",
    "avm.oneYear.term.threeDesc": "36 months, pro rata by month from the month of acquisition",
    "avm.oneYear.beforeCutoff":
        '"1 year" applies only from fiscal years ending after 31.12.2020 (BMF letter of 22.02.2022); this acquisition is written off over 3 years.',
    "avm.import.row.error.fullYearAfaCutoff":
        "1 year applies only from fiscal years ending after 31.12.2020; please choose 3 years",
    "avm.oneYear.saasHint": "SaaS and cloud subscriptions are not assets and do not belong in the asset register",
    "avm.businessUse90.label":
        "Leased, or used for business at least 90 % in a domestic permanent establishment, in the fiscal year of the investment and the following one",
    "avm.businessUse90.tooltip":
        "Special depreciation (Section 7g (6) no. 2 EStG) and the investment deduction (Section 7g (1) sentence 1 EStG) require the asset to be leased, or used exclusively or almost exclusively, that is at least 90 %, for business in a domestic permanent establishment, in the fiscal year of the investment and the following fiscal year. A car whose private use is taxed under the one-percent rule usually does not meet this.",
    "avm.businessUse90.tooltipLabel": "About the use in the fiscal year of the investment and the following one",
    "avm.businessUse90.rests.special": "A booked special depreciation rests on this confirmation.",
    "avm.businessUse90.rests.iab": "An assigned investment deduction rests on this confirmation.",
    "avm.businessUse90.rests.both":
        "A booked special depreciation and an assigned investment deduction rest on this confirmation.",
    "avm.businessUse90.remove.open": "To remove it, first {step}.",
    "avm.businessUse90.remove.closed": "Fiscal year {year} is closed; to remove it, first reopen it and then {step}.",
    "avm.businessUse90.remove.closedBefore":
        "Fiscal year {year} lies before the closed fiscal year {latest}; to remove it, first reopen the closings from {latest} back to {year} and then {step}.",
    "avm.businessUse90.step.special": "cancel the special depreciation",
    "avm.businessUse90.step.iab": "unassign the investment deduction",
    "avm.businessUse90.step.both": "cancel the special depreciation and unassign the investment deduction",
    "avm.import.field.businessUse90Confirmed": "Leased or at least 90 % business use",
    "avm.businessUse90.hint": "Required for special depreciation and the investment deduction under Section 7g EStG",
    "avm.wizard.error.businessUse90":
        "Please confirm the business use of at least 90 % or do without special depreciation and the investment deduction",
    "avm.movements.error.businessUse90":
        "Special depreciation requires business use of at least 90 % (Section 7g (6) no. 2 EStG). Please confirm it in the master data first or choose another movement type.",
    "avm.iab.form.assetBusinessUse90": "Business use of at least 90 % is not confirmed in the master data",
    "avm.assets.method.fullYear": "1 year (full depreciation in the year of acquisition)",
    "avm.groups.oneYearChoice": "Choice 1 year / 3 years",
    "avm.groups.oneYearChoice.none": "Do not offer",
    "avm.groups.oneYearChoice.always": "Always offer (software)",
    "avm.groups.oneYearChoice.askHardware": "After the hardware question (computer hardware)",
    "avm.groups.oneYearChoice.hint":
        "Under the BMF letter of 22.02.2022, software and computer hardware may be written off in full in the year of acquisition (1 year) or over 3 years.",
    "avm.groups.oneYearChoice.kind": "Only for groups of movable or intangible assets",
    "avm.rightKind.label": "Type of right",
    "avm.rightKind.patent": "Patent",
    "avm.rightKind.trademark": "Trademark",
    "avm.rightKind.other": "Other right",
    "avm.rightKind.filingDate": "Filing date",
    "avm.rightKind.hint.patent":
        "A patent runs 20 years from its filing date (Section 16 PatG). The useful life is prefilled with the remaining term from the acquisition and may not exceed it.",
    "avm.rightKind.hint.trademark":
        "An acquired trademark is prefilled with 180 months (BMF letter of 12.07.1999); a shorter useful life is possible.",
    "avm.rightKind.hint.other": "Other rights have no default. Please enter the useful life from the contract term.",
    "avm.rightKind.error.required": "Please choose the type of right: patent, trademark or other right.",
    "avm.rightKind.error.filingDate":
        "For a patent the longest useful life follows from the filing date (Section 16 PatG). Please enter the filing date or choose another type of right.",
    "avm.rightKind.error.expired":
        "The patent had already expired at the acquisition; please record it as another right or check the filing date",
    "avm.rightKind.error.lifeAbovePatent":
        "The patent runs 20 years from its filing date (Section 16 PatG); from the acquisition that is at most {max} months. Please enter at most {max} months or check the filing date.",
    "avm.rightKind.error.remainingAbovePatent":
        "The patent runs 20 years from its filing date (Section 16 PatG); from {start} that is at most {max} months. Please enter at most {max} months or check the filing date.",
    "avm.rightKind.error.movementExpired":
        "The patent had already expired on {start}. Please leave the remaining life empty or check the filing date.",
    "avm.rightKind.error.openingExpired":
        "The patent had already expired on {start}. Please record the asset as another right or check the filing date.",
    "avm.groups.asksRightKind": "Ask for the type of right",
    "avm.groups.asksRightKind.hint":
        "Every asset in this group chooses patent, trademark or other right; the type prefills the useful life.",
    "avm.groups.asksRightKind.kind":
        'The setting "Ask for the type of right" exists only for groups of intangible assets. Please switch it off.',
    "avm.import.field.fullYearAfa": "1 year (full depreciation)",
    "avm.import.field.hardwareBmf": "Computer hardware (BMF)",
    "avm.import.field.rightKind": "Type of right (Patent, Marke, Sonstiges Recht)",
    "avm.import.field.patentFilingDate": "Filing date (patent)",
    "avm.import.row.error.flag": "Yes/no value not readable (ja, nein, x or empty)",
    "avm.import.row.error.fullYearAfa":
        "This group does not offer 1 year (software or confirmed computer hardware only)",
    "avm.import.row.error.fullYearAfaTerms": "1 year requires straight-line depreciation over 12 months",
    "avm.import.row.error.fullYearAfaPool":
        "1 year is not possible: the cost falls into the Sammelposten the company's pool election prescribes",
    "avm.import.row.error.hardwareBmf": "The hardware answer exists only in a group that asks the hardware question",
    "avm.import.row.error.rightKind":
        "Type of right not readable or not asked in this group; please use Patent, Marke or Sonstiges Recht in a group that asks for it",
    "avm.import.row.error.patentFilingDate":
        "Patent without a readable filing date; please enter the filing date or choose another type of right",
    "avm.import.row.error.patentExpired":
        "The patent had already expired at the acquisition; please record it as another right or check the filing date",
    "avm.import.row.error.patentLife":
        "Useful life longer than the patent's remaining term (20 years from filing, Section 16 PatG); please shorten it or check the filing date",
    "avm.wizard.preview.title": "Preview",
    "avm.wizard.preview.summary": "{years} years, first-year depreciation {afa}",
    "avm.wizard.preview.col.year": "Year",
    "avm.wizard.preview.col.months": "Months",
    "avm.wizard.preview.col.afa": "Depreciation",
    "avm.wizard.preview.col.bookValueEnd": "Book value at end",
    "avm.wizard.preview.none":
        "No preview can be computed for these values. A Sammelposten is written off as a group, and an acquisition date outside the loaded fiscal years yields no plan.",
    "avm.wizard.preview.buildingMissing": "For the preview, please enter the use and date (or the lease end).",
    "avm.wizard.review.title": "This will be saved",
    "avm.wizard.review.inventoryNum": "Inventory number",
    "avm.wizard.review.inventoryAuto": "assigned automatically",
    "avm.wizard.review.konto": "Account",
    "avm.wizard.review.method": "Method",
    "avm.wizard.review.afaFirstYear": "Depreciation {year}",
    "avm.wizard.review.postingTitle": "Posting of the addition",
    "avm.wizard.review.postingNote":
        "The purchase invoice already posts the asset account. The register creates no addition posting, the linked record documents the addition.",
    "avm.wizard.review.linkedRecord": "Linked record",
    "avm.wizard.review.noRecord": "no record linked",
    "avm.wizard.review.specialDepreciation": "Special depreciation Section 7g (5)",
    "avm.wizard.review.businessUse90": "Leased or at least 90 % business use",
    "avm.wizard.review.confirmed": "confirmed",
    "avm.wizard.review.notConfirmed": "not confirmed",
    "avm.wizard.review.iabAssigned": "Investment allowance assigned",
    "avm.wizard.error.nameRequired": "Description required",
    "avm.wizard.error.groupRequired": "Asset group required",
    "avm.wizard.error.quantity": "Quantity must be at least 1",
    "avm.wizard.error.dateRequired": "Acquisition date required",
    "avm.wizard.error.amountRequired": "Acquisition cost required",
    "avm.wizard.error.recordAnyway": "Please confirm that the item should be recorded anyway",
    "avm.wizard.error.usefulLife": "Useful life required",
    "avm.wizard.error.method": "The chosen method is not allowed for this amount",
    "avm.wizard.error.specialMethod": "Special depreciation is only possible with straight-line depreciation",
    "avm.wizard.error.acquisitionDateTooEarly":
        "The acquisition date is before the earliest supported tax year {year}. Please choose a date on or after {year}-01-01.",
    "avm.noAccounting.proposals":
        "Entry proposals come from accounting and are available only with access to an accounting product.",
    "avm.noAccounting.recordDocuments":
        "Receipts from the linked booking are available only with access to an accounting product.",
    "avm.noAccounting.costCenter":
        "Cost centres come from accounting and are available only with access to an accounting product.",
    "avm.noAccounting.record":
        "Linking a record from accounting is available only with access to an accounting product.",
    "avm.noAccounting.batches":
        "The posting batch hands the depreciation over to accounting and is available only with access to an accounting product.",
    "avm.proposals.title": "Open proposals from accounting",
    "avm.proposals.empty": "No open proposals",
    "avm.proposals.loading": "Loading proposals",
    "avm.proposals.take": "take over",
    "avm.proposals.dismiss": "hide",
    "avm.proposals.net": "net {amount}",
    "avm.proposals.reason.noAsset": "Account {konto}, no asset recorded",
    "avm.proposals.reason.gwg": "Account {konto}, below {limit}, proposed as low-value asset",
    "avm.proposals.reason.expense": "Account {konto}, below {limit}, immediate expense possible",
    "avm.import.eyebrow": "Initial setup",
    "avm.import.title": "Import and legacy assets",
    "avm.import.lead":
        "Existing asset registers are read from an Excel or CSV file. Columns are detected and can be mapped, and the system checks plausibility and totals before the import.",
    "avm.import.drop": "Drop a file here or click (.xlsx, .xls, .csv)",
    "avm.import.dropActive": "Release the file",
    "avm.import.rejected": "This file type is not supported. Allowed formats are .xlsx, .xls and .csv.",
    "avm.import.file": "{fileName}, {count} rows",
    "avm.import.clear": "Choose another file",
    "avm.import.parseError": "The file could not be read",
    "avm.import.headerRow.label": "Header row",
    "avm.import.headerRow.hint":
        "Row detected as the header. If the column names below look wrong, choose the correct row.",
    "avm.import.headerRow.option": "Row {row}: {preview}",
    "avm.import.headerRow.emptyRow": "(empty row)",
    "avm.import.mapping.title": "Column mapping",
    "avm.import.mapping.col.column": "Column in the file",
    "avm.import.mapping.col.sample": "Sample value",
    "avm.import.mapping.col.field": "Field in the register",
    "avm.import.mapping.col.status": "Status",
    "avm.import.mapping.status.recognized": "recognised",
    "avm.import.mapping.status.optional": "optional",
    "avm.import.mapping.status.required": "required",
    "avm.import.mapping.ignore": "do not import",
    "avm.import.mapping.missing": "Required field not mapped: {field}",
    "avm.import.mapping.missingGroup": "The asset group column must be mapped",
    "avm.import.mapping.duplicateColumn": "Mapped to the same column as another field",
    "avm.import.field.inventoryNum": "Inventory number",
    "avm.import.field.name": "Item",
    "avm.import.field.group": "Asset group",
    "avm.import.field.acquisitionDate": "Acquisition date",
    "avm.import.field.acquisitionCost": "Acquisition cost",
    "avm.import.field.usefulLifeYears": "Useful life (years)",
    "avm.import.field.usefulLifeMonths": "Useful life (months)",
    "avm.import.field.openingBookValue": "Book value previous year",
    "avm.import.field.remainingMonths": "Remaining life (months)",
    "avm.import.field.supplier": "Supplier",
    "avm.import.field.location": "Location",
    "avm.import.field.costCenter": "Cost centre",
    "avm.import.field.quantity": "Quantity",
    "avm.import.field.usedBy": "Used by",
    "avm.import.field.note": "Note",
    "avm.import.field.method": "Depreciation method",
    "avm.import.field.memoValue": "Memo value",
    "avm.import.field.roundingMode": "Rounding",
    "avm.import.checks.title": "Checks before the import",
    "avm.import.checks.ok": "{count} rows without errors.",
    "avm.import.checks.needsLife": "{count} rows without a useful life. Proposal from the asset group, please confirm.",
    "avm.import.checks.acceptLife": "Accept the useful life proposals",
    "avm.import.checks.errors": "{count} rows with errors will not be imported.",
    "avm.import.checks.expectedTotal": "Expected total of remaining book values",
    "avm.import.checks.computedTotal": "Total from the file: {amount}",
    "avm.import.checks.totalMatches": "matches the previous year's closing",
    "avm.import.checks.totalDiffers": "differs from the previous year's closing by {delta}",
    "avm.import.checks.openingYear": "Opening balance at the start of fiscal year {year}",
    "avm.import.submit": "Import {count} rows",
    "avm.import.rows.title": "Rows",
    "avm.import.rows.col.row": "Row",
    "avm.import.rows.col.status": "Status",
    "avm.import.rows.col.problems": "Notes",
    "avm.import.rows.status.ok": "ok",
    "avm.import.rows.status.needsLife": "confirm useful life",
    "avm.import.rows.status.error": "error",
    "avm.import.row.error.name": "Description missing",
    "avm.import.row.error.group":
        'Asset group "{value}" not found. Please give the name of an existing asset group: {groups}',
    "avm.import.row.error.groupAmbiguous":
        'The asset account "{value}" belongs to several asset groups: {candidates}. Please give the name of the asset group.',
    "avm.import.row.error.date": "Acquisition date invalid",
    "avm.import.row.error.cost": "Acquisition cost invalid",
    "avm.import.row.error.bookValue": "Book value invalid",
    "avm.import.row.error.bookValueAboveCost": "Book value above the acquisition cost",
    "avm.import.row.error.usefulLife": "Useful life missing and the group has no default",
    "avm.import.row.error.costCenter": "Cost centre longer than 8 characters",
    "avm.import.row.error.quantity": "Quantity invalid",
    "avm.import.row.error.memoValue":
        "Invalid memo value: allowed are 1, 1.00, 0 or 0.00; empty takes the default from the settings",
    "avm.import.row.error.roundingMode":
        "Invalid rounding: allowed are Euro (round down to full euro) or Cent (to the cent); empty takes the preset from the settings",
    "avm.import.row.error.inventoryNum": "Inventory number invalid",
    "avm.import.row.error.degressiveWindow": "Declining-balance depreciation outside the statutory window",
    "avm.import.row.error.methodKind":
        "Depreciation method not allowed in this asset group. Please give an allowed method in the file or map the row to another asset group.",
    "avm.import.row.error.poolGroup":
        "Pool item without a pool asset group. Please create the pool group in the settings and read the file again.",
    "avm.import.row.note.poolGroup":
        "Moved into the pool group {name}: the pool election covers every asset in this cost band.",
    "avm.import.row.note.linearForKind":
        "Straight-line depreciation used because the asset group does not allow the derived method.",
    "avm.import.row.error.acquisitionDateTooEarly": "Acquisition date before the earliest tax year {year}",
    "avm.import.ways.title": "Ways into the register",
    "avm.import.ways.excel": "Excel import",
    "avm.import.ways.excelDesc": "Legacy assets with remaining book value and remaining life at the conversion date.",
    "avm.import.ways.wizard": "Guided entry",
    "avm.import.ways.wizardDesc": "Individual additions in four guided steps.",
    "avm.import.ways.proposals": "Proposal from accounting",
    "avm.import.ways.proposalsDesc": "Postings to asset accounts without an asset are proposed as additions.",
    "avm.building.title": "Building details",
    "avm.building.titleOptional": "Building details for the completion",
    "avm.building.optionalHint": "Needed only if the asset is transferred into a building group when it is completed.",
    "avm.building.basis": "Basis of the useful life",
    "avm.building.basis.building": "Building (§ 7 (4) EStG)",
    "avm.building.basis.lease": "Immovable leasehold improvement (remaining lease term)",
    "avm.building.type.building": "Building depreciation under Section 7 (4) EStG",
    "avm.building.type.buildingDesc": "useful life from use, building application and completion",
    "avm.building.type.lease": "Immovable leasehold improvement (remaining lease term)",
    "avm.building.type.leaseDesc": "useful life up to the end of the lease, at most 400 months",
    "avm.building.use": "Use of the building",
    "avm.building.use.nonResidentialBusiness": "Business building held as business property, not residential",
    "avm.building.use.other": "Other building, for example residential",
    "avm.building.applicationDate": "Building application filed on",
    "avm.building.completionDate": "Completed on",
    "avm.building.leaseEnd": "Lease ends on",
    "avm.building.limit.building":
        "Statutory useful life: {months} months. A shorter actual useful life is allowed with evidence.",
    "avm.building.limit.lease":
        "Remaining lease term: {months} months (at most 400). A shorter useful life is allowed.",
    "avm.building.error.missingUse":
        "Please enter the use of the building with its application and completion dates, or the end of the lease.",
    "avm.building.error.missingApplicationDate":
        "The rate for a business building depends on the date of the building application (§ 7 (4) sentence 1 no. 1 EStG). Please enter the application date.",
    "avm.building.error.missingCompletionDate":
        "The rate for the building depends on the year of completion (§ 7 (4) sentence 1 no. 2 EStG). Please enter the completion date.",
    "avm.building.error.missingLeaseEnd": "Please enter the end of the lease.",
    "avm.building.error.leaseBeforeStart": "The lease ends before depreciation starts. Please check the lease end.",
    "avm.building.error.completionLifeTooLong":
        "The completion carries a useful life of {life} months; these building details allow at most {max} months. Please check the building details or cancel the transfer and book it again with at most {max} months.",
    "avm.building.error.completionDateDiffers":
        "The building was completed by the transfer of {date}; that transfer is the completion date. Please set the completion date to {date} or leave it empty.",
    "avm.building.split.landFailed":
        "The building is saved, the land share of {amount} is not ({reason}). Please record it as its own asset in group {name}.",
    "avm.building.error.completionDateDiffersTransfer":
        "This transfer completes the building on {date}; the transfer is the completion date. The completion date in the building details differs. Please set it to {date} in the master data or leave it empty.",
    "avm.movements.form.targetGroup.buildingCompletionDate":
        "Completion date differs from the transfer: adjust it in the master data",
    "avm.building.error.flipClosed":
        "This change switches the building between the statutory rate (§ 7 (4) sentence 1 EStG) and spreading over the remaining life; it would change the amounts from the movement of {date} in the closed fiscal year {year}. Please reopen {latest} and first cancel the movement of {date}.",
    "avm.building.error.flipRemaining":
        "This change switches the building between the statutory rate (§ 7 (4) sentence 1 EStG) and spreading over the remaining life; the movement of {date} carries a remaining life of {months} months that would no longer apply. Please first cancel the movement of {date}.",
    "avm.movements.storno.lifeRests":
        "The remaining life of the movement of {date} rests on this completion. Please first cancel the movement of {date}.",
    "avm.building.error.quantityOneDisposed":
        "A building is kept as one asset with quantity 1; a part sale goes by floor area and is booked by hand in accounting. A partial disposal is already booked, so the quantity can no longer fall to 1. Please choose another asset group.",
    "avm.assets.quantity.error.belowDisposed":
        "The quantity cannot fall to {quantity}: the partial {count, plural, one {disposal} other {disposals}} of {dates} already took {units} {units, plural, one {unit} other {units}}. Please enter a quantity of at least {min} or cancel the partial {count, plural, one {disposal} other {disposals}} of {dates} first.",
    "avm.building.error.quantityOne":
        "A building is kept as one asset with quantity 1; a part sale goes by floor-area share and is booked by hand in the accounts. Please enter quantity 1 and record each further building as an asset of its own.",
    "avm.building.error.flipForward":
        "If the asset should sit in the new group only from now on, please leave the asset group unchanged here and book a transfer into the new group instead, dated after the last movement of {date} and in an open fiscal year.",
    "avm.building.error.flipRemainingTransfer":
        "This transfer switches the building between the statutory rate (§ 7 (4) sentence 1 EStG) and spreading over the remaining life; the movement of {date} carries a remaining life of {months} months that would no longer apply. Please date the transfer after {date} or first cancel the movement of {date}.",
    "avm.building.error.flipRemainingStorno":
        "Cancelling this transfer switches the building between the statutory rate (§ 7 (4) sentence 1 EStG) and spreading over the remaining life; the movement of {date} carries a remaining life of {months} months that would no longer apply. Please first cancel the movement of {date}, then the transfer.",
    "avm.movements.form.targetGroup.buildingQuantity":
        "Quantity above 1: a building has quantity 1, adjust it in the master data first",
    "avm.movements.form.targetGroup.buildingQuantityDisposed":
        "Quantity above 1 with a booked partial disposal: a building has quantity 1, choose another target group",
    "avm.movements.error.beforeOpening":
        "The asset was taken over with an opening balance in {openingYear}; a movement before fiscal year {openingYear} is contained in the opening book value. Please choose a date in fiscal year {openingYear} or later.",
    "avm.movements.error.buildingPartialDisposal":
        "A building is kept as one asset with quantity 1; a part sale goes by floor-area share and is booked by hand in the accounts. Please book the disposal of the whole building, or book the part sale by hand.",
    "avm.building.error.lifeTooLong":
        "The useful life is longer than the allowed {max} months. Please enter at most {max} months.",
    "avm.building.error.completionAttributes":
        "Building details are missing for a transfer into a building group. Please first enter the use with its application and completion dates, or the lease end, in the asset's master data.",
    "avm.building.remainingMonths":
        "A building keeps its statutory rate, also after subsequent costs (R 7.4 (9) EStR). No remaining life is set here.",
    "avm.movements.form.targetGroup.buildingAttributes":
        "Building details missing: enter them in the master data first",
    "avm.movements.form.targetGroup.buildingLife": "Useful life longer than § 7 (4) EStG allows",
    "avm.building.split.label": "Split the purchase price between land and building",
    "avm.building.split.hint":
        "The land share is recorded as its own asset without depreciation in the group with the role land. Please record outdoor facilities and operating equipment separately.",
    "avm.building.split.land": "Land share",
    "avm.building.split.building": "Building share",
    "avm.building.split.sum":
        "Land and building add up to {total}. Please split the shares so they add up to exactly {cost}.",
    "avm.building.split.amount": "Please enter both shares above 0.00 or save without a split.",
    "avm.building.split.noLandGroup":
        "There is no group with the role land for the land share of built-on plots. Please create the default groups in the settings, give one of your own non-depreciable groups this role, or save without a split.",
    "avm.building.split.landName": "{name}, land",
    "avm.building.split.review": "Land {land} in group {name}, building {building}",
    "avm.import.field.buildingUse": "Use (business building or other building)",
    "avm.import.field.buildingApplicationDate": "Building application",
    "avm.import.field.completionDate": "Completion",
    "avm.import.field.leaseEnd": "Lease end",
    "avm.import.field.openingAfaBase": "Depreciation base",
    "avm.import.field.openingUnimpairedBookValue": "Book value without impairment",
    "avm.import.field.specialBuildingAfa": "Special or declining-balance building depreciation claimed",
    "avm.import.row.error.buildingUse":
        "Use unreadable: enter exactly „Wirtschaftsgebäude im Betriebsvermögen, nicht zu Wohnzwecken“ or „Anderes Gebäude, zum Beispiel Wohngebäude“ (or NON_RESIDENTIAL_BUSINESS / OTHER)",
    "avm.import.row.error.buildingDate": "Building application, completion or lease end is not a valid date",
    "avm.import.row.error.buildingUseOrLease": "Use and lease end exclude each other: enter only one of them",
    "avm.import.row.error.buildingMissing":
        "Building data missing: a business building needs the building application, another building the completion, an immovable leasehold improvement the lease end",
    "avm.import.row.error.buildingLife":
        "Useful life longer than section 7 (4) EStG allows, or lease end before the acquisition",
    "avm.import.row.error.buildingColumns":
        "Use, building application, completion and lease end belong to a building group: check the asset group or empty the cells",
    "avm.import.row.error.specialBuildingAfa":
        "Special or declining-balance building depreciation before the takeover: please record it in the accounting",
    "avm.import.row.error.openingBase":
        "Depreciation base only for an imported building, between the prior-year book value and the acquisition cost",
    "avm.import.row.error.openingUnimpaired":
        "Book value without impairment only for an imported building, between the prior-year book value and the acquisition cost",
    "avm.import.rows.col.building": "Building",
    "avm.import.rows.building.lease": "Lease end {date}",
    "avm.import.rows.building.base": "Depreciation base {amount}",
    "avm.import.rows.building.unimpaired": "without impairment {amount}",
    "avm.assets.openingAfaBase": "Depreciation base",
    "avm.assets.openingAfaBase.hint":
        "Leave empty when it equals the acquisition cost. After an impairment or special depreciation before the takeover, enter the reduced base.",
    "avm.assets.openingUnimpairedBookValue": "Book value without impairment",
    "avm.assets.openingUnimpairedBookValue.hint":
        "The book value at the start of the opening year without the impairments; it caps a later write-up.",
    "avm.import.field.openingCumulativeAfa": "Cumulative depreciation",
    "avm.import.row.error.cumulativeAfa":
        "Acquisition cost {cost} less cumulative depreciation {cumulative} does not give the prior-year book value {bookValue}: correct the book value or the cumulative depreciation",
    "avm.import.row.error.cumulativeAfaColumn":
        "Cumulative depreciation only as an amount from 0.00 and only on a row with an opening balance: check the amount or empty the cell",
    "app.agent.skippedNoRight": "No permission for {product}, this part is skipped.",
    "app.agent.stepFailed": "The step {step} failed, continuing with the next one",
    "app.agent.skippedDependency": "The step {step} is skipped because an earlier step did not run",
    "app.agent.nothingToDo": "Nothing to do for the step {step}",
    "app.agent.continuesOnServer": "The job continues in the background, you will find the result in Binale",
    "app.agent.step.start": "Start",
    "app.agent.step.upload": "Upload",
    "app.agent.step.aiReading": "AI reading",
    "app.agent.step.aiRetry": "AI re-reading",
    "app.agent.step.autoSort": "Auto-sort",
    "app.agent.step.sortQuestions": "Sort questions",
    "app.agent.step.documentContacts": "Contacts from documents",
    "app.agent.step.kasseFromDocuments": "Cash register from documents",
    "app.agent.step.bankImport": "Bank import",
    "app.agent.step.bankContacts": "Bank contacts",
    "app.agent.step.draftsFromDocuments": "Drafts from documents",
    "app.agent.step.draftsFromTransactions": "Drafts from transactions",
    "app.agent.step.fillBank": "Fill bank",
    "app.agent.step.reconcile": "Reconciliation",
    "app.agent.step.aiCheck": "AI check",
    "app.agent.step.postingRules": "Posting rules",
    "app.agent.step.report": "Report",
    "app.agent.title": "Binale Agent",
    "app.agent.button": "Binale Agent",
    "app.agent.stepOf": "Step {step} of {total}",
    "app.agent.finished": "Done",
    "app.agent.paused": "Paused",
    "app.agent.progress": "{done} of {total} done",
    "app.agent.pause": "Pause",
    "app.agent.resume": "Resume",
    "app.agent.stop": "Stop",
    "app.agent.close": "Close",
    "app.agent.confirmStop.title": "Stop the agent?",
    "app.agent.confirmStop.body": "The running step is finished first, then the agent stops.",
    "app.agent.confirmStop.keep": "Keep running",
    "app.agent.panelPlaceholder": "This view is not available yet.",
    "app.agent.panelSkip": "Skip",
    "app.agent.start.thinking": "Looking at your documents, bank accounts and mailbox",
    "app.agent.start.overview":
        "I see {documents} documents in New documents and {banks} bank accounts with finAPI for {year}.",
    "app.agent.start.open":
        "Still open for {year}: {documents} documents to book, {bank} bank transactions and {kasse} cash transactions without a booking.",
    "app.agent.start.mailboxError":
        "The mailbox reports an error, so new email documents are not arriving. Please check the mailbox connection.",
    "app.agent.start.bankStale": "The bank account {bank} has not been imported for more than 30 days.",
    "app.agent.start.uploadQuestion": "Would you like to upload new documents now?",
    "app.agent.start.upload": "Upload documents",
    "app.agent.start.skipUpload": "Continue without upload",
    "app.agent.upload.done": "{count} documents uploaded",
    "app.agent.aiReading.progress": "The AI is reading your documents",
    "app.agent.aiReading.done": "{done} documents read, {failed} could not be read",
    "app.agent.aiRetry.question": "The AI could not read {count} documents. Should it try again?",
    "app.agent.aiRetry.retry": "Try again",
    "app.agent.aiRetry.skip": "Skip",
    "app.agent.aiRetry.thinking": "Starting the reading again",
    "app.agent.aiRetry.done": "{done} documents read, {failed} still failing",
    "app.agent.autoSort.thinking": "Sorting the documents from New documents",
    "app.agent.autoSort.done": "{moved} of {total} documents sorted",
    "app.agent.report.uploaded": "{count} documents uploaded",
    "app.agent.report.aiRead": "{count} documents read by the AI",
    "app.agent.report.aiRetried": "{count} documents read on the second try",
    "app.agent.report.aiFailed": "{count} documents could not be read",
    "app.agent.report.sorted": "{moved} of {total} documents sorted automatically",
    "app.agent.aiStillRunning": "{count} documents are still being read, the result will be in Binale",
    "app.agent.start.overviewNoDocuments": "I see {banks} bank accounts with finAPI for {year}.",
    "app.agent.report.aiNotStarted": "AI reading was not started for {count} documents",
    "app.agent.sortQuestions.intro": "I need your help with {count} documents",
    "app.agent.sortQuestions.later": "Later",
    "app.agent.sortQuestions.done": "{answered} documents answered, {later} postponed",
    "app.agent.report.sortAnswered": "{count} documents sorted from your answers",
    "app.agent.report.sortLater": "{count} documents are still waiting for your decision",
    "app.agent.sortQuestions.documentMissing": "The document could not be found",
    "app.agent.documentContacts.thinking": "I am assigning your documents to contacts",
    "app.agent.documentContacts.done": "{linked} documents linked to existing contacts, {created} contacts created",
    "app.agent.report.documentContactsLinked": "{count} documents linked to existing contacts",
    "app.agent.report.documentContactsCreated": "{count} contacts created from documents",
    "app.agent.kasse.thinking": "I am booking the cash documents into the cash register",
    "app.agent.kasse.done": "{count} cash register entries created for {register}",
    "app.agent.report.kasseCreated": "{count} cash register entries created from documents",
    "app.agent.report.kasseRefused":
        "{count} cash documents could not be booked, please check them in the cash register",
    "app.agent.bankImport.start": "Importing bank transactions for {bank}. Please start the import with Update.",
    "app.agent.bankImport.done": "{bank}: {count} bank transactions imported",
    "app.agent.bankImport.doneNoCount": "{bank}: bank transactions imported",
    "app.agent.bankImport.skipped": "Bank {bank} skipped",
    "app.agent.bankImport.skipBank": "Skip this bank",
    "app.agent.report.bankImportedNoCount": "{bank}: bank transactions imported",
    "app.agent.report.bankImported": "{bank}: {count} bank transactions imported",
    "app.agent.report.bankSkipped": "{count} bank accounts skipped",
    "app.agent.bankContacts.thinking": "I am assigning the bank transactions to existing contacts",
    "app.agent.bankContacts.matched": "{count} bank transactions linked to contacts",
    "app.agent.bankContacts.suggesting": "I am looking for new contacts in the remaining bank transactions",
    "app.agent.bankContacts.ask": "Create {count} contacts?",
    "app.agent.bankContacts.create": "Create",
    "app.agent.bankContacts.skip": "Skip",
    "app.agent.bankContacts.creating": "I am creating the contacts",
    "app.agent.bankContacts.created": "{count} contacts created",
    "app.agent.bankContacts.noContactsProduct": "The Contacts module is not active for this company.",
    "app.agent.report.bankContactsMatched": "{count} bank transactions linked to contacts",
    "app.agent.report.bankContactsCreated": "{count} contacts created from bank transactions",
    "app.agent.report.bankContactsLinkedExisting": "{count} bank transactions linked to existing contacts",
    "app.agent.report.bankContactsOpen": "{count} contacts from bank transactions not created yet",
    "app.agent.drafts.thinking": "I am looking for documents without a draft",
    "app.agent.drafts.progress": "Drafts from documents ({product})",
    "app.agent.drafts.done": "{count} drafts created from documents ({product})",
    "app.agent.report.draftsCreated": "{count} drafts created from documents",
    "app.agent.report.draftRefused": "{count} documents not booked: {reason}",
    "app.agent.report.draftsFailed": "{count} documents could not be booked",
    "app.agent.report.draftsRunning":
        "{count} documents are being booked in the background, the result will be in Binale",
    "app.agent.txDrafts.bankThinking": "I am creating drafts from bank transactions",
    "app.agent.txDrafts.kasseThinking": "I am creating drafts from the cash register",
    "app.agent.txDrafts.bankDone": "{count} drafts created from bank transactions",
    "app.agent.txDrafts.kasseDone": "{count} drafts created from the cash register",
    "app.agent.report.txDraftsBank": "{count} drafts created from bank transactions",
    "app.agent.report.txDraftsKasse": "{count} drafts created from the cash register",
    "app.agent.report.txDraftsNotFound": "{count} transactions not found, please reload the page and start again",
    "app.agent.report.txDraftsInvalid": "{count} transactions not booked, the details are incomplete",
    "app.agent.fillBank.thinking": "I am matching the bank transactions to your bookings",
    "app.agent.fillBank.done": "{count} bank transactions matched",
    "app.agent.report.fillBankMatched": "{count} bank transactions matched (Fill bank)",
    "app.agent.report.fillBankAmbiguous":
        "{count} bank transactions have several possible bookings and need your choice",
    "app.agent.report.fillBankJournaled": "{count} bank transactions are locked and were not changed",
    "app.agent.reconcile.thinking": "I am reconciling the accounts",
    "app.agent.reconcile.done": "{count} payments created in the account reconciliation",
    "app.agent.report.reconciled": "{count} payments created in the account reconciliation",
    "app.agent.report.reconcileSkipped": "{count} reconciliation groups need your review",
    "app.agent.report.reconcileChanged": "{count} reconciliation groups changed and were not applied",
    "app.agent.report.reconcileFailed": "{count} reconciliation groups could not be created",
    "app.agent.report.reconcileAccountFailed":
        "{count} accounts could not be reconciled, please check your rights for Bank and Kasse",
    "app.agent.aiCheck.thinking": "I am checking the bookings with AI",
    "app.agent.aiCheck.done": "{checked} bookings checked, {invalid} with deviations",
    "app.agent.report.aiChecked": "{count} bookings checked with AI",
    "app.agent.report.aiCheckInvalid": "{count} bookings deviate from the document, please review them",
    "app.agent.report.aiCheckNotChecked": "{count} bookings could not be checked, please review them yourself",
    "app.agent.postingRules.thinking": "I am looking for posting rule suggestions",
    "app.agent.postingRules.askIncoming": "There are {count} posting rule suggestions for incoming invoices.",
    "app.agent.postingRules.askOutgoing": "There are {count} posting rule suggestions for outgoing invoices.",
    "app.agent.postingRules.import": "Import as draft",
    "app.agent.postingRules.skip": "Later",
    "app.agent.postingRules.importing": "I am importing the suggestions as drafts",
    "app.agent.postingRules.done": "{count} posting rules imported as drafts",
    "app.agent.report.postingRulesImported": "{count} posting rules imported as drafts, please activate them yourself",
    "app.agent.report.postingRulesOpen": "{count} posting rule suggestions are waiting for your decision",
    "app.agent.report.title": "Report",
    "app.agent.report.thinking": "I am summing up what happened",
    "app.agent.report.sectionDone": "Done",
    "app.agent.report.sectionOpen": "Needs you",
    "app.agent.report.nothingDone": "There was nothing to do.",
    "app.agent.report.nothingOpen": "Nothing is left open.",
    "app.agent.report.continuesInBinale": "{step}: keeps running in the background, you will find the result in Binale",
    "app.agent.report.pending.DOCUMENTS_NEED_ANSWER": "{count} documents are waiting for your answer",
    "app.agent.report.pending.DOCUMENTS_NOT_PROCESSED": "{count} documents have not been read by the AI yet",
    "app.agent.report.pending.DOCUMENTS_READY_TO_BOOK": "{count} documents are ready to book",
    "app.agent.report.pending.BANK_TRANSACTIONS_NOT_BOOKED": "{count} bank transactions are not booked yet",
    "app.agent.report.pending.KASSE_TRANSACTIONS_NOT_BOOKED": "{count} cash transactions are not booked yet",
    "app.agent.report.pending.AI_DRAFTS_TO_REVIEW": "{count} AI drafts are waiting for your review",
    "app.agent.report.pending.DRAFTS_WITHOUT_ACCOUNT": "{count} drafts have no account yet",
    "app.agent.report.ustvaOpen": "No VAT return for {period} has been transmitted in Binale yet",
    "app.agent.report.reviewDrafts": "Review drafts",
    "app.agent.report.download": "Download report",
    "app.fields.istNonGeldkontoHint": "This account is not a money/payment account and does not make the VAT due under cash-basis taxation (Ist-Versteuerung).",
};
